import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTheme, alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import DesignServicesOutlinedIcon from "@mui/icons-material/DesignServicesOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import TextureOutlinedIcon from "@mui/icons-material/TextureOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import ViewSidebarOutlinedIcon from "@mui/icons-material/ViewSidebarOutlined";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import { jwtDecode } from "jwt-decode";
import { Trans, useTranslation } from "react-i18next";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  pagePrimaryButtonSx,
  pageFieldSx,
} from "../../styles/pageModernStyles";
import { MEETING_SURFACE_BG } from "../Meetings/meetingUiStyles";
import { motionSafe, rise, staggerIn } from "../AI/studioDialogMotion";
import { getUserById } from "../../api/User/userApi";
import { isSkillsApprover } from "./skillFichaHelpers";
import SkillAgentBrandSourcesFields from "./skillAgentBrandSourcesFields";
import SkillAgentCustomSourcesFields, {
  customSourcesToUrls,
  normalizeCustomSources,
} from "./skillAgentCustomSourcesFields";
import SkillAgentResearchCatalogDialog from "./SkillAgentResearchCatalogDialog";
import {
  cancelSkillAgentTask,
  createSkillAgentTask,
  deleteSkillAgentTask,
  dispatchSkillAgentTask,
  fetchSkillAgentArtifactBlobUrl,
  getSkillAgentTaskById,
  getSkillAgentTasksMeta,
  listSkillAgentArtifacts,
  listSkillAgentTasks,
  markSkillAgentTaskDone,
  pushSkillAgentArtifactsToDrive,
  runSkillAgentTask,
  updateSkillAgentTask,
} from "../../api/Skills/skillAgentTasksApi";
import {
  createSkillAgentSearch,
  getSkillAgentSearchById,
  getSkillAgentSearchesMeta,
  listSkillAgentSearches,
  updateSkillAgentSearch,
} from "../../api/Skills/skillAgentSearchesApi";
import {
  createSkillAgentMoodboard,
  getSkillAgentMoodboardById,
  listSkillAgentMoodboards,
  updateSkillAgentMoodboard,
} from "../../api/Skills/skillAgentMoodboardsApi";
import { buildFichaTecnicaPages } from "./skillAgentMoodboardLayout";
import {
  createSkillAgentRapport,
  getSkillAgentRapportById,
  listSkillAgentRapports,
  updateSkillAgentRapport,
} from "../../api/Skills/skillAgentRapportsApi";
import {
  createSkillAgentProject,
  deleteSkillAgentProject,
  getSkillAgentProjectBundle,
  getSkillAgentProjectByMeeting,
  listSkillAgentProjectTreeRoots,
  syncSkillAgentProjectsFromMeetings,
  updateSkillAgentProject,
} from "../../api/Skills/skillAgentProjectsApi";
import {
  createMeetingSession,
  getMeetingSession,
  patchMeetingSession,
} from "../../api/Meetings/meetingSessionsApi";
import { getCustomers } from "../../api/Customer/customerApi";
import { getTemporadas } from "../../api/Temporadas/temporadaApi";
import MeetingSessionPage from "../Meetings/MeetingSessionPage";
import { optionsFromCustomers } from "../Meetings/meetingMoodboardClientOptions";
import { sortTemporadasFromLabel } from "../Meetings/meetingTemporadaSort";
import { listSkillFichas } from "../../api/Skills/skillFichasApi";
import {
  buildCompassProjectsTree,
  collectCompassExpandIds,
  extendCompassPathToOrgBranch,
  filterCompassTreeByType,
  findCompassArtifactPathIds,
  findCompassClientPathIds,
  findCompassTreePathIds,
  isResumenBoard,
  isSketchTipoTarea,
  projectIdsFromTreeRoots,
  rapportStatusLabel,
  searchStatusLabel,
  taskClientLabel,
  taskStatusColor,
  taskStatusLabel,
  taskTypeLabel,
  TREE_TYPE_FILTERS,
} from "./skillAgentTaskTree";
import SkillAgentCompassColumns, {
  COMPASS_COL_WIDTH,
} from "./SkillAgentCompassColumns";
import AiDesignsStudioPage from "../AI/AiDesignsStudioPage";
import {
  loadCompassFavorites,
  renameCompassProjectFavorite,
  saveCompassFavorites,
  syncCompassFavoriteLabelsFromRoots,
  toggleCompassFavorite,
} from "./skillAgentCompassFavorites";
import SkillAgentTasksHelpDialog from "./SkillAgentTasksHelpDialog";
import SkillAgentInterpretHelpDialog from "./SkillAgentInterpretHelpDialog";
import SkillAgentTileHelpDialog from "./SkillAgentTileHelpDialog";
import SkillAgentSectionHelpDialog from "./SkillAgentSectionHelpDialog";
import SkillAgentRapportPickDialog from "./SkillAgentRapportPickDialog";
import SkillAgentDesignPickDialog from "./SkillAgentDesignPickDialog";
import SkillAgentHubCreateDialog from "./SkillAgentHubCreateDialog";
import SkillAgentNewProjectDialog from "./SkillAgentNewProjectDialog";
import SkillAgentEditProjectDialog from "./SkillAgentEditProjectDialog";
import SkillAgentDeleteProjectDialog from "./SkillAgentDeleteProjectDialog";
import SkillAgentMoveOrgBrandDialog from "./SkillAgentMoveOrgBrandDialog";
import SkillAgentSearchIntro from "./SkillAgentSearchIntro";
import SkillAgentBoardIntro from "./SkillAgentBoardIntro";
import SkillAgentSearchDetail from "./SkillAgentSearchDetail";
import SkillAgentScrapingBeeCreditsAlert from "./SkillAgentScrapingBeeCreditsAlert";
import SkillAgentMoodboardDetail from "./SkillAgentMoodboardDetail";
import SkillAgentRapportDetail from "./SkillAgentRapportDetail";
import SkillAgentCaratulasDialog from "./SkillAgentCaratulasDialog";
import SkillAgentCaratulaEditor from "./SkillAgentCaratulaEditor";
import {
  listSkillAgentCaratulas,
  resolveSkillAgentCaratulaForCreate,
} from "../../api/Skills/skillAgentCaratulasApi";
import SkillAgentClientInfoDetail from "./SkillAgentClientInfoDetail";
import { rapportToolKind } from "./skillAgentRapport";
import {
  createSkillAgentClientInfo,
  updateSkillAgentClientInfo,
} from "../../api/Skills/skillAgentClientInfosApi";
import {
  ORG_BRANDS,
  normalizeOrgBrand,
  otherOrgBrand,
} from "./skillAgentOrgBrand";

const TASK_TYPE_KEYS = [
  "propuestas",
  "siluetas",
  "nuevos_disenos",
  // Legacy (tareas antiguas)
  "presentacion",
  "mood",
  "revision",
  "otro",
];

function TreeBranch({
  node,
  depth = 0,
  expanded,
  toggle,
  selectedId,
  selectedSearchId,
  selectedMoodboardId,
  selectedRapportId,
  selectedClientInfoId,
  selectedMeetingId,
  selectedProjectId,
  onSelectTask,
  onSelectSearch,
  onSelectMoodboard,
  onSelectResumen,
  onSelectRapport,
  onSelectClientInfo,
  onSelectAiDesign,
  onSelectProject,
  onNewSearch,
  onNewMoodboard,
  onNewResumen,
  onNewFicha,
  onNewRapport,
  onNewClientInfo,
  onNewProduccion,
  onNewPresentaciones,
  onNewStatus,
  onNewTask,
  onNewMeeting,
  onOpenMeeting,
  onHubCreate,
  onEditProjectSeason,
  onDeleteProject,
  onMoveOrgBrand,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [projectMenuEl, setProjectMenuEl] = useState(null);
  const [moveMenu, setMoveMenu] = useState(null);
  const open = expanded.has(node.id);
  const isSeason = node.kind === "season";
  const isClient = node.kind === "client";
  const isProject = node.kind === "project";
  const isOrgBranch = node.kind === "orgBranch";
  const isMeetings = node.kind === "meetings";
  const isMeeting = node.kind === "meeting";
  const canHubAdd = isMeeting || isOrgBranch;
  const missingSeason = isProject && !String(node.Temporada || "").trim();
  const projectMenuOpen = Boolean(projectMenuEl);
  const isSearchs = node.kind === "searchs";
  const isMoodboards = node.kind === "moodboards";
  const isFichas = node.kind === "fichasTecnicas";
  const isResumenes = node.kind === "resumenes";
  const isRapports = node.kind === "rapports";
  const isRapportTiles = node.kind === "rapportTiles";
  const isRapportInterpret = node.kind === "rapportInterpret";
  const isClientInfos = node.kind === "clientInfos";
  const isProducciones = node.kind === "producciones";
  const isPresentaciones = node.kind === "presentaciones";
  const isStatus = node.kind === "status";
  const isDisenos = node.kind === "disenos";
  const isSketches = node.kind === "sketches";
  const isLibrary =
    isSearchs ||
    isMoodboards ||
    isFichas ||
    isResumenes ||
    isDisenos ||
    isSketches ||
    isRapports ||
    isRapportTiles ||
    isRapportInterpret ||
    isClientInfos ||
    isProducciones ||
    isPresentaciones ||
    isStatus ||
    isMeetings;
  const BranchIcon = isStatus
    ? FlagOutlinedIcon
    : isPresentaciones
      ? PictureAsPdfOutlinedIcon
      : isProducciones
        ? PrecisionManufacturingOutlinedIcon
        : isClientInfos
          ? InfoOutlinedIcon
          : isRapportInterpret
            ? AutoFixHighOutlinedIcon
            : isRapportTiles
              ? GridViewOutlinedIcon
              : isRapports
                ? TextureOutlinedIcon
                : isResumenes
                  ? SummarizeOutlinedIcon
                  : isFichas
                    ? DescriptionOutlinedIcon
                    : isMoodboards
                      ? DashboardCustomizeOutlinedIcon
                      : isSearchs
                        ? TravelExploreOutlinedIcon
                        : isDisenos || isSketches
                          ? DesignServicesOutlinedIcon
                          : isMeetings
                            ? CalendarMonthOutlinedIcon
                            : isMeeting
                              ? GroupsOutlinedIcon
                              : isProject
                                ? DesignServicesOutlinedIcon
                                : isOrgBranch
                                  ? BusinessOutlinedIcon
                                  : isSeason
                                    ? CalendarMonthOutlinedIcon
                                    : BusinessOutlinedIcon;
  // NML/STAGE: acento slate (no naranja de MEETINGS).
  // Carpeta MEETINGS = calendario naranja; reunión = grupo en slate (no hereda el naranja).
  const accent = isStatus
    ? "#455a64"
    : isPresentaciones
      ? "#ad1457"
      : isProducciones
        ? "#bf360c"
        : isClientInfos
          ? "#0277bd"
          : isRapportInterpret
            ? "#6a1b9a"
            : isRapportTiles || isRapports
              ? "#6a1b9a"
              : isResumenes
                ? "#1565c0"
                : isFichas
                  ? "#00695c"
                  : isMoodboards
                    ? "#6a1b9a"
                    : isSearchs
                      ? "#00897b"
                      : isSketches
                        ? "#5e35b1"
                        : isDisenos
                          ? "#1565c0"
                          : isMeetings
                            ? "#e65100"
                            : isMeeting
                              ? "#546e7a"
                              : isSeason
                                ? "#00897b"
                                : isClient
                                  ? "#546e7a"
                                  : isProject || isOrgBranch
                                    ? "#455a64"
                                    : "#e65100";

  const tasks = node.tasks || [];
  const searches = node.searches || [];
  const moodboards = node.moodboards || [];
  const fichasTecnicas = node.fichasTecnicas || node.moodboards || [];
  const resumenes = node.resumenes || node.moodboards || [];
  const rapports = node.rapports || [];
  const clientInfos = node.clientInfos || [];

  /** Solo hojas reales. Nunca cuenta las carpetas vacías. */
  const childCount = (() => {
    if (isMeetings) return (node.children || []).length;
    if (isSearchs) return searches.length;
    if (isMoodboards) return moodboards.length;
    if (isFichas) return fichasTecnicas.length;
    if (isResumenes) return resumenes.length;
    if (isRapports || isRapportTiles || isRapportInterpret)
      return rapports.length;
    if (isClientInfos || isProducciones || isPresentaciones || isStatus)
      return clientInfos.length;
    if (isDisenos) return tasks.length;
    if (isSketches) return (node.moodboards || node.aiDesigns || []).length;
    if (isProject) {
      return (node.orgBranches || []).reduce(
        (sum, b) => sum + (Number(b.count) || 0),
        0,
      );
    }
    if (isOrgBranch || isMeeting) {
      const meetings = node.meetingsNode?.count || 0;
      const designs = node.disenosNode?.count || 0;
      const sketches = node.sketchesNode?.count || 0;
      const sr = node.searchsNode?.count || 0;
      const mb = node.moodboardsNode?.count || 0;
      const ft = node.fichasNode?.count || 0;
      const rs = node.resumenesNode?.count || 0;
      const rp = node.rapportsNode?.count || 0;
      const ci = node.infoNode?.count || 0;
      const pr = node.produccionNode?.count || 0;
      const pp = node.presentacionesNode?.count || 0;
      const st = node.statusNode?.count || 0;
      return (
        meetings +
        designs +
        sketches +
        sr +
        mb +
        ft +
        rs +
        rp +
        ci +
        pr +
        pp +
        st
      );
    }
    if (node.children?.length) {
      return node.children.reduce((sum, child) => sum + (child.count || 0), 0);
    }
    return Number(node.count) || 0;
  })();

  const childBranchProps = {
    depth: depth + 1,
    expanded,
    toggle,
    selectedId,
    selectedSearchId,
    selectedMoodboardId,
    selectedRapportId,
    selectedClientInfoId,
    selectedMeetingId,
    selectedProjectId,
    onSelectTask,
    onSelectSearch,
    onSelectMoodboard,
    onSelectResumen,
    onSelectRapport,
    onSelectClientInfo,
    onSelectAiDesign,
    onSelectProject,
    onNewSearch,
    onNewMoodboard,
    onNewResumen,
    onNewFicha,
    onNewRapport,
    onNewClientInfo,
    onNewProduccion,
    onNewPresentaciones,
    onNewStatus,
    onNewTask,
    onNewMeeting,
    onOpenMeeting,
    onHubCreate,
    onEditProjectSeason,
    onDeleteProject,
    onMoveOrgBrand,
  };

  const meetingSelected =
    isMeeting &&
    Number(selectedMeetingId) > 0 &&
    (Number(node.MeetingSessionId) === Number(selectedMeetingId) ||
      Number(node.meeting?.id) === Number(selectedMeetingId));
  const projectSelected =
    isProject &&
    Number(selectedProjectId) > 0 &&
    Number(node.ProjectId || node.projectId) === Number(selectedProjectId);

  const isDark = theme.palette.mode === "dark";
  const seasonBg = isDark ? alpha("#00897b", 0.22) : alpha("#00897b", 0.12);
  const clientBg = isDark ? alpha(theme.palette.common.white, 0.07) : "#e4e9ee";
  const projectBg = projectSelected
    ? isDark
      ? alpha("#455a64", 0.28)
      : alpha("#00897b", 0.12)
    : isDark
      ? alpha(theme.palette.common.white, 0.06)
      : "#ffffff";
  const libraryBg = isDark ? alpha(accent, 0.16) : alpha(accent, 0.1);
  // Reunión: fondo neutro (como proyecto), selección slate — nunca naranja de MEETINGS.
  const meetingBg = meetingSelected
    ? isDark
      ? alpha("#546e7a", 0.28)
      : alpha("#546e7a", 0.12)
    : isDark
      ? alpha(theme.palette.common.white, 0.06)
      : "#ffffff";

  // Proyecto → NML|STAGE (madre) → carpetas/reuniones. Sin el escalón genérico por depth
  // NML/STAGE se iba demasiado a la derecha y perdía la lectura de padre.
  const branchPl = isSeason
    ? 1
    : isClient
      ? 1.75
      : isProject
        ? 2.5
        : isOrgBranch
          ? 2.85
          : isLibrary || isMeeting
            ? 3.45 + Math.max(0, depth - 4) * 1.15
            : 1 + depth * 1.6;
  const leafPad = branchPl + 2.75;

  const leafSx = (selected, tone) => ({
    display: "flex",
    alignItems: "center",
    gap: 0.75,
    ml: leafPad,
    mr: 0.65,
    minHeight: 30,
    px: 0.85,
    py: 0.3,
    mb: 0.15,
    borderRadius: 1.15,
    cursor: "pointer",
    border: "1px solid",
    borderColor: selected ? alpha(tone, 0.35) : "transparent",
    bgcolor: selected
      ? alpha(tone, 0.1)
      : isDark
        ? alpha(theme.palette.common.white, 0.03)
        : alpha("#fff", 0.75),
    transition: "background-color 0.12s ease, border-color 0.12s ease",
    "&:hover": {
      bgcolor: alpha(tone, selected ? 0.14 : 0.06),
      "& .leaf-status": { opacity: 1 },
      "& .leaf-move": { opacity: 1 },
    },
  });

  const openMoveMenu = (e, kind, item) => {
    e.preventDefault();
    e.stopPropagation();
    setMoveMenu({
      mouseX: e.clientX + 2,
      mouseY: e.clientY - 6,
      kind,
      item,
    });
  };

  const leafMoveBtn = (kind, item) => (
    <Tooltip
      title={t("skillAgent.tree.moveToBrand", {
        brand: otherOrgBrand(item?.OrgBrand || item?.orgBrand),
        defaultValue: `Mover a ${otherOrgBrand(item?.OrgBrand || item?.orgBrand)}`,
      })}
    >
      <IconButton
        size="small"
        className="leaf-move"
        aria-label={t("skillAgent.tree.moveToBrand", {
          brand: otherOrgBrand(item?.OrgBrand || item?.orgBrand),
          defaultValue: `Mover a ${otherOrgBrand(item?.OrgBrand || item?.orgBrand)}`,
        })}
        onClick={(e) => {
          e.stopPropagation();
          onMoveOrgBrand?.(
            kind,
            item,
            otherOrgBrand(item?.OrgBrand || item?.orgBrand),
          );
        }}
        sx={{
          width: 22,
          height: 22,
          opacity: 0,
          flexShrink: 0,
          color: "text.secondary",
          transition: "opacity 0.12s",
          "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.08) },
        }}
      >
        <SwapHorizOutlinedIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  );

  const statusChipSx = {
    height: 18,
    maxWidth: 92,
    borderRadius: 1,
    fontWeight: 600,
    fontSize: "0.62rem",
    opacity: 0.72,
    "& .MuiChip-label": { px: 0.6 },
  };

  const countPillSx = {
    height: 20,
    minWidth: 22,
    borderRadius: 1.25,
    fontWeight: 700,
    fontSize: "0.65rem",
    bgcolor: isSeason
      ? alpha("#00897b", isDark ? 0.28 : 0.16)
      : isClient
        ? alpha("#546e7a", isDark ? 0.35 : 0.12)
        : alpha(theme.palette.text.primary, 0.06),
    color: isSeason
      ? "#00897b"
      : isClient
        ? isDark
          ? "#cbd5e1"
          : "#37474f"
        : "text.secondary",
    "& .MuiChip-label": { px: 0.7 },
  };

  const orgBranchBg = isDark ? alpha("#455a64", 0.2) : alpha("#455a64", 0.07);

  const rowBg = meetingSelected
    ? meetingBg
    : isSeason
      ? seasonBg
      : isClient
        ? clientBg
        : isProject
          ? projectBg
          : isOrgBranch
            ? orgBranchBg
            : isLibrary
              ? libraryBg
              : isMeeting
                ? meetingBg
                : "transparent";

  return (
    <Box
      sx={{
        mb: isSeason
          ? 1.15
          : isClient
            ? 0.55
            : isProject
              ? 0.3
              : isOrgBranch
                ? 0.45
                : 0.12,
        ...(isSeason
          ? {
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid",
              borderColor: alpha("#00897b", isDark ? 0.35 : 0.22),
              bgcolor: isDark ? alpha("#00897b", 0.08) : alpha("#00897b", 0.04),
            }
          : isClient
            ? {
                borderRadius: 1.5,
                overflow: "hidden",
                border: "1px solid",
                borderColor: isDark
                  ? alpha(theme.palette.common.white, 0.1)
                  : "rgba(84, 110, 122, 0.22)",
                bgcolor: isDark
                  ? alpha(theme.palette.common.white, 0.025)
                  : "#eef1f4",
              }
            : isOrgBranch
              ? {
                  borderRadius: 1.35,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: isDark
                    ? alpha("#78909c", 0.35)
                    : alpha("#546e7a", 0.22),
                  bgcolor: isDark
                    ? alpha("#455a64", 0.12)
                    : alpha("#546e7a", 0.04),
                  ml: 0.35,
                  mr: 0.35,
                }
              : {}),
      }}
    >
      <Box
        className="tree-branch-row"
        data-tree-id={node.id}
        onClick={() => {
          if (isMeeting) {
            onOpenMeeting?.(node);
            return;
          }
          if (isProject) {
            onSelectProject?.(node);
            return;
          }
          toggle(node.id);
        }}
        onContextMenu={
          isMeeting
            ? (e) =>
                openMoveMenu(e, "meeting", {
                  id: node.MeetingSessionId || node.meeting?.id,
                  OrgBrand: node.OrgBrand || node.orgBrand,
                  orgBrand: node.orgBrand || node.OrgBrand,
                  ProjectId: node.ProjectId || node.projectId,
                  ...(node.meeting || {}),
                })
            : undefined
        }
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.55,
          minHeight: isSeason ? 42 : isClient ? 38 : isLibrary ? 30 : 34,
          px: 0.85,
          py: isSeason || isClient ? 0.55 : 0.4,
          pl: branchPl,
          mx: isSeason || isClient ? 0 : 0.4,
          borderRadius: isSeason || isClient ? 0 : 1.35,
          cursor: "pointer",
          userSelect: "none",
          bgcolor: rowBg,
          border:
            isProject || isMeeting
              ? `1px solid ${
                  meetingSelected
                    ? alpha("#546e7a", 0.45)
                    : projectSelected
                      ? alpha("#00897b", 0.45)
                      : isDark
                        ? alpha(theme.palette.common.white, 0.1)
                        : "rgba(148, 163, 184, 0.28)"
                }`
              : "1px solid transparent",
          borderBottom:
            (isSeason || isClient) && open
              ? `1px solid ${
                  isDark
                    ? alpha(theme.palette.common.white, 0.08)
                    : alpha("#64748b", 0.14)
                }`
              : undefined,
          boxShadow:
            (isProject || isMeeting) && !isDark
              ? "0 1px 2px rgba(15, 23, 42, 0.04)"
              : "none",
          transition: "background-color 0.12s ease",
          "&:hover": {
            bgcolor: meetingSelected
              ? alpha("#546e7a", isDark ? 0.34 : 0.16)
              : projectSelected
                ? alpha("#00897b", isDark ? 0.34 : 0.16)
                : isSeason
                  ? alpha("#00897b", isDark ? 0.3 : 0.18)
                  : isClient
                    ? isDark
                      ? alpha(theme.palette.common.white, 0.11)
                      : "#e4e9ee"
                    : isOrgBranch
                      ? alpha("#455a64", isDark ? 0.32 : 0.14)
                      : isProject
                        ? isDark
                          ? alpha(theme.palette.common.white, 0.08)
                          : "#fff"
                        : isMeeting
                          ? isDark
                            ? alpha(theme.palette.common.white, 0.08)
                            : "#fff"
                          : isLibrary
                            ? alpha(accent, isDark ? 0.22 : 0.14)
                            : alpha(accent, 0.06),
            "& .tree-add-btn": { opacity: 1 },
            "& .leaf-move": { opacity: 1 },
          },
        }}
      >
        <ExpandMoreIcon
          onClick={(e) => {
            e.stopPropagation();
            toggle(node.id);
          }}
          sx={{
            fontSize: isSeason || isClient || isOrgBranch ? 18 : 16,
            color: isSeason
              ? "#00897b"
              : isClient || isOrgBranch
                ? "#607d8b"
                : "text.disabled",
            flexShrink: 0,
            transform: open ? "rotate(0deg)" : "rotate(-90deg)",
            transition: "transform 0.15s",
          }}
        />
        <BranchIcon
          sx={{
            fontSize: isSeason || isClient || isOrgBranch ? 17 : 15,
            color: isLibrary
              ? accent
              : isSeason
                ? "#00897b"
                : isClient || isOrgBranch || isProject
                  ? "#607d8b"
                  : isMeeting
                    ? accent
                    : "text.disabled",
            flexShrink: 0,
            opacity: isLibrary || isMeeting || isOrgBranch ? 1 : 0.9,
          }}
        />
        <Typography
          variant="body2"
          fontWeight={
            isSeason || isClient || isLibrary || isOrgBranch
              ? 800
              : isProject || isMeeting
                ? 700
                : 600
          }
          letterSpacing={
            isSeason ? 0.04 : isClient || isLibrary || isOrgBranch ? 0.05 : 0
          }
          sx={{
            flex: 1,
            minWidth: 0,
            color: isLibrary
              ? accent
              : isOrgBranch
                ? isDark
                  ? "#cfd8dc"
                  : "#37474f"
                : isSeason
                  ? "#00695c"
                  : isClient
                    ? isDark
                      ? "#e2e8f0"
                      : "#37474f"
                    : isMeeting
                      ? isDark
                        ? "#e2e8f0"
                        : "#37474f"
                      : "text.primary",
            textTransform:
              isSeason || isClient || isLibrary || isOrgBranch
                ? "uppercase"
                : "none",
            fontSize: isLibrary
              ? "0.66rem"
              : isOrgBranch
                ? "0.7rem"
                : isSeason
                  ? "0.8125rem"
                  : isClient
                    ? "0.78rem"
                    : "0.8125rem",
          }}
          noWrap
        >
          {node.label}
        </Typography>
        {isProject ? (
          <>
            <Tooltip
              title={t("skillAgent.tree.projectActions", {
                defaultValue: "Acciones del proyecto",
              })}
              placement="top"
            >
              <IconButton
                size="small"
                className={
                  missingSeason || projectMenuOpen ? undefined : "tree-add-btn"
                }
                aria-label={t("skillAgent.tree.projectActions", {
                  defaultValue: "Acciones del proyecto",
                })}
                aria-haspopup="menu"
                aria-expanded={projectMenuOpen ? "true" : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  setProjectMenuEl(e.currentTarget);
                }}
                sx={{
                  width: 22,
                  height: 22,
                  color: missingSeason ? "#00897b" : accent,
                  opacity: missingSeason || projectMenuOpen ? 1 : 0,
                  flexShrink: 0,
                  transition: "opacity 0.12s",
                  "&:hover": {
                    bgcolor: alpha(missingSeason ? "#00897b" : accent, 0.12),
                  },
                }}
              >
                <AddOutlinedIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={projectMenuEl}
              open={projectMenuOpen}
              onClose={(e) => {
                e?.stopPropagation?.();
                setProjectMenuEl(null);
              }}
              onClick={(e) => e.stopPropagation()}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              transitionDuration={{ enter: 180, exit: 120 }}
              slotProps={{
                paper: {
                  elevation: 0,
                  sx: {
                    mt: 0.75,
                    minWidth: 248,
                    overflow: "hidden",
                    borderRadius: 2.75,
                    border: "1px solid",
                    borderColor: alpha("#00897b", isDark ? 0.35 : 0.22),
                    boxShadow: `0 18px 40px ${alpha("#0f172a", 0.18)}, 0 4px 12px ${alpha("#00897b", 0.12)}`,
                    // Colores opacos: un alpha en el gradient deja ver el árbol detrás.
                    bgcolor: isDark
                      ? theme.palette.background.paper
                      : "#ffffff",
                    backgroundImage: isDark
                      ? `linear-gradient(165deg, ${alpha("#00897b", 0.22)} 0%, ${theme.palette.background.paper} 52%)`
                      : "linear-gradient(165deg, #e8f6f4 0%, #ffffff 52%)",
                    animation: projectMenuOpen
                      ? `${rise} 280ms cubic-bezier(0.22, 1, 0.36, 1) both`
                      : undefined,
                    ...motionSafe,
                    "& .MuiList-root": { py: 0.75, px: 0.75 },
                  },
                },
              }}
            >
              <Box
                sx={{
                  px: 1.15,
                  pt: 0.85,
                  pb: 1,
                  mb: 0.35,
                  borderRadius: 2,
                  bgcolor: isDark ? alpha("#00897b", 0.28) : "#dff3ef",
                  border: `1px solid ${alpha("#00897b", 0.22)}`,
                  animation: `${staggerIn} 280ms cubic-bezier(0.22, 1, 0.36, 1) both`,
                  ...motionSafe,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    fontWeight: 800,
                    letterSpacing: 0.04,
                    textTransform: "uppercase",
                    color: "#00897b",
                    fontSize: "0.62rem",
                    mb: 0.2,
                  }}
                >
                  {t("skillAgent.tree.projectMenuEyebrow", {
                    defaultValue: "Proyecto",
                  })}
                </Typography>
                <Typography
                  variant="body2"
                  noWrap
                  title={node.label}
                  sx={{ fontWeight: 800, lineHeight: 1.25, maxWidth: 210 }}
                >
                  {node.label}
                </Typography>
              </Box>
              {[
                {
                  key: "add",
                  icon: <AddOutlinedIcon sx={{ fontSize: 18 }} />,
                  accent: "#00897b",
                  primary: t("skillAgent.tree.addInProject", {
                    defaultValue: "Añadir al proyecto",
                  }),
                  secondary: t("skillAgent.tree.addInProjectHint", {
                    defaultValue: "Reunión, moodboard, ficha…",
                  }),
                  onClick: () => onHubCreate?.(node),
                },
                {
                  key: "edit",
                  icon: <EditOutlinedIcon sx={{ fontSize: 18 }} />,
                  accent: missingSeason ? "#00897b" : "#546e7a",
                  primary: t("skillAgent.tree.editProject", {
                    defaultValue: "Editar proyecto",
                  }),
                  secondary: missingSeason
                    ? t("skillAgent.tree.editProjectMissingSeason", {
                        defaultValue: "Falta temporada",
                      })
                    : t("skillAgent.tree.editProjectHint", {
                        defaultValue: "Nombre y temporada",
                      }),
                  onClick: () => onEditProjectSeason?.(node),
                },
                {
                  key: "delete",
                  icon: <DeleteOutlineIcon sx={{ fontSize: 18 }} />,
                  accent: "#c62828",
                  danger: true,
                  primary: t("skillAgent.tree.deleteProject", {
                    defaultValue: "Eliminar proyecto",
                  }),
                  secondary: t("skillAgent.tree.deleteProjectHint", {
                    defaultValue: "Solo si está vacío",
                  }),
                  onClick: () => onDeleteProject?.(node),
                },
              ].map((action, idx) => (
                <MenuItem
                  key={action.key}
                  onClick={() => {
                    setProjectMenuEl(null);
                    action.onClick();
                  }}
                  sx={{
                    my: 0.2,
                    py: 1,
                    px: 1,
                    borderRadius: 2,
                    gap: 1,
                    alignItems: "center",
                    color: action.danger ? "#c62828" : "text.primary",
                    animation: `${staggerIn} 320ms cubic-bezier(0.22, 1, 0.36, 1) both`,
                    animationDelay: `${60 + idx * 45}ms`,
                    ...motionSafe,
                    "&:hover": {
                      bgcolor: alpha(action.accent, isDark ? 0.18 : 0.1),
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: 1.75,
                      flexShrink: 0,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: alpha(action.accent, isDark ? 0.22 : 0.12),
                      color: action.accent,
                      border: `1px solid ${alpha(action.accent, 0.28)}`,
                    }}
                  >
                    {action.icon}
                  </Box>
                  <ListItemText
                    primary={action.primary}
                    secondary={action.secondary}
                    primaryTypographyProps={{
                      fontWeight: 800,
                      fontSize: "0.84rem",
                      lineHeight: 1.2,
                    }}
                    secondaryTypographyProps={{
                      fontSize: "0.7rem",
                      color: action.danger
                        ? alpha("#c62828", 0.75)
                        : "text.secondary",
                    }}
                  />
                </MenuItem>
              ))}
            </Menu>
          </>
        ) : null}
        {canHubAdd ? (
          <IconButton
            size="small"
            className="tree-add-btn"
            aria-label={t("skillAgent.tree.ariaAddInProject", {
              defaultValue: "Añadir al proyecto",
            })}
            onClick={(e) => {
              e.stopPropagation();
              onHubCreate?.(node);
            }}
            sx={{
              width: 22,
              height: 22,
              color: accent,
              opacity: 0,
              flexShrink: 0,
              transition: "opacity 0.12s",
              "&:hover": { bgcolor: alpha(accent, 0.12) },
            }}
          >
            <AddOutlinedIcon sx={{ fontSize: 15 }} />
          </IconButton>
        ) : isDisenos ||
          isSketches ||
          isSearchs ||
          isMoodboards ||
          isFichas ||
          isResumenes ||
          isRapports ||
          isRapportTiles ||
          isRapportInterpret ||
          isClientInfos ||
          isProducciones ||
          isPresentaciones ||
          isStatus ? (
          <IconButton
            size="small"
            className="tree-add-btn"
            aria-label={
              isDisenos
                ? t("skillAgent.tree.ariaNewDesign")
                : isSketches
                  ? t("skillAgent.tree.ariaNewSketch", {
                      defaultValue: "Nuevo Sketch / New design",
                    })
                  : isSearchs
                    ? t("skillAgent.tree.ariaNewSearch")
                    : isMoodboards
                      ? t("skillAgent.tree.ariaNewMoodboard")
                      : isFichas
                        ? t("skillAgent.tree.ariaNewFicha", {
                            defaultValue: "Nueva ficha técnica",
                          })
                        : isResumenes
                          ? t("skillAgent.tree.ariaNewResumen")
                          : isStatus
                            ? t("skillAgent.tree.ariaNewStatus", {
                                defaultValue: "Nuevo Status",
                              })
                            : isPresentaciones
                              ? t("skillAgent.tree.ariaNewPresentaciones", {
                                  defaultValue: "Nueva Presentación PDF",
                                })
                              : isProducciones
                                ? t("skillAgent.tree.ariaNewProduccion", {
                                    defaultValue: "Nueva Producción",
                                  })
                                : isClientInfos
                                  ? t("skillAgent.tree.ariaNewClientInfo", {
                                      defaultValue: "Nuevo Client Info",
                                    })
                                  : isRapportTiles
                                    ? t("skillAgent.tree.ariaNewRapportTile")
                                    : isRapportInterpret
                                      ? t(
                                          "skillAgent.tree.ariaNewRapportInterpret",
                                        )
                                      : t("skillAgent.tree.ariaNewRapport")
            }
            onClick={(e) => {
              e.stopPropagation();
              if (isDisenos || isSketches) onNewTask?.(node);
              else if (isSearchs) onNewSearch?.(node);
              else if (isMoodboards) onNewMoodboard?.(node);
              else if (isFichas) onNewFicha?.(node);
              else if (isResumenes) onNewResumen?.(node);
              else if (isStatus) onNewStatus?.(node);
              else if (isPresentaciones) onNewPresentaciones?.(node);
              else if (isProducciones) onNewProduccion?.(node);
              else if (isClientInfos) onNewClientInfo?.(node);
              else if (isRapportTiles) onNewRapport?.(node, "tartan");
              else if (isRapportInterpret)
                onNewRapport?.(node, "interpretacion");
              else onNewRapport?.(node);
            }}
            sx={{
              width: 22,
              height: 22,
              color: accent,
              opacity: 0,
              flexShrink: 0,
              transition: "opacity 0.12s",
              "&:hover": { bgcolor: alpha(accent, 0.12) },
            }}
          >
            <AddOutlinedIcon sx={{ fontSize: 15 }} />
          </IconButton>
        ) : null}
        {isMeeting
          ? leafMoveBtn("meeting", {
              id: node.MeetingSessionId || node.meeting?.id,
              OrgBrand: node.OrgBrand || node.orgBrand,
              orgBrand: node.orgBrand || node.OrgBrand,
              ProjectId: node.ProjectId || node.projectId,
              ...(node.meeting || {}),
            })
          : null}
        {isSeason || isClient ? (
          <Chip size="small" label={childCount} sx={countPillSx} />
        ) : (
          <Typography
            variant="caption"
            sx={{
              color: isLibrary ? alpha(accent, 0.75) : "text.disabled",
              fontWeight: 700,
              fontSize: "0.65rem",
              minWidth: 16,
              textAlign: "right",
              flexShrink: 0,
            }}
          >
            {childCount}
          </Typography>
        )}
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box
          sx={
            isSeason || isClient
              ? { px: 0.5, py: 0.45 }
              : isProject
                ? { py: 0.35, pl: 0.25 }
                : isOrgBranch
                  ? {
                      py: 0.3,
                      pl: 0.65,
                      pr: 0.35,
                      ml: 1.15,
                      borderLeft: `2px solid ${alpha(
                        "#546e7a",
                        isDark ? 0.45 : 0.28,
                      )}`,
                    }
                  : { py: 0.2 }
          }
        >
          {node.children?.map((child) => (
            <TreeBranch key={child.id} node={child} {...childBranchProps} />
          ))}
          {(node.orgBranches || []).map((branch) => (
            <TreeBranch key={branch.id} node={branch} {...childBranchProps} />
          ))}
          {(isOrgBranch
            ? [
                node.meetingsNode,
                node.disenosNode,
                node.sketchesNode,
                node.searchsNode,
                node.moodboardsNode,
                node.fichasNode,
                node.resumenesNode,
                node.rapportsNode,
                node.infoNode,
                node.produccionNode,
                node.presentacionesNode,
                node.statusNode,
              ]
            : isProject
              ? []
              : [
                  node.meetingsNode,
                  node.disenosNode,
                  node.sketchesNode,
                  node.searchsNode,
                  node.moodboardsNode,
                  node.fichasNode,
                  node.resumenesNode,
                  node.rapportsNode,
                  node.infoNode,
                  node.produccionNode,
                  node.presentacionesNode,
                  node.statusNode,
                ]
          )
            .filter(Boolean)
            .map((hub) => (
              <TreeBranch key={hub.id} node={hub} {...childBranchProps} />
            ))}
          {isSearchs &&
            searches.map((search) => {
              const selected = Number(selectedSearchId) === Number(search.id);
              return (
                <Box
                  key={`s-${search.id}`}
                  data-tree-id={`search:${search.id}`}
                  onClick={() => onSelectSearch?.(search.id)}
                  onContextMenu={(e) => openMoveMenu(e, "search", search)}
                  sx={leafSx(selected, accent)}
                >
                  <Typography
                    variant="body2"
                    fontWeight={selected ? 700 : 500}
                    noWrap
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      lineHeight: 1.25,
                      fontSize: "0.8125rem",
                    }}
                  >
                    {search.Titulo}
                  </Typography>
                  <Chip
                    className="leaf-status"
                    size="small"
                    label={searchStatusLabel(search.Status)}
                    variant="outlined"
                    sx={statusChipSx}
                  />
                  {leafMoveBtn("search", search)}
                </Box>
              );
            })}
          {isMoodboards &&
            moodboards.map((mb) => {
              const selected = Number(selectedMoodboardId) === Number(mb.id);
              const fromMeeting = Number(mb.MeetingSessionId) > 0;
              return (
                <Box
                  key={`mb-${mb.id}`}
                  data-tree-id={`moodboard:${mb.id}`}
                  onClick={() => onSelectMoodboard?.(mb.id)}
                  onContextMenu={(e) => openMoveMenu(e, "moodboard", mb)}
                  sx={leafSx(selected, accent)}
                >
                  <Tooltip
                    title={
                      fromMeeting
                        ? t("skillAgent.moodboard.originMeeting")
                        : t("skillAgent.moodboard.originSearch")
                    }
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        flexShrink: 0,
                        color: fromMeeting ? "info.main" : "warning.main",
                        mr: 0.5,
                      }}
                      aria-hidden
                    >
                      {fromMeeting ? (
                        <GroupsOutlinedIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <PhotoLibraryOutlinedIcon sx={{ fontSize: 16 }} />
                      )}
                    </Box>
                  </Tooltip>
                  <Typography
                    variant="body2"
                    fontWeight={selected ? 700 : 500}
                    noWrap
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      lineHeight: 1.25,
                      fontSize: "0.8125rem",
                    }}
                  >
                    {mb.Titulo}
                  </Typography>
                  {leafMoveBtn("moodboard", mb)}
                </Box>
              );
            })}
          {isFichas &&
            fichasTecnicas.map((mb) => {
              const selected = Number(selectedMoodboardId) === Number(mb.id);
              const fromMeeting = Number(mb.MeetingSessionId) > 0;
              return (
                <Box
                  key={`ft-${mb.id}`}
                  data-tree-id={`moodboard:${mb.id}`}
                  onClick={() => onSelectMoodboard?.(mb.id)}
                  onContextMenu={(e) => openMoveMenu(e, "moodboard", mb)}
                  sx={leafSx(selected, accent)}
                >
                  <Tooltip
                    title={
                      fromMeeting
                        ? t("skillAgent.moodboard.originMeeting")
                        : t("skillAgent.tree.folderFichasTecnicas", {
                            defaultValue: "FICHAS TÉCNICAS",
                          })
                    }
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        flexShrink: 0,
                        color: accent,
                        mr: 0.5,
                      }}
                      aria-hidden
                    >
                      <DescriptionOutlinedIcon sx={{ fontSize: 16 }} />
                    </Box>
                  </Tooltip>
                  <Typography
                    variant="body2"
                    fontWeight={selected ? 700 : 500}
                    noWrap
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      lineHeight: 1.25,
                      fontSize: "0.8125rem",
                    }}
                  >
                    {mb.Titulo}
                  </Typography>
                  {leafMoveBtn("moodboard", mb)}
                </Box>
              );
            })}
          {isResumenes &&
            resumenes.map((mb) => {
              const selected = Number(selectedMoodboardId) === Number(mb.id);
              const fromMeeting = Number(mb.MeetingSessionId) > 0;
              return (
                <Box
                  key={`rs-${mb.id}`}
                  data-tree-id={`resumen:${mb.id}`}
                  onClick={() => onSelectResumen?.(mb.id)}
                  onContextMenu={(e) => openMoveMenu(e, "moodboard", mb)}
                  sx={leafSx(selected, accent)}
                >
                  <Tooltip
                    title={
                      fromMeeting
                        ? t("skillAgent.moodboard.originMeeting")
                        : t("skillAgent.moodboard.originSearch")
                    }
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        flexShrink: 0,
                        color: fromMeeting ? "info.main" : "warning.main",
                        mr: 0.5,
                      }}
                      aria-hidden
                    >
                      {fromMeeting ? (
                        <GroupsOutlinedIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <PhotoLibraryOutlinedIcon sx={{ fontSize: 16 }} />
                      )}
                    </Box>
                  </Tooltip>
                  <Typography
                    variant="body2"
                    fontWeight={selected ? 700 : 500}
                    noWrap
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      lineHeight: 1.25,
                      fontSize: "0.8125rem",
                    }}
                  >
                    {mb.Titulo}
                  </Typography>
                  {leafMoveBtn("moodboard", mb)}
                </Box>
              );
            })}
          {isRapports && node.tilesNode ? (
            <TreeBranch
              key={node.tilesNode.id}
              node={node.tilesNode}
              {...childBranchProps}
            />
          ) : null}
          {isRapports && node.interpretNode ? (
            <TreeBranch
              key={node.interpretNode.id}
              node={node.interpretNode}
              {...childBranchProps}
            />
          ) : null}
          {(isRapportTiles || isRapportInterpret) &&
            rapports.map((rapport) => {
              const selected = Number(selectedRapportId) === Number(rapport.id);
              return (
                <Box
                  key={`rp-${rapport.id}`}
                  data-tree-id={`rapport:${rapport.id}`}
                  onClick={() => onSelectRapport?.(rapport.id)}
                  onContextMenu={(e) => openMoveMenu(e, "rapport", rapport)}
                  sx={leafSx(selected, accent)}
                >
                  <Typography
                    variant="body2"
                    fontWeight={selected ? 700 : 500}
                    noWrap
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      lineHeight: 1.25,
                      fontSize: "0.8125rem",
                    }}
                  >
                    {rapport.Titulo}
                  </Typography>
                  <Chip
                    className="leaf-status"
                    size="small"
                    label={rapportStatusLabel(rapport.Status)}
                    variant="outlined"
                    sx={statusChipSx}
                  />
                  {leafMoveBtn("rapport", rapport)}
                </Box>
              );
            })}
          {(isClientInfos || isProducciones || isPresentaciones || isStatus) &&
            clientInfos.map((ci) => {
              const selected = Number(selectedClientInfoId) === Number(ci.id);
              const leafKind = isProducciones
                ? "produccion"
                : isPresentaciones
                  ? "presentacion"
                  : isStatus
                    ? "status"
                    : "clientInfo";
              return (
                <Box
                  key={`${leafKind}-${ci.id}`}
                  data-tree-id={`clientInfo:${ci.id}`}
                  onClick={() => onSelectClientInfo?.(ci.id)}
                  onContextMenu={(e) => openMoveMenu(e, "clientInfo", ci)}
                  sx={leafSx(selected, accent)}
                >
                  <Typography
                    variant="body2"
                    fontWeight={selected ? 700 : 500}
                    noWrap
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      lineHeight: 1.25,
                      fontSize: "0.8125rem",
                    }}
                  >
                    {ci.Titulo}
                  </Typography>
                  {leafMoveBtn("clientInfo", ci)}
                </Box>
              );
            })}
          {isSketches &&
            (node.moodboards || node.aiDesigns || []).map((ad) => {
              const selected =
                Number(selectedMoodboardId) === Number(ad.id) ||
                Number(selectedId) === Number(ad.id);
              return (
                <Box
                  key={`ai-${ad.id}`}
                  data-tree-id={`aiDesign:${ad.id}`}
                  onClick={() => onSelectAiDesign?.(ad)}
                  onContextMenu={(e) => openMoveMenu(e, "moodboard", ad)}
                  sx={leafSx(selected, accent)}
                >
                  <Typography
                    variant="body2"
                    fontWeight={selected ? 700 : 500}
                    noWrap
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      lineHeight: 1.25,
                      fontSize: "0.8125rem",
                    }}
                  >
                    {ad.Titulo}
                  </Typography>
                  {leafMoveBtn("moodboard", ad)}
                </Box>
              );
            })}
          {isDisenos &&
            tasks.map((task) => {
              const selected = Number(selectedId) === Number(task.id);
              return (
                <Box
                  key={task.id}
                  data-tree-id={`task:${task.id}`}
                  onClick={() => onSelectTask(task.id)}
                  onContextMenu={(e) => openMoveMenu(e, "task", task)}
                  sx={leafSx(selected, accent)}
                >
                  <Typography
                    variant="body2"
                    fontWeight={selected ? 700 : 500}
                    noWrap
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      lineHeight: 1.25,
                      fontSize: "0.8125rem",
                    }}
                  >
                    {task.Titulo}
                  </Typography>
                  <Chip
                    className="leaf-status"
                    size="small"
                    label={taskStatusLabel(task.Status)}
                    color={taskStatusColor(task.Status)}
                    variant="outlined"
                    sx={statusChipSx}
                  />
                  {leafMoveBtn("task", task)}
                </Box>
              );
            })}
        </Box>
      </Collapse>
      <Menu
        open={Boolean(moveMenu)}
        onClose={() => setMoveMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          moveMenu ? { top: moveMenu.mouseY, left: moveMenu.mouseX } : undefined
        }
        onClick={(e) => e.stopPropagation()}
      >
        {ORG_BRANDS.map((brand) => {
          const current = normalizeOrgBrand(
            moveMenu?.item?.OrgBrand || moveMenu?.item?.orgBrand,
          );
          const disabled = current === brand;
          return (
            <MenuItem
              key={brand}
              disabled={disabled}
              onClick={() => {
                const payload = moveMenu;
                setMoveMenu(null);
                if (!payload) return;
                onMoveOrgBrand?.(payload.kind, payload.item, brand);
              }}
            >
              <ListItemIcon>
                <SwapHorizOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t("skillAgent.tree.moveToBrand", {
                  brand,
                  defaultValue: `Mover a ${brand}`,
                })}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}

function parseArtifactMeta(artifact) {
  const raw = artifact?.MetaJson;
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

function ArtifactThumb({ artifact, selected, onToggle, showResearchDebug }) {
  const proposalThumbAccent = "#4338ca";
  const isText =
    artifact.Kind === "text" || parseArtifactMeta(artifact).kind === "research";
  const [src, setSrc] = useState("");
  const [failed, setFailed] = useState(false);
  const [researchOpen, setResearchOpen] = useState(false);

  useEffect(() => {
    if (isText) return undefined;
    let revoked = false;
    let objectUrl = "";
    (async () => {
      try {
        objectUrl = await fetchSkillAgentArtifactBlobUrl(artifact.id);
        if (!revoked) setSrc(objectUrl);
      } catch {
        if (!revoked) {
          setSrc("");
          setFailed(true);
        }
      }
    })();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [artifact.id, isText]);

  if (isText) {
    if (!showResearchDebug) return null;
    const meta = parseArtifactMeta(artifact);
    const brief = meta.brief || artifact.Title || "";
    const brandCount = Array.isArray(meta.brandsOk) ? meta.brandsOk.length : 0;
    return (
      <Box
        sx={{
          gridColumn: "1 / -1",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: alpha("#00838f", 0.04),
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          onClick={() => setResearchOpen((v) => !v)}
          sx={{
            px: 1.25,
            py: 0.75,
            cursor: "pointer",
            userSelect: "none",
            "&:hover": { bgcolor: alpha("#00838f", 0.06) },
          }}
        >
          <Chip
            size="small"
            color="info"
            label="Research"
            sx={{ height: 22 }}
          />
          <Typography variant="subtitle2" fontWeight={800} sx={{ flex: 1 }}>
            {artifact.Title || "Research radar"}
            {brandCount ? (
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1, fontWeight: 600 }}
              >
                {brandCount} marcas
              </Typography>
            ) : null}
          </Typography>
          <ExpandMoreIcon
            fontSize="small"
            sx={{
              color: "text.secondary",
              transform: researchOpen ? "rotate(180deg)" : "none",
              transition: "transform 0.15s ease",
            }}
          />
        </Stack>
        <Collapse in={researchOpen}>
          <Box sx={{ px: 1.5, pb: 1.5, pt: 0.25 }}>
            {Array.isArray(meta.brandsOk) && meta.brandsOk.length ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.75 }}
              >
                {meta.brandsOk.join(" · ")}
              </Typography>
            ) : null}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: "pre-wrap", lineHeight: 1.45 }}
            >
              {brief}
            </Typography>
          </Box>
        </Collapse>
      </Box>
    );
  }

  return (
    <Box
      onClick={onToggle}
      sx={{
        border: "1.5px solid",
        borderColor: selected ? proposalThumbAccent : "divider",
        borderRadius: 2.5,
        overflow: "hidden",
        cursor: "pointer",
        bgcolor: "background.paper",
        position: "relative",
        boxShadow: selected
          ? `0 8px 20px ${alpha(proposalThumbAccent, 0.22)}`
          : `0 4px 12px ${alpha("#0f172a", 0.05)}`,
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": { transform: "translateY(-2px)" },
      }}
    >
      <Box
        sx={{
          aspectRatio: "3 / 4",
          bgcolor: "action.hover",
          display: "grid",
          placeItems: "center",
        }}
      >
        {src ? (
          <Box
            component="img"
            src={src}
            alt={artifact.Title || artifact.FileName}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : failed ? (
          <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>
            Sin preview
          </Typography>
        ) : (
          <CircularProgress size={28} />
        )}
      </Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ px: 1, py: 0.75 }}
      >
        <Checkbox size="small" checked={selected} sx={{ p: 0.25 }} />
        <Typography variant="caption" fontWeight={700} noWrap sx={{ flex: 1 }}>
          {artifact.Title || artifact.FileName}
        </Typography>
        {artifact.DriveFileId ? (
          <Chip
            size="small"
            label="Drive"
            color="success"
            sx={{ height: 20 }}
          />
        ) : null}
      </Stack>
    </Box>
  );
}

function TaskDetailPanel({
  taskId,
  fichas,
  searches = [],
  onChanged,
  onDeleted,
  showResearchDebug = false,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const noSeasonLabel = t("skillAgent.tree.noSeason");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [task, setTask] = useState(null);
  const [form, setForm] = useState(null);
  const [artifacts, setArtifacts] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [autoUploadDrive, setAutoUploadDrive] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [pushMsg, setPushMsg] = useState("");
  const [pushing, setPushing] = useState(false);

  const loadArtifacts = useCallback(async () => {
    if (!taskId) return;
    try {
      const data = await listSkillAgentArtifacts(taskId);
      setArtifacts(Array.isArray(data?.artifacts) ? data.artifacts : []);
      if (data?.status && task && data.status !== task.Status) {
        const row = await getSkillAgentTaskById(taskId);
        setTask(row);
        onChanged?.(row);
      }
    } catch {
      /* ignore */
    }
  }, [taskId, task, onChanged]);

  const load = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    setError("");
    setOkMsg("");
    try {
      const row = await getSkillAgentTaskById(taskId);
      setTask(row);
      setAutoUploadDrive(Boolean(row.AutoUploadDrive));
      setRunning(row.Status === "running");
      const c =
        row.ConstraintsJson && typeof row.ConstraintsJson === "object"
          ? row.ConstraintsJson
          : {};
      setForm({
        Titulo: row.Titulo || "",
        Brief: row.Brief || "",
        Temporada: row.Temporada || "",
        TipoTarea: row.TipoTarea || "otro",
        SkillFichaId: row.SkillFichaId ? String(row.SkillFichaId) : "",
        ConstraintsJson: {
          tipología: c.tipología || c.tipologia || "",
          coste: c.coste || "",
          mercado: c.mercado || "",
          refsAncla: c.refsAncla || "",
          numPropuestas:
            c.numPropuestas != null ? String(c.numPropuestas) : "6",
          searchId: c.searchId != null ? String(c.searchId) : "",
        },
      });
      try {
        const data = await listSkillAgentArtifacts(taskId);
        setArtifacts(Array.isArray(data?.artifacts) ? data.artifacts : []);
      } catch {
        setArtifacts([]);
      }
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.taskDetail.loadError"),
      );
    } finally {
      setLoading(false);
    }
  }, [taskId, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!taskId || !running) return undefined;
    const pollTimer = setInterval(async () => {
      try {
        const row = await getSkillAgentTaskById(taskId);
        setTask(row);
        onChanged?.(row);
        if (row.Status !== "running") {
          setRunning(false);
          const data = await listSkillAgentArtifacts(taskId);
          setArtifacts(Array.isArray(data?.artifacts) ? data.artifacts : []);
          if (row.Status === "needs_review") {
            setOkMsg(
              row.ResultSummary || t("skillAgent.taskDetail.proposalsReady"),
            );
          } else if (row.Status === "failed") {
            setError(
              row.ResultSummary || t("skillAgent.taskDetail.proposalsFailed"),
            );
          }
        }
      } catch {
        /* keep polling */
      }
    }, 4000);
    return () => clearInterval(pollTimer);
  }, [taskId, running, onChanged, t]);

  const readOnly = task && ["running", "done"].includes(task.Status);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const constraints = {
        ...form.ConstraintsJson,
        numPropuestas: Number(form.ConstraintsJson.numPropuestas) || 6,
        searchId: form.ConstraintsJson.searchId
          ? Number(form.ConstraintsJson.searchId)
          : undefined,
      };
      if (!constraints.searchId) delete constraints.searchId;
      const updated = await updateSkillAgentTask(taskId, {
        Titulo: form.Titulo,
        Brief: form.Brief,
        Temporada: form.Temporada,
        TipoTarea: form.TipoTarea,
        SkillFichaId: form.SkillFichaId ? Number(form.SkillFichaId) : undefined,
        ConstraintsJson: constraints,
      });
      setTask(updated);
      setOkMsg(
        updated?.PromptSnapshot
          ? t("skillAgent.taskDetail.savedWithPrompt")
          : t("skillAgent.taskDetail.savedPlain"),
      );
      onChanged?.(updated);
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.taskDetail.saveError"),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRun = async () => {
    setSaving(true);
    setError("");
    setOkMsg("");
    try {
      await handleSaveInner();
      const res = await runSkillAgentTask(taskId, {
        autoUploadDrive,
        customerFolderName: taskClientLabel(task),
      });
      setTask(res.task);
      setRunning(true);
      setSelectedIds(new Set());
      setOkMsg(res.message || t("skillAgent.taskDetail.generationStarted"));
      onChanged?.(res.task);
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.taskDetail.launchError"),
      );
      setRunning(false);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveInner = async () => {
    const constraints = {
      ...form.ConstraintsJson,
      numPropuestas: Number(form.ConstraintsJson.numPropuestas) || 6,
      searchId: form.ConstraintsJson.searchId
        ? Number(form.ConstraintsJson.searchId)
        : undefined,
    };
    if (!constraints.searchId) delete constraints.searchId;
    const updated = await updateSkillAgentTask(taskId, {
      Titulo: form.Titulo,
      Brief: form.Brief,
      Temporada: form.Temporada,
      TipoTarea: form.TipoTarea,
      SkillFichaId: form.SkillFichaId ? Number(form.SkillFichaId) : undefined,
      ConstraintsJson: constraints,
    });
    setTask(updated);
    return updated;
  };

  const handleDispatch = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await dispatchSkillAgentTask(taskId, {
        promptOnly: true,
        customerFolderName: taskClientLabel(task),
      });
      setTask(res.task);
      setOkMsg(
        res.task?.DriveFolderUrl
          ? t("skillAgent.taskDetail.driveReadyAdvanced")
          : t("skillAgent.taskDetail.driveFailed", {
              detail:
                res.driveNote ||
                res.driveStatus?.initError ||
                t("skillAgent.taskDetail.driveFailedFallback"),
            }),
      );
      onChanged?.(res.task);
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.taskDetail.prepareError"),
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handlePushDrive = async (all) => {
    setPushing(true);
    setSaving(true);
    setPushMsg("");
    setError("");
    setOkMsg("");
    try {
      if (!all && !selectedIds.size) {
        const pickMsg = t("skillAgent.taskDetail.needSelectProposalClick");
        setPushMsg(`error:${pickMsg}`);
        setError(pickMsg);
        return;
      }
      setPushMsg(`info:${t("skillAgent.taskDetail.pushInfo")}`);
      const body = all
        ? { all: true, customerFolderName: taskClientLabel(task) }
        : {
            artifactIds: [...selectedIds],
            customerFolderName: taskClientLabel(task),
          };
      const res = await pushSkillAgentArtifactsToDrive(taskId, body);
      setArtifacts(Array.isArray(res.artifacts) ? res.artifacts : artifacts);
      if (res.task) {
        setTask(res.task);
        onChanged?.(res.task);
      }
      const n = res.uploaded?.length || 0;
      const msg = all
        ? t("skillAgent.taskDetail.uploadedProposals", { n })
        : t("skillAgent.taskDetail.uploadedSelectedProposals", { n });
      setOkMsg(msg);
      setPushMsg(`ok:${msg}`);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        t("skillAgent.taskDetail.uploadDriveError");
      setError(msg);
      setPushMsg(`error:${msg}`);
    } finally {
      setPushing(false);
      setSaving(false);
    }
  };

  const handleDone = async () => {
    setSaving(true);
    try {
      const updated = await markSkillAgentTaskDone(taskId);
      setTask(updated);
      setOkMsg(t("skillAgent.taskDetail.markedDone"));
      onChanged?.(updated);
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.taskDetail.errorGeneric"),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm(t("skillAgent.taskDetail.confirmCancel"))) return;
    setSaving(true);
    try {
      const updated = await cancelSkillAgentTask(taskId);
      setTask(updated);
      setRunning(false);
      onChanged?.(updated);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t("skillAgent.taskDetail.confirmDelete"))) return;
    try {
      await deleteSkillAgentTask(taskId);
      onDeleted?.(taskId);
    } catch {
      setError(t("skillAgent.taskDetail.deleteError"));
    }
  };

  const copyPrompt = async () => {
    if (!task?.PromptSnapshot) return;
    try {
      await navigator.clipboard.writeText(task.PromptSnapshot);
      setOkMsg(t("skillAgent.taskDetail.promptCopiedShort"));
    } catch {
      setError(t("skillAgent.taskDetail.copyFailed"));
    }
  };

  if (loading || !form) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  const canLaunch =
    task.Status !== "done" &&
    task.Status !== "cancelled" &&
    task.Status !== "running";

  const imageArtifacts = artifacts.filter(
    (a) => a.Kind !== "text" && parseArtifactMeta(a).kind !== "research",
  );

  const proposalAccent = "#4338ca";

  return (
    <Stack
      spacing={2}
      sx={{
        p: { xs: 1.5, md: 2.5 },
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.75,
          borderRadius: 3,
          border: "1px solid",
          borderColor: alpha(proposalAccent, 0.22),
          background: `linear-gradient(135deg, ${alpha(proposalAccent, 0.14)} 0%, ${alpha(
            theme.palette.background.paper,
            0.92,
          )} 62%)`,
          boxShadow: `0 8px 24px ${alpha(proposalAccent, 0.08)}`,
        }}
      >
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                bgcolor: alpha(proposalAccent, 0.16),
                color: proposalAccent,
              }}
            >
              <DesignServicesOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  letterSpacing: 0.4,
                  color: proposalAccent,
                  textTransform: "uppercase",
                }}
              >
                {t("skillAgent.taskDetail.orderLabel", { id: task.id })}
              </Typography>
              <Typography
                variant="h5"
                fontWeight={800}
                sx={{ lineHeight: 1.2 }}
              >
                {task.Titulo}
              </Typography>
            </Box>
          </Stack>
          <Tooltip title={t("skillAgent.taskDetail.deleteTooltip")}>
            <IconButton
              color="error"
              onClick={handleDelete}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.error.main, 0.08),
                borderRadius: 1.5,
              }}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack
          direction="row"
          spacing={0.75}
          sx={{ mt: 1.25 }}
          flexWrap="wrap"
          useFlexGap
        >
          <Chip
            size="small"
            icon={<CalendarMonthOutlinedIcon />}
            label={task.Temporada || noSeasonLabel}
            sx={{
              fontWeight: 700,
              bgcolor: alpha(theme.palette.background.paper, 0.8),
            }}
          />
          <Chip
            size="small"
            icon={<BusinessOutlinedIcon />}
            label={taskClientLabel(task)}
            sx={{
              fontWeight: 700,
              bgcolor: alpha(theme.palette.background.paper, 0.8),
            }}
          />
          <Chip
            size="small"
            label={taskStatusLabel(task.Status)}
            color={taskStatusColor(task.Status)}
            sx={{ fontWeight: 800 }}
          />
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {okMsg && (
        <Alert severity="success" onClose={() => setOkMsg("")}>
          {okMsg}
        </Alert>
      )}
      {task.ResultSummary && task.Status === "failed" && (
        <Alert severity="error">{task.ResultSummary}</Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 3,
          border: "1px solid",
          borderColor: alpha(proposalAccent, 0.16),
          bgcolor: alpha(theme.palette.background.paper, 0.92),
          boxShadow: `0 6px 18px ${alpha("#0f172a", 0.04)}`,
        }}
      >
        <Stack spacing={1.5}>
          <TextField
            label={t("skillAgent.form.title")}
            size="small"
            value={form.Titulo}
            disabled={readOnly}
            onChange={(e) => setForm((p) => ({ ...p, Titulo: e.target.value }))}
            sx={pageFieldSx}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <FormControl
              size="small"
              sx={{ flex: 1, ...pageFieldSx }}
              disabled={readOnly}
            >
              <InputLabel>{t("skillAgent.form.clientFicha")}</InputLabel>
              <Select
                label={t("skillAgent.form.clientFicha")}
                value={form.SkillFichaId}
                onChange={(e) =>
                  setForm((p) => ({ ...p, SkillFichaId: e.target.value }))
                }
              >
                <MenuItem value="">—</MenuItem>
                {fichas.map((f) => (
                  <MenuItem key={f.id} value={String(f.id)}>
                    {f.Titulo || f.SkillSlug}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: 140, ...pageFieldSx }}
              disabled={readOnly}
            >
              <InputLabel>{t("skillAgent.form.taskType")}</InputLabel>
              <Select
                label={t("skillAgent.form.taskType")}
                value={form.TipoTarea}
                onChange={(e) =>
                  setForm((p) => ({ ...p, TipoTarea: e.target.value }))
                }
              >
                {TASK_TYPE_KEYS.map((k) => (
                  <MenuItem key={k} value={k}>
                    {taskTypeLabel(k)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t("skillAgent.form.season")}
              size="small"
              value={form.Temporada}
              disabled={readOnly}
              onChange={(e) =>
                setForm((p) => ({ ...p, Temporada: e.target.value }))
              }
              sx={{ minWidth: 110, ...pageFieldSx }}
            />
          </Stack>
          <TextField
            label={t("skillAgent.form.brief")}
            multiline
            minRows={3}
            value={form.Brief}
            disabled={readOnly}
            onChange={(e) => setForm((p) => ({ ...p, Brief: e.target.value }))}
            sx={pageFieldSx}
          />
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2.5,
              bgcolor: alpha(proposalAccent, 0.045),
              border: "1px solid",
              borderColor: alpha(proposalAccent, 0.12),
            }}
          >
            <Typography
              variant="caption"
              fontWeight={800}
              sx={{ display: "block", mb: 1.25, color: proposalAccent }}
            >
              {t("skillAgent.form.constraintsHeading")}
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.25}
              flexWrap="wrap"
              useFlexGap
            >
              {["tipología", "coste", "mercado", "refsAncla"].map((key) => (
                <TextField
                  key={key}
                  label={key}
                  size="small"
                  value={form.ConstraintsJson[key] || ""}
                  disabled={readOnly}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      ConstraintsJson: {
                        ...p.ConstraintsJson,
                        [key]: e.target.value,
                      },
                    }))
                  }
                  sx={{ flex: 1, ...pageFieldSx }}
                />
              ))}
              <TextField
                label={t("skillAgent.form.numProposals")}
                size="small"
                type="number"
                inputProps={{ min: 1, max: 8 }}
                value={form.ConstraintsJson.numPropuestas || "6"}
                disabled={readOnly}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    ConstraintsJson: {
                      ...p.ConstraintsJson,
                      numPropuestas: e.target.value,
                    },
                  }))
                }
                sx={{ width: 120, ...pageFieldSx }}
              />
              <FormControl size="small" sx={{ minWidth: 200, ...pageFieldSx }}>
                <InputLabel>{t("skillAgent.form.useSearch")}</InputLabel>
                <Select
                  label={t("skillAgent.form.useSearch")}
                  value={form.ConstraintsJson.searchId || ""}
                  disabled={readOnly}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      ConstraintsJson: {
                        ...p.ConstraintsJson,
                        searchId: e.target.value,
                      },
                    }))
                  }
                >
                  <MenuItem value="">
                    {t("skillAgent.form.radarOnRun")}
                  </MenuItem>
                  {(searches || [])
                    .filter((s) => s.Status === "done")
                    .map((s) => (
                      <MenuItem key={s.id} value={String(s.id)}>
                        {s.Titulo}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Stack>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={autoUploadDrive}
                disabled={readOnly}
                onChange={(e) => setAutoUploadDrive(e.target.checked)}
              />
            }
            label={t("skillAgent.taskDetail.autoUploadDrive")}
          />

          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{
              pt: 0.5,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            {!readOnly && (
              <Button
                variant="outlined"
                startIcon={<SaveOutlinedIcon />}
                disabled={saving}
                onClick={handleSave}
                sx={{
                  borderRadius: 2,
                  fontWeight: 800,
                  textTransform: "none",
                  minHeight: 40,
                }}
              >
                {t("skillAgent.common.save")}
              </Button>
            )}
            {canLaunch && (
              <Button
                variant="contained"
                startIcon={
                  saving || running ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PlayArrowOutlinedIcon />
                  )
                }
                sx={{
                  ...pagePrimaryButtonSx(theme),
                  bgcolor: proposalAccent,
                  "&:hover": {
                    bgcolor: proposalAccent,
                    filter: "brightness(0.92)",
                  },
                }}
                disabled={saving || running}
                onClick={handleRun}
              >
                {t("skillAgent.taskDetail.launchProposals")}
              </Button>
            )}
            {task.Status === "running" && (
              <Chip
                icon={<CircularProgress size={14} />}
                label={t("skillAgent.taskDetail.generatingChip")}
                color="info"
              />
            )}
            {task.Status !== "done" && task.Status !== "cancelled" && (
              <Button
                color="success"
                variant="contained"
                startIcon={<CheckCircleOutlineIcon />}
                disabled={saving || task.Status === "running"}
                onClick={handleDone}
                sx={{
                  borderRadius: 2,
                  fontWeight: 800,
                  textTransform: "none",
                  minHeight: 40,
                }}
              >
                {t("skillAgent.taskDetail.doneButton")}
              </Button>
            )}
            {task.Status !== "cancelled" && task.Status !== "done" && (
              <Button
                color="inherit"
                disabled={saving}
                onClick={handleCancel}
                sx={{
                  borderRadius: 2,
                  fontWeight: 700,
                  textTransform: "none",
                  minHeight: 40,
                }}
              >
                {t("skillAgent.common.cancel")}
              </Button>
            )}
            {task.Status === "done" || task.Status === "cancelled" ? (
              <Button
                variant="outlined"
                disabled={saving}
                onClick={async () => {
                  setSaving(true);
                  try {
                    const updated = await updateSkillAgentTask(taskId, {
                      Status: "draft",
                    });
                    setTask(updated);
                    setRunning(false);
                    setOkMsg(t("skillAgent.taskDetail.reopenSuccess"));
                    onChanged?.(updated);
                  } catch (err) {
                    setError(
                      err?.response?.data?.error ||
                        t("skillAgent.taskDetail.reopenError"),
                    );
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                {t("skillAgent.taskDetail.reopen")}
              </Button>
            ) : null}
            {task.DriveFolderUrl && (
              <Button
                startIcon={<OpenInNewOutlinedIcon />}
                href={task.DriveFolderUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("skillAgent.taskDetail.openDriveFolder")}
              </Button>
            )}
            <Button
              size="small"
              variant="text"
              disabled={saving || task.Status === "running"}
              onClick={handleDispatch}
            >
              {t("skillAgent.taskDetail.prepareFolderOnly")}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 3,
          border: "1px solid",
          borderColor: alpha("#1b5e20", 0.16),
          background: `linear-gradient(180deg, ${alpha("#1b5e20", 0.07)} 0%, ${alpha(
            theme.palette.background.paper,
            0.96,
          )} 72px)`,
          boxShadow: `0 6px 18px ${alpha("#0f172a", 0.04)}`,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
          spacing={1}
          sx={{ mb: 1.5 }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={800}>
              {t("skillAgent.taskDetail.resultsTitle")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {imageArtifacts.length
                ? t("skillAgent.taskDetail.resultsHintSelected", {
                    n: imageArtifacts.length,
                  })
                : t("skillAgent.taskDetail.resultsHintEmpty")}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              variant="outlined"
              startIcon={
                pushing ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <CloudUploadOutlinedIcon />
                )
              }
              disabled={pushing || saving || !imageArtifacts.length}
              onClick={() => handlePushDrive(false)}
            >
              {t("skillAgent.taskDetail.saveSelectedLabel")}
              {selectedIds.size ? ` (${selectedIds.size})` : ""}
            </Button>
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={
                pushing ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <CloudUploadOutlinedIcon />
                )
              }
              disabled={pushing || saving || !imageArtifacts.length}
              onClick={() => handlePushDrive(true)}
            >
              {t("skillAgent.taskDetail.saveAll")}
            </Button>
            <Button
              size="small"
              onClick={loadArtifacts}
              disabled={saving || pushing}
            >
              {t("skillAgent.taskDetail.refresh")}
            </Button>
          </Stack>
        </Stack>

        {pushMsg ? (
          <Alert
            severity={
              pushMsg.startsWith("ok:")
                ? "success"
                : pushMsg.startsWith("info:")
                  ? "info"
                  : "error"
            }
            sx={{ mb: 1.5 }}
            onClose={() => setPushMsg("")}
          >
            {pushMsg.replace(/^(ok|error|info):/, "")}
          </Alert>
        ) : null}

        {task.Status === "running" && !imageArtifacts.length ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {t("skillAgent.taskDetail.generatingPlaceholder")}
            </Typography>
          </Box>
        ) : imageArtifacts.length ||
          (showResearchDebug && artifacts.some((a) => a.Kind === "text")) ? (
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: {
                xs: "1fr 1fr",
                sm: "repeat(3, 1fr)",
                md: "repeat(4, 1fr)",
              },
            }}
          >
            {artifacts.map((a) => (
              <ArtifactThumb
                key={a.id}
                artifact={a}
                selected={selectedIds.has(a.id)}
                onToggle={() => toggleSelect(a.id)}
                showResearchDebug={showResearchDebug}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" component="span">
            <Trans
              i18nKey="skillAgent.taskDetail.noProposalsHtml"
              components={{ b: <b /> }}
            />
          </Typography>
        )}
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box
          onClick={() => setPromptOpen((v) => !v)}
          sx={{
            px: 2,
            py: 1.35,
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
            background: `linear-gradient(135deg, ${alpha("#0d47a1", 0.08)} 0%, transparent 70%)`,
            "&:hover": { bgcolor: alpha("#0d47a1", 0.06) },
          }}
        >
          <ExpandMoreIcon
            sx={{
              transform: promptOpen ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.15s",
              fontSize: 20,
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {t("skillAgent.taskDetail.technicalSection")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("skillAgent.taskDetail.promptHeading")}
              {task.SkillVersionUsed != null
                ? ` · skill v${task.SkillVersionUsed}`
                : ""}
              {task.DispatchMode ? ` · ${task.DispatchMode}` : ""}
            </Typography>
          </Box>
          {task.PromptSnapshot && (
            <Button
              size="small"
              startIcon={<ContentCopyOutlinedIcon />}
              onClick={(e) => {
                e.stopPropagation();
                copyPrompt();
              }}
            >
              {t("skillAgent.common.copy")}
            </Button>
          )}
        </Box>
        <Collapse in={promptOpen}>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: alpha("#000", 0.03),
              fontSize: "0.75rem",
              whiteSpace: "pre-wrap",
              maxHeight: 280,
              overflow: "auto",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            }}
          >
            {task.PromptSnapshot || t("skillAgent.taskDetail.promptEmpty")}
          </Box>
        </Collapse>
      </Paper>
    </Stack>
  );
}

export default function SkillAgentTasksPage() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { contentMargin } = useContext(SidebarContext);

  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [sessionUser, setSessionUser] = useState(null);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [searches, setSearches] = useState([]);
  const [moodboards, setMoodboards] = useState([]);
  const [rapports, setRapports] = useState([]);
  const [rootGroups, setRootGroups] = useState([]);
  const [projectBundles, setProjectBundles] = useState({});
  const [createLinkCtx, setCreateLinkCtx] = useState({
    ProjectId: null,
    MeetingSessionId: null,
    OrgBrand: null,
  });
  const [searchBrands, setSearchBrands] = useState([]);
  const [fichas, setFichas] = useState([]);
  const [meta, setMeta] = useState(null);
  const [scrapingBeeStatus, setScrapingBeeStatus] = useState(null);
  const [groupBy, setGroupBy] = useState("season");
  const [treeTypeFilter, setTreeTypeFilter] = useState("all");
  const [treeFilterMenuEl, setTreeFilterMenuEl] = useState(null);
  const [treeFilterLoading, setTreeFilterLoading] = useState(false);
  const projectBundlesRef = useRef(projectBundles);
  projectBundlesRef.current = projectBundles;
  const [columnPath, setColumnPath] = useState([]);
  const [compassFavorites, setCompassFavorites] = useState([]);
  const compassFavoritesUserIdRef = useRef(null);
  const [treeCollapsed, setTreeCollapsed] = useState(() => {
    try {
      return localStorage.getItem("skillAgentTreeCollapsed") === "1";
    } catch {
      return false;
    }
  });
  /** Tamaño del overlay Compás (arrastre borde derecho / inferior). */
  const [compassPanelSize, setCompassPanelSize] = useState(() => {
    try {
      const raw = JSON.parse(
        localStorage.getItem("skillAgentCompassPanelSize") || "null",
      );
      if (!raw || typeof raw !== "object") return { width: null, height: null };
      const width = Number(raw.width);
      const height = Number(raw.height);
      return {
        width: Number.isFinite(width) && width > 0 ? width : null,
        height: Number.isFinite(height) && height > 0 ? height : null,
      };
    } catch {
      return { width: null, height: null };
    }
  });
  const compassPanelRef = useRef(null);
  const compassResizeRef = useRef(null);
  /** Rail de captura aparcado en la columna del árbol (como Blablaton). */
  const [captureParked, setCaptureParked] = useState(false);
  const [captureSlotEl, setCaptureSlotEl] = useState(null);
  const unparkCaptureRef = useRef(null);
  const captureParkedRef = useRef(false);
  captureParkedRef.current = captureParked;
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [selectedId, setSelectedId] = useState(
    () => searchParams.get("id") || "",
  );
  const [selectedSearchId, setSelectedSearchId] = useState(
    () => searchParams.get("search") || "",
  );
  const [selectedMoodboardId, setSelectedMoodboardId] = useState(
    () => searchParams.get("moodboard") || searchParams.get("resumen") || "",
  );
  const [selectedRapportId, setSelectedRapportId] = useState(
    () => searchParams.get("rapport") || "",
  );
  const [selectedClientInfoId, setSelectedClientInfoId] = useState(
    () => searchParams.get("clientInfo") || "",
  );
  const [selectedAiDesignId, setSelectedAiDesignId] = useState(
    () => searchParams.get("aiDesign") || "",
  );
  const [aiDesignCompassState, setAiDesignCompassState] = useState(() =>
    location.state?.fromCompass && typeof location.state === "object"
      ? location.state
      : null,
  );

  // Deep link /aiDesigns/:id → ?aiDesign= llega con state Compás.
  useEffect(() => {
    if (!location.state?.fromCompass || typeof location.state !== "object") {
      return;
    }
    const id = Number(
      location.state.AiDesignId ||
        location.state.MoodboardId ||
        searchParams.get("aiDesign"),
    );
    if (!Number.isFinite(id) || id <= 0) return;
    setAiDesignCompassState(location.state);
    if (String(selectedAiDesignId) !== String(id)) {
      setSelectedAiDesignId(String(id));
    }
  }, [location.state, searchParams, selectedAiDesignId]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedMeetingId, setSelectedMeetingId] = useState(
    () => searchParams.get("meeting") || "",
  );
  const selectedMeetingProjectIdRef = useRef(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [taskCreateMode, setTaskCreateMode] = useState("skills");
  const [createSearchOpen, setCreateSearchOpen] = useState(false);
  const [createMoodboardOpen, setCreateMoodboardOpen] = useState(false);
  const [createRapportOpen, setCreateRapportOpen] = useState(false);
  const [hubCreateOpen, setHubCreateOpen] = useState(false);
  const [hubCreateNode, setHubCreateNode] = useState(null);
  const [projectSeasonEdit, setProjectSeasonEdit] = useState(null);
  const [projectSeasonValue, setProjectSeasonValue] = useState("");
  const [projectNameValue, setProjectNameValue] = useState("");
  const [projectSeasonSaving, setProjectSeasonSaving] = useState(false);
  const [projectSeasonError, setProjectSeasonError] = useState("");
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [createProjectSaving, setCreateProjectSaving] = useState(false);
  const [createProjectError, setCreateProjectError] = useState("");
  const [createProjectForm, setCreateProjectForm] = useState({
    Name: "",
    Temporada: "",
    customer: null,
    customerInput: "",
  });
  const [customerOptions, setCustomerOptions] = useState([]);
  const [catalogSeasons, setCatalogSeasons] = useState([]);
  const [deleteProjectTarget, setDeleteProjectTarget] = useState(null);
  const [deleteProjectSaving, setDeleteProjectSaving] = useState(false);
  const [deleteProjectError, setDeleteProjectError] = useState("");
  const [moveOrgBrandTarget, setMoveOrgBrandTarget] = useState(null);
  const [moveOrgBrandSaving, setMoveOrgBrandSaving] = useState(false);
  const [moveOrgBrandError, setMoveOrgBrandError] = useState("");
  const [syncingBlablaton, setSyncingBlablaton] = useState(false);
  const [searchCatalogOpen, setSearchCatalogOpen] = useState(false);
  const [meetingCreateOpen, setMeetingCreateOpen] = useState(false);
  const [meetingCreateNode, setMeetingCreateNode] = useState(null);
  const [meetingCreateTitle, setMeetingCreateTitle] = useState("");
  const [creatingMeeting, setCreatingMeeting] = useState(false);
  const [searchContext, setSearchContext] = useState(null);
  const [moodboardContext, setMoodboardContext] = useState(null);
  const [helpTopic, setHelpTopic] = useState("");
  const [creating, setCreating] = useState(false);
  const [creatingSearch, setCreatingSearch] = useState(false);
  const [creatingMoodboard, setCreatingMoodboard] = useState(false);
  const [creatingRapport, setCreatingRapport] = useState(false);
  const [moodboardFormError, setMoodboardFormError] = useState("");
  const [taskFormError, setTaskFormError] = useState("");
  const [searchFormError, setSearchFormError] = useState("");
  const [rapportFormError, setRapportFormError] = useState("");
  const [newTask, setNewTask] = useState({
    SkillFichaId: "",
    CustomerId: null,
    CustomerName: "",
    Titulo: "",
    Temporada: "",
    TipoTarea: "propuestas",
    Brief: "",
    searchId: "",
    lockClient: false,
    lockSeason: false,
  });
  const [newSearch, setNewSearch] = useState({
    Titulo: "",
    Temporada: "",
    Tipologia: "chaqueta Urban",
    CustomerId: "",
    CustomerName: "",
    branchPath: "",
    brandIds: [],
    urlSelection: {},
    customSources: [],
    maxImagesPerBrand: "3",
  });
  const [newMoodboard, setNewMoodboard] = useState({
    Titulo: "",
    CategoryLabel: "Aviator - JKT",
    Temporada: "",
    CustomerId: "",
    CustomerName: "",
    Kind: "moodboard",
    CaratulaId: "",
  });
  const [caratulasOpen, setCaratulasOpen] = useState(false);
  const [editingCaratula, setEditingCaratula] = useState(null);
  const [caratulaOptions, setCaratulaOptions] = useState([]);
  const [loadingCaratulas, setLoadingCaratulas] = useState(false);
  const [newRapport, setNewRapport] = useState({
    Titulo: "",
    Temporada: "",
    CustomerId: "",
    CustomerName: "",
    Kind: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }
      const decoded = jwtDecode(token);
      const user = await getUserById(decoded.userId);
      setSessionUser(user || null);
      const favoritesUserId = Number(user?.id || decoded.userId) || null;
      compassFavoritesUserIdRef.current = favoritesUserId;
      if (!user?.Permiso_TareasAgentes) {
        setForbidden(true);
        return;
      }
      // Sync Blablaton → Compás solo a mano (botón). Auto-sync reasignaba
      // reuniones ya ligadas a un proyecto si el título no coincidía.
      const [roots, fichasData, metaData, searchesMeta] = await Promise.all([
        listSkillAgentProjectTreeRoots(),
        listSkillFichas({}),
        getSkillAgentTasksMeta(),
        getSkillAgentSearchesMeta().catch(() => null),
      ]);
      {
        const nextRoots = Array.isArray(roots) ? roots : [];
        setRootGroups(nextRoots);
        const loaded = loadCompassFavorites(favoritesUserId);
        const next = syncCompassFavoriteLabelsFromRoots(loaded, nextRoots);
        saveCompassFavorites(next, favoritesUserId);
        setCompassFavorites(next);
      }
      setProjectBundles({});
      setRows([]);
      setSearches([]);
      setMoodboards([]);
      setRapports([]);
      setFichas(Array.isArray(fichasData) ? fichasData : []);
      setMeta(metaData || null);
      setScrapingBeeStatus(searchesMeta?.scrapingBee || null);
      setSearchBrands(
        (Array.isArray(searchesMeta?.brands) ? searchesMeta.brands : [])
          .slice()
          .sort((a, b) =>
            String(a.name || a.id).localeCompare(String(b.name || b.id), "es", {
              sensitivity: "base",
            }),
          ),
      );
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 403) setForbidden(true);
      else setError(t("skillAgent.page.loadTasksError"));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  // Sync selection from URL
  useEffect(() => {
    if (loading || forbidden) return;
    const urlId = searchParams.get("id");
    const urlSearch = searchParams.get("search");
    const urlMoodboard = searchParams.get("moodboard");
    const urlResumen = searchParams.get("resumen");
    const urlRapport = searchParams.get("rapport");
    const urlClientInfo = searchParams.get("clientInfo");
    const urlMeeting = searchParams.get("meeting");
    const urlAiDesign = searchParams.get("aiDesign");
    if (urlMeeting) {
      if (String(selectedMeetingId) !== String(urlMeeting)) {
        setSelectedMeetingId(String(urlMeeting));
        setSelectedRapportId("");
        setSelectedClientInfoId("");
        setSelectedMoodboardId("");
        setSelectedSearchId("");
        setSelectedId("");
        setSelectedAiDesignId("");
        setSelectedProjectId("");
      }
      return;
    }
    if (urlAiDesign) {
      if (String(selectedAiDesignId) !== String(urlAiDesign)) {
        setSelectedAiDesignId(String(urlAiDesign));
        setSelectedMeetingId("");
        setSelectedRapportId("");
        setSelectedClientInfoId("");
        setSelectedMoodboardId("");
        setSelectedSearchId("");
        setSelectedId("");
        setSelectedProjectId("");
      }
      return;
    }
    if (urlClientInfo) {
      if (String(selectedClientInfoId) !== String(urlClientInfo)) {
        setSelectedClientInfoId(String(urlClientInfo));
        setSelectedMeetingId("");
        setSelectedRapportId("");
        setSelectedMoodboardId("");
        setSelectedSearchId("");
        setSelectedId("");
        setSelectedAiDesignId("");
        setSelectedProjectId("");
      }
      return;
    }
    if (urlRapport) {
      if (String(selectedRapportId) !== String(urlRapport)) {
        setSelectedRapportId(String(urlRapport));
        setSelectedClientInfoId("");
        setSelectedMeetingId("");
        setSelectedMoodboardId("");
        setSelectedSearchId("");
        setSelectedId("");
        setSelectedAiDesignId("");
        setSelectedProjectId("");
      }
      return;
    }
    if (urlResumen) {
      if (String(selectedMoodboardId) !== String(urlResumen)) {
        setSelectedMoodboardId(String(urlResumen));
        setSelectedMeetingId("");
        setSelectedRapportId("");
        setSelectedClientInfoId("");
        setSelectedSearchId("");
        setSelectedId("");
        setSelectedAiDesignId("");
        setSelectedProjectId("");
      }
      return;
    }
    if (urlMoodboard) {
      if (String(selectedMoodboardId) !== String(urlMoodboard)) {
        setSelectedMoodboardId(String(urlMoodboard));
        setSelectedMeetingId("");
        setSelectedRapportId("");
        setSelectedClientInfoId("");
        setSelectedSearchId("");
        setSelectedId("");
        setSelectedAiDesignId("");
        setSelectedProjectId("");
      }
      return;
    }
    if (urlSearch) {
      if (String(selectedSearchId) !== String(urlSearch)) {
        setSelectedSearchId(String(urlSearch));
        setSelectedMeetingId("");
        setSelectedMoodboardId("");
        setSelectedRapportId("");
        setSelectedClientInfoId("");
        setSelectedId("");
        setSelectedAiDesignId("");
        setSelectedProjectId("");
      }
      return;
    }
    if (urlId) {
      if (String(selectedId) !== String(urlId)) {
        setSelectedId(String(urlId));
        setSelectedProjectId("");
        setSelectedMeetingId("");
        setSelectedSearchId("");
        setSelectedMoodboardId("");
        setSelectedRapportId("");
        setSelectedClientInfoId("");
        setSelectedAiDesignId("");
      }
    }
  }, [
    loading,
    forbidden,
    searchParams,
    selectedId,
    selectedSearchId,
    selectedMoodboardId,
    selectedRapportId,
    selectedClientInfoId,
    selectedMeetingId,
    selectedAiDesignId,
  ]);

  const tree = useMemo(
    () =>
      filterCompassTreeByType(
        buildCompassProjectsTree(rootGroups, projectBundles, groupBy, q),
        treeTypeFilter,
      ),
    [rootGroups, projectBundles, groupBy, q, treeTypeFilter, i18n.language],
  );

  const treeTypeFilterMeta = useMemo(
    () => ({
      all: {
        label: t("skillAgent.tree.filterAll", { defaultValue: "Todo" }),
        hint: t("skillAgent.tree.filterAllHint", {
          defaultValue: "Árbol completo",
        }),
        accent: "#00897b",
        Icon: WidgetsOutlinedIcon,
      },
      meetings: {
        label: t("skillAgent.tree.filterMeetings", {
          defaultValue: "Meetings",
        }),
        hint: t("skillAgent.tree.filterMeetingsHint", {
          defaultValue: "Reuniones y visitas",
        }),
        accent: "#e65100",
        Icon: CalendarMonthOutlinedIcon,
      },
      moodboards: {
        label: t("skillAgent.tree.filterMoodboards", {
          defaultValue: "Moodboards",
        }),
        hint: t("skillAgent.tree.filterMoodboardsHint", {
          defaultValue: "Collages y tableros visuales",
        }),
        accent: "#6a1b9a",
        Icon: DashboardCustomizeOutlinedIcon,
      },
      propuestas: {
        label: t("skillAgent.tree.filterPropuestas", {
          defaultValue: "Propuestas",
        }),
        hint: t("skillAgent.tree.filterPropuestasHint", {
          defaultValue: "Collages desde Sketch, meeting y SEARCH",
        }),
        accent: "#7c3aed",
        Icon: AutoFixHighOutlinedIcon,
      },
      prints: {
        label: t("skillAgent.tree.filterPrints", { defaultValue: "Prints" }),
        hint: t("skillAgent.tree.filterPrintsHint", {
          defaultValue: "Tiles e interpretaciones",
        }),
        accent: "#a16207",
        Icon: TextureOutlinedIcon,
      },
      clientInfo: {
        label: t("skillAgent.tree.filterClientInfo", {
          defaultValue: "Client Info",
        }),
        hint: t("skillAgent.tree.filterClientInfoHint", {
          defaultValue: "Dossier del cliente",
        }),
        accent: "#0277bd",
        Icon: InfoOutlinedIcon,
      },
      produccion: {
        label: t("skillAgent.tree.filterProduccion", {
          defaultValue: "Producción",
        }),
        hint: t("skillAgent.tree.filterProduccionHint", {
          defaultValue: "Archivos de producción",
        }),
        accent: "#bf360c",
        Icon: PrecisionManufacturingOutlinedIcon,
      },
      presentaciones: {
        label: t("skillAgent.tree.filterPresentaciones", {
          defaultValue: "Presentaciones PDF",
        }),
        hint: t("skillAgent.tree.filterPresentacionesHint", {
          defaultValue: "PDFs de muestras y presentaciones",
        }),
        accent: "#ad1457",
        Icon: PictureAsPdfOutlinedIcon,
      },
      status: {
        label: t("skillAgent.tree.filterStatus", {
          defaultValue: "Status",
        }),
        hint: t("skillAgent.tree.filterStatusHint", {
          defaultValue: "Archivos de seguimiento del proyecto",
        }),
        accent: "#455a64",
        Icon: FlagOutlinedIcon,
      },
      searchs: {
        label: t("skillAgent.tree.filterSearchs", { defaultValue: "Searchs" }),
        hint: t("skillAgent.tree.filterSearchsHint", {
          defaultValue: "Investigación de mercado",
        }),
        accent: "#00897b",
        Icon: TravelExploreOutlinedIcon,
      },
      disenos: {
        label: t("skillAgent.tree.filterDisenos", {
          defaultValue: "Skills/Agentes",
        }),
        hint: t("skillAgent.tree.filterDisenosHint", {
          defaultValue: "Skills/Agentes y sketches",
        }),
        accent: "#1565c0",
        Icon: DesignServicesOutlinedIcon,
      },
      fichas: {
        label: t("skillAgent.tree.filterFichas", { defaultValue: "Fichas" }),
        hint: t("skillAgent.tree.filterFichasHint", {
          defaultValue: "Fichas técnicas",
        }),
        accent: "#00695c",
        Icon: DescriptionOutlinedIcon,
      },
      resumenes: {
        label: t("skillAgent.tree.filterResumenes", { defaultValue: "Recaps" }),
        hint: t("skillAgent.tree.filterResumenesHint", {
          defaultValue: "Resúmenes de reunión",
        }),
        accent: "#1565c0",
        Icon: SummarizeOutlinedIcon,
      },
    }),
    [t],
  );

  const activeTreeFilter =
    treeTypeFilterMeta[treeTypeFilter] || treeTypeFilterMeta.all;
  const ActiveFilterIcon = activeTreeFilter.Icon;

  // Al buscar, expandir L1/L2 una vez por query (no en cada rebuild del tree).
  const lastSearchExpandQRef = useRef("");
  useEffect(() => {
    const qq = q.trim();
    if (!qq || !tree.length) {
      lastSearchExpandQRef.current = "";
      return;
    }
    if (lastSearchExpandQRef.current === qq) return;
    lastSearchExpandQRef.current = qq;
    setExpanded((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const l1 of tree) {
        if (!next.has(l1.id)) {
          next.add(l1.id);
          changed = true;
        }
        for (const l2 of l1.children || []) {
          if (!next.has(l2.id)) {
            next.add(l2.id);
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [q, tree]);

  const mergeById = useCallback((prev, incoming) => {
    const map = new Map(
      (Array.isArray(prev) ? prev : []).map((r) => [Number(r.id), r]),
    );
    for (const row of Array.isArray(incoming) ? incoming : []) {
      if (row?.id == null) continue;
      map.set(Number(row.id), { ...(map.get(Number(row.id)) || {}), ...row });
    }
    return [...map.values()];
  }, []);

  const loadProjectBundle = useCallback(
    async (projectId, { force = false } = {}) => {
      const pid = Number(projectId);
      if (!Number.isFinite(pid) || pid <= 0) return;
      if (!force && projectBundlesRef.current[pid]) return;
      try {
        const bundle = await getSkillAgentProjectBundle(pid);
        setProjectBundles((prev) => ({ ...prev, [pid]: bundle }));
        setRows((prev) => mergeById(prev, bundle?.tasks));
        setSearches((prev) => mergeById(prev, bundle?.searches));
        setMoodboards((prev) => mergeById(prev, bundle?.moodboards));
        setRapports((prev) => mergeById(prev, bundle?.rapports));
        // clientInfos live in the bundle tree; no separate list state needed
      } catch (err) {
        console.error("loadProjectBundle", err);
      }
    },
    [mergeById],
  );

  // Al filtrar por tipo: precargar TODOS los proyectos (no solo el cliente abierto).
  useEffect(() => {
    if (treeTypeFilter === "all") {
      setTreeFilterLoading(false);
      return undefined;
    }
    let cancelled = false;
    const ids = projectIdsFromTreeRoots(rootGroups);
    const missing = ids.filter((id) => !projectBundlesRef.current[id]);
    if (!missing.length) {
      setTreeFilterLoading(false);
      return undefined;
    }
    setTreeFilterLoading(true);
    (async () => {
      const batchSize = 8;
      for (let i = 0; i < missing.length; i += batchSize) {
        if (cancelled) return;
        const batch = missing.slice(i, i + batchSize);
        await Promise.all(batch.map((id) => loadProjectBundle(id)));
      }
      if (!cancelled) setTreeFilterLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [treeTypeFilter, rootGroups, loadProjectBundle]);

  // Expandir temporada → … → carpetas del filtro para pintar el árbol completo.
  useEffect(() => {
    if (treeTypeFilter === "all" || treeFilterLoading) return;
    const ids = collectCompassExpandIds(tree);
    if (!ids.length) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const id of ids) {
        if (id && !next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [treeTypeFilter, treeFilterLoading, tree]);

  const searchesForNewTask = useMemo(() => {
    const ficha = fichas.find(
      (f) => String(f.id) === String(newTask.SkillFichaId),
    );
    const customerId = ficha?.CustomerId || newTask.CustomerId || null;
    const temp = String(newTask.Temporada || "").trim();
    return searches.filter((s) => {
      if (s.Status !== "done") return false;
      if (
        customerId &&
        s.CustomerId &&
        Number(s.CustomerId) !== Number(customerId)
      ) {
        return false;
      }
      if (temp && String(s.Temporada || "").trim() !== temp) return false;
      return true;
    });
  }, [
    searches,
    fichas,
    newTask.SkillFichaId,
    newTask.CustomerId,
    newTask.Temporada,
  ]);

  const toggleNode = (id) => {
    const key = String(id || "");
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    if (key.startsWith("project:")) {
      const pid = Number(key.slice("project:".length));
      if (Number.isFinite(pid) && pid > 0) loadProjectBundle(pid);
    }
  };

  const rememberCreateLink = (ctxNode, extras = {}) => {
    const projectId =
      ctxNode?.ProjectId != null && Number(ctxNode.ProjectId) > 0
        ? Number(ctxNode.ProjectId)
        : null;
    const meetingSessionId =
      ctxNode?.MeetingSessionId != null && Number(ctxNode.MeetingSessionId) > 0
        ? Number(ctxNode.MeetingSessionId)
        : null;
    const fromNode = ctxNode?.OrgBrand ?? ctxNode?.orgBrand ?? null;
    const fromExtras = extras?.OrgBrand ?? extras?.orgBrand ?? null;
    const orgBrand = normalizeOrgBrand(fromExtras || fromNode || "STAGE");
    setCreateLinkCtx({
      ProjectId: projectId,
      MeetingSessionId: meetingSessionId,
      OrgBrand: orgBrand,
    });
  };

  const applyColumnPathIds = useCallback((ids) => {
    const list = (ids || []).map(String).filter(Boolean);
    if (!list.length) return;
    setColumnPath(list);
    setExpanded((prev) => {
      const next = new Set(prev);
      for (const id of list) next.add(id);
      return next;
    });
  }, []);

  const handleToggleCompassFavorite = useCallback((entry) => {
    setCompassFavorites((prev) => {
      const next = toggleCompassFavorite(prev, entry);
      saveCompassFavorites(next, compassFavoritesUserIdRef.current);
      return next;
    });
  }, []);

  const collapseTreeToRail = useCallback(() => {
    setTreeCollapsed(true);
    try {
      localStorage.setItem("skillAgentTreeCollapsed", "1");
    } catch {
      /* ignore */
    }
  }, []);

  const persistCompassPanelSize = useCallback((next) => {
    setCompassPanelSize(next);
    try {
      localStorage.setItem("skillAgentCompassPanelSize", JSON.stringify(next));
    } catch {
      /* ignore */
    }
  }, []);

  const beginCompassPanelResize = useCallback(
    (edge) => (event) => {
      event.preventDefault();
      event.stopPropagation();
      const el = compassPanelRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const parent = el.parentElement?.getBoundingClientRect();
      compassResizeRef.current = {
        edge,
        startX: event.clientX,
        startY: event.clientY,
        startW: rect.width,
        startH: rect.height,
        maxW: parent?.width || window.innerWidth,
        maxH: parent?.height || window.innerHeight,
        pointerId: event.pointerId,
      };
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
      document.body.style.userSelect = "none";
      document.body.style.cursor =
        edge === "e" ? "ew-resize" : edge === "s" ? "ns-resize" : "nwse-resize";
    },
    [],
  );

  const onCompassPanelResizeMove = useCallback((event) => {
    const drag = compassResizeRef.current;
    if (!drag) return;
    const minW = COMPASS_COL_WIDTH;
    const minH = 280;
    let width = drag.startW;
    let height = drag.startH;
    if (drag.edge === "e" || drag.edge === "se") {
      width = Math.round(
        Math.min(
          drag.maxW,
          Math.max(minW, drag.startW + (event.clientX - drag.startX)),
        ),
      );
    }
    if (drag.edge === "s" || drag.edge === "se") {
      height = Math.round(
        Math.min(
          drag.maxH,
          Math.max(minH, drag.startH + (event.clientY - drag.startY)),
        ),
      );
    }
    setCompassPanelSize((prev) => ({
      width:
        drag.edge === "e" || drag.edge === "se"
          ? width
          : (prev.width ?? Math.round(drag.startW)),
      height: drag.edge === "s" || drag.edge === "se" ? height : prev.height,
    }));
  }, []);

  const endCompassPanelResize = useCallback((event) => {
    if (!compassResizeRef.current) return;
    compassResizeRef.current = null;
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    } catch {
      /* ignore */
    }
    setCompassPanelSize((prev) => {
      try {
        localStorage.setItem(
          "skillAgentCompassPanelSize",
          JSON.stringify(prev),
        );
      } catch {
        /* ignore */
      }
      return prev;
    });
  }, []);

  const resetCompassPanelSize = useCallback(() => {
    persistCompassPanelSize({ width: null, height: null });
  }, [persistCompassPanelSize]);

  const treeRef = useRef(tree);
  treeRef.current = tree;

  const handleOpenCompassFavorite = useCallback(
    async (fav) => {
      if (!fav?.kind || !fav?.id) return;
      if (fav.kind === "client") {
        setTreeCollapsed(false);
        try {
          localStorage.setItem("skillAgentTreeCollapsed", "0");
        } catch {
          /* ignore */
        }
        const resolveClient = () => {
          const treeNow = treeRef.current || [];
          const path = findCompassClientPathIds(treeNow, fav.id);
          if (!path.length) return;
          applyColumnPathIds(path);
        };
        resolveClient();
        window.setTimeout(resolveClient, 80);
        return;
      }
      const projectId = Number(fav.projectId);
      if (Number.isFinite(projectId) && projectId > 0) {
        await loadProjectBundle(projectId, { force: true });
      }
      setTreeCollapsed(false);
      try {
        localStorage.setItem("skillAgentTreeCollapsed", "0");
      } catch {
        /* ignore */
      }
      const targetId =
        fav.kind === "meeting" ? `meeting:${fav.id}` : `project:${fav.id}`;
      const resolve = () => {
        const treeNow = treeRef.current || [];
        let path = findCompassTreePathIds(treeNow, targetId);
        if (!path.length && Number.isFinite(projectId) && projectId > 0) {
          path = findCompassTreePathIds(treeNow, `project:${projectId}`);
          if (fav.kind === "meeting" && path.length) {
            const brand = fav.orgBrand || "STAGE";
            path = [
              ...path,
              `project:${projectId}::org:${brand}`,
              `project:${projectId}::org:${brand}::meetings`,
              targetId,
            ];
          }
        }
        if (!path.length) return;
        // Misma profundidad que el drill manual: Empresa → primer concepto.
        path = extendCompassPathToOrgBranch(treeNow, path);
        applyColumnPathIds(path);
      };
      resolve();
      // Bundle → re-render del árbol: reintentar cuando ya hay orgBranches.
      window.setTimeout(resolve, 80);
      window.setTimeout(resolve, 220);
    },
    [applyColumnPathIds, loadProjectBundle],
  );

  const expandOrgBrandPaths = useCallback(
    (projectId, orgBrand, meetingId = null, folderKey = null) => {
      const pid = Number(projectId);
      const brand = normalizeOrgBrand(orgBrand);
      const mid = Number(meetingId);
      const tail = [];
      if (Number.isFinite(pid) && pid > 0) {
        tail.push(`project:${pid}`);
        tail.push(`project:${pid}::org:${brand}`);
        if (Number.isFinite(mid) && mid > 0) {
          tail.push(`project:${pid}::org:${brand}::meetings`);
          tail.push(`meeting:${mid}`);
          if (folderKey) tail.push(`meeting:${mid}::${folderKey}`);
        } else if (folderKey) {
          tail.push(`project:${pid}::org:${brand}::${folderKey}`);
        }
      }
      setExpanded((prev) => {
        const next = new Set(prev);
        for (const id of tail) next.add(id);
        return next;
      });
      const projectPath = findCompassTreePathIds(
        treeRef.current,
        `project:${pid}`,
      );
      if (projectPath.length) {
        const merged = [...projectPath];
        for (const id of tail) {
          if (!merged.includes(id)) merged.push(id);
        }
        applyColumnPathIds(merged);
      } else if (tail.length) {
        setColumnPath((prev) => {
          const kept = prev.filter(
            (id) =>
              !String(id).startsWith(`project:${pid}`) &&
              !String(id).startsWith("meeting:"),
          );
          return [...kept, ...tail];
        });
      }
    },
    [applyColumnPathIds],
  );

  const openMeetingInCompass = useCallback(
    (mid, ctxNode = null) => {
      const id = String(mid);
      const projectId = Number(ctxNode?.ProjectId);
      const brand = normalizeOrgBrand(
        ctxNode?.OrgBrand || ctxNode?.orgBrand || "STAGE",
      );
      selectedMeetingProjectIdRef.current =
        Number.isFinite(projectId) && projectId > 0 ? projectId : null;
      setSelectedMeetingId(id);
      setSelectedProjectId("");
      setSelectedId("");
      setSelectedSearchId("");
      setSelectedMoodboardId("");
      setSelectedRapportId("");
      setSelectedAiDesignId("");
      setAiDesignCompassState(null);
      setSearchParams({ meeting: id });
      expandOrgBrandPaths(projectId, brand, id);
      collapseTreeToRail();
      if (Number.isFinite(projectId) && projectId > 0) {
        loadProjectBundle(projectId);
      }
    },
    [
      expandOrgBrandPaths,
      loadProjectBundle,
      setSearchParams,
      collapseTreeToRail,
    ],
  );

  const openMoveOrgBrand = useCallback((kind, item, targetBrand) => {
    const id = Number(item?.id);
    const brand = normalizeOrgBrand(targetBrand);
    if (!Number.isFinite(id) || id <= 0) return;
    const from = normalizeOrgBrand(item?.OrgBrand || item?.orgBrand);
    if (from === brand) return;
    setMoveOrgBrandError("");
    setMoveOrgBrandTarget({ kind, item, targetBrand: brand });
  }, []);

  const handleMoveOrgBrand = useCallback(async () => {
    const kind = moveOrgBrandTarget?.kind;
    const item = moveOrgBrandTarget?.item;
    const brand = normalizeOrgBrand(moveOrgBrandTarget?.targetBrand);
    const id = Number(item?.id);
    if (!Number.isFinite(id) || id <= 0) return;
    const projectId =
      Number(item?.ProjectId || item?.projectId) ||
      Number(selectedProjectId) ||
      Number(selectedMeetingProjectIdRef.current) ||
      null;
    setMoveOrgBrandSaving(true);
    setMoveOrgBrandError("");
    try {
      if (kind === "task") {
        await updateSkillAgentTask(id, { OrgBrand: brand });
      } else if (kind === "search") {
        await updateSkillAgentSearch(id, { OrgBrand: brand });
      } else if (kind === "moodboard") {
        await updateSkillAgentMoodboard(id, { OrgBrand: brand });
      } else if (kind === "rapport") {
        const fd = new FormData();
        fd.append("OrgBrand", brand);
        await updateSkillAgentRapport(id, fd);
      } else if (kind === "clientInfo") {
        await updateSkillAgentClientInfo(id, {
          Titulo: item?.Titulo || "Client Info",
          OrgBrand: brand,
        });
      } else if (kind === "meeting") {
        await patchMeetingSession(id, { orgBrand: brand });
      } else {
        setMoveOrgBrandSaving(false);
        return;
      }
      setMoveOrgBrandTarget(null);
      if (Number.isFinite(projectId) && projectId > 0) {
        await loadProjectBundle(projectId, { force: true });
        expandOrgBrandPaths(projectId, brand, kind === "meeting" ? id : null);
      } else {
        await load();
      }
    } catch (err) {
      setMoveOrgBrandError(
        err?.response?.data?.error ||
          t("skillAgent.tree.moveBrandError", {
            defaultValue: "No se pudo mover a {{brand}}.",
            brand,
          }),
      );
    } finally {
      setMoveOrgBrandSaving(false);
    }
  }, [
    expandOrgBrandPaths,
    loadProjectBundle,
    load,
    moveOrgBrandTarget,
    selectedProjectId,
    t,
  ]);

  /** Tras crear / al hacer click: limpia el panel derecho, expande L1→proyecto y scrollea. */
  const openProjectInCompass = useCallback(
    (projectId, { roots, clearFilter = false } = {}) => {
      const pid = Number(projectId);
      if (!Number.isFinite(pid) || pid <= 0) return;
      if (clearFilter) setQ("");
      setSelectedProjectId(String(pid));
      setSelectedMeetingId("");
      setSelectedId("");
      setSelectedSearchId("");
      setSelectedMoodboardId("");
      setSelectedRapportId("");
      setSearchParams({});
      setTreeCollapsed(false);
      const sourceRoots = Array.isArray(roots) ? roots : rootGroups;
      const filterQ = clearFilter ? "" : q;
      const nextTree = buildCompassProjectsTree(
        sourceRoots,
        projectBundlesRef.current || {},
        groupBy,
        filterQ,
      );
      const path = findCompassTreePathIds(nextTree, `project:${pid}`);
      const ids = path.length ? path : [`project:${pid}`];
      applyColumnPathIds(ids);
      loadProjectBundle(pid, { force: true });
      window.setTimeout(() => {
        const el = document.querySelector(`[data-tree-id="project:${pid}"]`);
        el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }, 160);
    },
    [
      rootGroups,
      groupBy,
      q,
      loadProjectBundle,
      setSearchParams,
      applyColumnPathIds,
    ],
  );

  const handleOpenMeeting = useCallback(
    (ctxNode) => {
      const mid =
        Number(ctxNode?.MeetingSessionId) ||
        Number(ctxNode?.meetingSessionId) ||
        Number(ctxNode?.meeting?.id);
      if (Number.isFinite(mid) && mid > 0) {
        openMeetingInCompass(mid, ctxNode);
      }
    },
    [openMeetingInCompass],
  );

  const handleCloseMeeting = useCallback(() => {
    const projectId = selectedMeetingProjectIdRef.current;
    selectedMeetingProjectIdRef.current = null;
    setCaptureParked(false);
    setCaptureSlotEl(null);
    setSelectedMeetingId("");
    setSearchParams({});
    if (Number.isFinite(projectId) && projectId > 0) {
      loadProjectBundle(projectId, { force: true });
      listSkillAgentProjectTreeRoots()
        .then((roots) => {
          if (Array.isArray(roots)) setRootGroups(roots);
        })
        .catch(() => null);
    }
  }, [loadProjectBundle, setSearchParams]);

  const onCaptureRailChange = useCallback((open) => {
    setCaptureParked(!open);
    if (!open) {
      setTreeCollapsed(true);
      try {
        localStorage.setItem("skillAgentTreeCollapsed", "1");
      } catch {
        /* ignore */
      }
    }
  }, []);

  const registerUnparkCapture = useCallback((fn) => {
    unparkCaptureRef.current = typeof fn === "function" ? fn : null;
  }, []);

  const expandCompassTree = useCallback(() => {
    if (captureParkedRef.current) {
      unparkCaptureRef.current?.();
    }
    setTreeCollapsed(false);
    try {
      localStorage.setItem("skillAgentTreeCollapsed", "0");
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (selectedMeetingId) return;
    setCaptureParked(false);
    setCaptureSlotEl(null);
  }, [selectedMeetingId]);

  // Expandir y scrollear la reunión en el árbol (?meeting= / volver desde Recap).
  // Solo auto-expandir al cambiar de reunión (o la 1ª vez que el path aparece tras cargar bundle);
  // no reabrir ramas si el usuario las cerró y el tree se rebuild-ea.
  const lastScrolledMeetingRef = useRef("");
  const lastExpandedMeetingRef = useRef("");
  useEffect(() => {
    if (!selectedMeetingId) {
      lastScrolledMeetingRef.current = "";
      lastExpandedMeetingRef.current = "";
      return;
    }
    if (loading || forbidden) return;
    let cancelled = false;
    const mid = String(selectedMeetingId);

    const expandIds = (ids) => {
      if (!ids?.length) return;
      applyColumnPathIds(ids);
    };

    const scrollToMeeting = () => {
      window.setTimeout(() => {
        if (cancelled) return;
        const el = document.querySelector(`[data-tree-id="meeting:${mid}"]`);
        if (!el) return;
        if (lastScrolledMeetingRef.current === mid) return;
        lastScrolledMeetingRef.current = mid;
        el.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }, 120);
    };

    (async () => {
      const pathExisting = findCompassTreePathIds(tree, `meeting:${mid}`);
      if (pathExisting.length) {
        if (lastExpandedMeetingRef.current !== mid) {
          expandIds(pathExisting);
          lastExpandedMeetingRef.current = mid;
          collapseTreeToRail();
        }
        scrollToMeeting();
        return;
      }

      let projectId = selectedMeetingProjectIdRef.current;
      if (!projectId) {
        for (const [pid, bundle] of Object.entries(
          projectBundlesRef.current || {},
        )) {
          if (
            (bundle?.meetings || []).some((m) => Number(m.id) === Number(mid))
          ) {
            projectId = Number(pid);
            break;
          }
        }
      }
      if (!projectId) {
        const mb = moodboards.find(
          (m) => Number(m.MeetingSessionId) === Number(mid),
        );
        if (mb?.ProjectId) projectId = Number(mb.ProjectId);
      }
      if (!projectId) {
        try {
          const link = await getSkillAgentProjectByMeeting(mid);
          if (cancelled) return;
          projectId = Number(link?.projectId) || null;
        } catch {
          /* fallback abajo */
        }
      }
      if (!projectId) {
        try {
          const session = await getMeetingSession(mid);
          if (cancelled) return;
          projectId = Number(session?.projectId || session?.ProjectId) || null;
        } catch {
          return;
        }
      }
      if (cancelled || !projectId) return;
      selectedMeetingProjectIdRef.current = projectId;

      // Solo una pasada de expand+load por reunión (evita reabrir al rebuild del tree).
      if (
        lastExpandedMeetingRef.current === mid ||
        lastExpandedMeetingRef.current === `pending:${mid}`
      ) {
        return;
      }
      lastExpandedMeetingRef.current = `pending:${mid}`;

      const pathToProject = findCompassTreePathIds(
        tree,
        `project:${projectId}`,
      );
      expandIds([
        ...pathToProject,
        `project:${projectId}`,
        `project:${projectId}::meetings`,
        `meeting:${mid}`,
      ]);
      collapseTreeToRail();
      await loadProjectBundle(projectId, { force: false });
      // Tras cargar el bundle, el effect se re-ejecuta con tree actualizado
      // y marca/scrollea la reunión (pathExisting).
    })();

    return () => {
      cancelled = true;
    };
  }, [
    selectedMeetingId,
    loading,
    forbidden,
    tree,
    moodboards,
    loadProjectBundle,
    applyColumnPathIds,
    collapseTreeToRail,
  ]);

  // Camino de columnas al seleccionar (?rapport= / ?moodboard= / …).
  // Una sola vez por pieza: si el usuario cierra ramas y otro click rebuild-ea el tree, no reabrir.
  const lastScrolledArtifactRef = useRef("");
  const lastExpandedArtifactRef = useRef("");
  useEffect(() => {
    if (loading || forbidden) return;
    if (selectedMeetingId) return;
    const urlResumen = searchParams.get("resumen");
    const kind = selectedAiDesignId
      ? "aiDesign"
      : selectedRapportId
        ? "rapport"
        : selectedSearchId
          ? "search"
          : selectedId
            ? "task"
            : selectedMoodboardId
              ? urlResumen ||
                isResumenBoard(
                  moodboards.find(
                    (m) => Number(m.id) === Number(selectedMoodboardId),
                  ),
                )
                ? "resumen"
                : "moodboard"
              : null;
    const artifactId =
      kind === "aiDesign"
        ? selectedAiDesignId
        : kind === "rapport"
          ? selectedRapportId
          : kind === "search"
            ? selectedSearchId
            : kind === "task"
              ? selectedId
              : kind === "moodboard" || kind === "resumen"
                ? selectedMoodboardId
                : "";
    if (!kind || !artifactId) {
      lastExpandedArtifactRef.current = "";
      return;
    }

    let cancelled = false;
    const scrollKey =
      kind === "aiDesign" ? `aiDesign:${artifactId}` : `${kind}:${artifactId}`;

    const expandIds = (ids) => {
      if (!ids?.length) return;
      applyColumnPathIds(ids);
    };

    const scrollToLeaf = () => {
      window.setTimeout(() => {
        if (cancelled) return;
        const el = document.querySelector(`[data-tree-id="${scrollKey}"]`);
        if (!el) return;
        if (lastScrolledArtifactRef.current === scrollKey) return;
        lastScrolledArtifactRef.current = scrollKey;
        el.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }, 120);
    };

    const selectionArgs = {
      rapportId: kind === "rapport" ? artifactId : "",
      searchId: kind === "search" ? artifactId : "",
      taskId: kind === "task" ? artifactId : "",
      moodboardId:
        kind === "moodboard" || kind === "aiDesign" ? artifactId : "",
      resumenId: kind === "resumen" ? artifactId : "",
    };

    (async () => {
      const pathExisting = findCompassArtifactPathIds(tree, selectionArgs);
      if (pathExisting.length) {
        if (lastExpandedArtifactRef.current !== scrollKey) {
          expandIds(pathExisting);
          lastExpandedArtifactRef.current = scrollKey;
          collapseTreeToRail();
        }
        scrollToLeaf();
        return;
      }

      let projectId = null;
      let meetingSessionId = null;
      const fromLocal = (row) => {
        if (!row) return;
        if (Number(row.ProjectId) > 0) projectId = Number(row.ProjectId);
        if (Number(row.MeetingSessionId) > 0) {
          meetingSessionId = Number(row.MeetingSessionId);
        }
      };
      if (kind === "rapport") {
        fromLocal(rapports.find((r) => Number(r.id) === Number(artifactId)));
        if (!projectId) {
          try {
            fromLocal(await getSkillAgentRapportById(artifactId));
          } catch {
            return;
          }
        }
      } else if (kind === "task") {
        fromLocal(rows.find((r) => Number(r.id) === Number(artifactId)));
        if (!projectId) {
          try {
            fromLocal(await getSkillAgentTaskById(artifactId));
          } catch {
            return;
          }
        }
      } else if (kind === "search") {
        fromLocal(searches.find((r) => Number(r.id) === Number(artifactId)));
        if (!projectId) {
          try {
            fromLocal(await getSkillAgentSearchById(artifactId));
          } catch {
            return;
          }
        }
      } else if (
        kind === "aiDesign" ||
        kind === "moodboard" ||
        kind === "resumen"
      ) {
        fromLocal(moodboards.find((r) => Number(r.id) === Number(artifactId)));
        if (!projectId) {
          try {
            fromLocal(await getSkillAgentMoodboardById(artifactId));
          } catch {
            return;
          }
        }
      } else {
        return;
      }
      if (cancelled || !projectId) return;

      if (
        lastExpandedArtifactRef.current === scrollKey ||
        lastExpandedArtifactRef.current === `pending:${scrollKey}`
      ) {
        return;
      }
      lastExpandedArtifactRef.current = `pending:${scrollKey}`;

      const pathToProject = findCompassTreePathIds(
        tree,
        `project:${projectId}`,
      );
      const extra = [];
      if (meetingSessionId) {
        extra.push(
          `project:${projectId}::meetings`,
          `meeting:${meetingSessionId}`,
        );
      }
      expandIds([...pathToProject, `project:${projectId}`, ...extra]);
      collapseTreeToRail();
      await loadProjectBundle(projectId);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    loading,
    forbidden,
    selectedMeetingId,
    selectedMoodboardId,
    selectedSearchId,
    selectedId,
    selectedRapportId,
    selectedAiDesignId,
    tree,
    rows,
    searches,
    moodboards,
    rapports,
    searchParams,
    loadProjectBundle,
    applyColumnPathIds,
    collapseTreeToRail,
  ]);

  const openNewMeetingDialog = useCallback((ctxNode) => {
    rememberCreateLink(ctxNode);
    setMeetingCreateNode(ctxNode || null);
    setMeetingCreateTitle(
      String(ctxNode?.label || ctxNode?.clientLabel || "").trim(),
    );
    setMeetingCreateOpen(true);
  }, []);

  const handleCreateMeeting = useCallback(async () => {
    const ctxNode = meetingCreateNode;
    const projectId = Number(ctxNode?.ProjectId);
    const title = String(meetingCreateTitle || "").trim();
    if (!Number.isFinite(projectId) || projectId <= 0) return;
    if (!title) {
      setError(
        t("skillAgent.create.meetingTitleRequired", {
          defaultValue: "Pon un título a la reunión.",
        }),
      );
      return;
    }
    setCreatingMeeting(true);
    setError("");
    try {
      const created = await createMeetingSession({
        title,
        customerName: String(
          ctxNode?.CustomerName || ctxNode?.clientLabel || "",
        ).trim(),
        projectId,
        orgBrand: normalizeOrgBrand(
          ctxNode?.OrgBrand ||
            ctxNode?.orgBrand ||
            createLinkCtx.OrgBrand ||
            "STAGE",
        ),
        consentClientsInformed: true,
        consentRecordingAccepted: true,
        consentAiAccepted: true,
      });
      setMeetingCreateOpen(false);
      setMeetingCreateNode(null);
      setMeetingCreateTitle("");
      const roots = await listSkillAgentProjectTreeRoots().catch(() => null);
      if (Array.isArray(roots)) setRootGroups(roots);
      await loadProjectBundle(projectId, { force: true });
      if (created?.id) openMeetingInCompass(created.id, ctxNode);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          t("skillAgent.create.meetingError", {
            defaultValue: "No se pudo crear la reunión",
          }),
      );
    } finally {
      setCreatingMeeting(false);
    }
  }, [
    meetingCreateNode,
    meetingCreateTitle,
    loadProjectBundle,
    openMeetingInCompass,
    t,
  ]);

  const handleMeetingSessionChange = useCallback((patch) => {
    if (patch?.id == null) return;
    const mid = Number(patch.id);
    if (!Number.isFinite(mid) || mid <= 0) return;
    const nextTitle = patch.title != null ? String(patch.title) : null;
    if (nextTitle == null) return;
    setProjectBundles((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [pid, bundle] of Object.entries(prev || {})) {
        const meetings = Array.isArray(bundle?.meetings)
          ? bundle.meetings
          : null;
        if (!meetings) continue;
        let localChange = false;
        const updated = meetings.map((m) => {
          if (Number(m.id) !== mid) return m;
          if (String(m.title || "") === nextTitle) return m;
          localChange = true;
          return { ...m, title: nextTitle };
        });
        if (localChange) {
          next[pid] = { ...bundle, meetings: updated };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, []);

  const openHubCreate = useCallback((ctxNode) => {
    setHubCreateNode(ctxNode || null);
    setHubCreateOpen(true);
  }, []);

  const knownSeasons = useMemo(() => {
    const noSeason = t("skillAgent.tree.noSeason", {
      defaultValue: "Sin temporada",
    });
    const set = new Set();
    for (const g of rootGroups || []) {
      const s = String(g.temporada || "").trim();
      if (!s || s === noSeason) continue;
      set.add(s);
    }
    return [...set].sort((a, b) => b.localeCompare(a));
  }, [rootGroups, t]);

  const openEditProjectSeason = useCallback((ctxNode) => {
    const projectId = Number(ctxNode?.ProjectId || ctxNode?.projectId);
    if (!Number.isFinite(projectId) || projectId <= 0) return;
    const name = String(ctxNode?.label || ctxNode?.Name || "").trim();
    setProjectSeasonEdit({
      projectId,
      name,
      Temporada: String(ctxNode?.Temporada || "").trim(),
    });
    setProjectNameValue(name);
    setProjectSeasonValue(String(ctxNode?.Temporada || "").trim());
    setProjectSeasonError("");
  }, []);

  const openDeleteProject = useCallback((ctxNode) => {
    const projectId = Number(ctxNode?.ProjectId || ctxNode?.projectId);
    if (!Number.isFinite(projectId) || projectId <= 0) return;
    const meetingCount = Number(ctxNode?.meetingCount) || 0;
    const artifactCount = Number(ctxNode?.artifactCount) || 0;
    const itemCount =
      Number(ctxNode?.count) || meetingCount + artifactCount || 0;
    setDeleteProjectTarget({
      projectId,
      name: String(ctxNode?.label || ctxNode?.Name || "").trim(),
      itemCount,
      meetingCount,
      artifactCount,
    });
    setDeleteProjectError("");
  }, []);

  const handleDeleteProject = useCallback(async () => {
    const pid = Number(deleteProjectTarget?.projectId);
    if (!Number.isFinite(pid) || pid <= 0) return;
    if (Number(deleteProjectTarget?.itemCount) > 0) return;
    setDeleteProjectSaving(true);
    setDeleteProjectError("");
    try {
      await deleteSkillAgentProject(pid);
      setDeleteProjectTarget(null);
      if (String(selectedProjectId) === String(pid)) {
        setSelectedProjectId("");
      }
      setProjectBundles((prev) => {
        if (!prev?.[pid]) return prev;
        const next = { ...prev };
        delete next[pid];
        return next;
      });
      const roots = await listSkillAgentProjectTreeRoots();
      setRootGroups(Array.isArray(roots) ? roots : []);
    } catch (err) {
      const data = err?.response?.data;
      if (data?.code === "NOT_EMPTY") {
        setDeleteProjectTarget((prev) =>
          prev
            ? {
                ...prev,
                itemCount: Number(data.count) || prev.itemCount,
                meetingCount: Number(data.meetingCount) || prev.meetingCount,
                artifactCount: Number(data.artifactCount) || prev.artifactCount,
              }
            : prev,
        );
        setDeleteProjectError(
          t("skillAgent.dialog.deleteProjectNotEmpty", {
            defaultValue:
              "El proyecto tiene contenido. Bórralo antes de eliminar el proyecto.",
          }),
        );
      } else {
        setDeleteProjectError(
          data?.error ||
            t("skillAgent.dialog.deleteProjectError", {
              defaultValue: "No se pudo eliminar el proyecto.",
            }),
        );
      }
    } finally {
      setDeleteProjectSaving(false);
    }
  }, [deleteProjectTarget, selectedProjectId, t]);

  const handleSyncFromBlablaton = useCallback(async () => {
    setSyncingBlablaton(true);
    setError("");
    try {
      await syncSkillAgentProjectsFromMeetings();
      const roots = await listSkillAgentProjectTreeRoots();
      setRootGroups(Array.isArray(roots) ? roots : []);
      setProjectBundles({});
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          t("skillAgent.tree.syncError", {
            defaultValue: "No se pudo sincronizar desde Blablaton.",
          }),
      );
    } finally {
      setSyncingBlablaton(false);
    }
  }, [t]);

  const openCreateProjectDialog = useCallback(() => {
    setCreateProjectForm({
      Name: "",
      Temporada: "",
      customer: null,
      customerInput: "",
    });
    setCreateProjectError("");
    setCreateProjectOpen(true);
    (async () => {
      try {
        const [customers, temps] = await Promise.all([
          getCustomers().catch(() => []),
          getTemporadas().catch(() => []),
        ]);
        setCustomerOptions(optionsFromCustomers(customers));
        const labels = sortTemporadasFromLabel(
          (Array.isArray(temps) ? temps : [])
            .map((t) => String(t.TEMPORADA || t.Temporada || t || "").trim())
            .filter(Boolean),
        );
        setCatalogSeasons(labels);
      } catch {
        /* ignore catalog load */
      }
    })();
  }, []);

  const handleCreateProject = async () => {
    const name = String(createProjectForm.Name || "").trim();
    const temporada = String(createProjectForm.Temporada || "").trim();
    const customer = createProjectForm.customer;
    const typedClient = String(createProjectForm.customerInput || "").trim();
    const customerName = (customer && customer.label) || typedClient || "";
    if (!name) {
      setCreateProjectError(
        t("skillAgent.dialog.newProjectNameRequired", {
          defaultValue: "Pon un nombre al proyecto.",
        }),
      );
      return;
    }
    if (!customerName) {
      setCreateProjectError(
        t("skillAgent.dialog.newProjectClientRequired", {
          defaultValue: "Elige o escribe un cliente.",
        }),
      );
      return;
    }
    if (!temporada) {
      setCreateProjectError(
        t("skillAgent.dialog.newProjectSeasonRequired", {
          defaultValue: "Indica la temporada (ej. AW2027).",
        }),
      );
      return;
    }
    setCreateProjectSaving(true);
    setCreateProjectError("");
    try {
      let customerId =
        customer?.id != null && Number(customer.id) > 0
          ? Number(customer.id)
          : null;
      let resolvedName = customerName;
      if (!customerId && typedClient) {
        const needle = typedClient.toLowerCase();
        const match = (customerOptions || []).find(
          (o) =>
            String(o?.label || "")
              .trim()
              .toLowerCase() === needle,
        );
        if (match?.id) {
          customerId = Number(match.id);
          resolvedName = String(match.label || resolvedName).trim();
        }
      }
      const created = await createSkillAgentProject({
        Name: name,
        Temporada: temporada,
        CustomerId: customerId,
        CustomerName: resolvedName,
      });
      const rootsRaw = await listSkillAgentProjectTreeRoots();
      const roots = Array.isArray(rootsRaw) ? rootsRaw : [];
      setRootGroups(roots);
      setCreateProjectOpen(false);
      const pid = Number(created?.id);
      if (Number.isFinite(pid) && pid > 0) {
        openProjectInCompass(pid, { roots, clearFilter: true });
      }
    } catch (err) {
      setCreateProjectError(
        err?.response?.data?.error ||
          t("skillAgent.dialog.newProjectError", {
            defaultValue: "No se pudo crear el proyecto.",
          }),
      );
    } finally {
      setCreateProjectSaving(false);
    }
  };

  const handleSaveProjectSeason = async () => {
    if (!projectSeasonEdit?.projectId) return;
    const name = String(projectNameValue || "").trim();
    if (!name) {
      setProjectSeasonError(
        t("skillAgent.dialog.editProjectNameRequired", {
          defaultValue: "Pon un nombre al proyecto.",
        }),
      );
      return;
    }
    const raw = String(projectSeasonValue || "").trim();
    setProjectSeasonSaving(true);
    setProjectSeasonError("");
    try {
      const projectId = projectSeasonEdit.projectId;
      await updateSkillAgentProject(projectId, {
        Name: name,
        Temporada: raw || null,
      });
      const roots = await listSkillAgentProjectTreeRoots();
      const nextRoots = Array.isArray(roots) ? roots : [];
      setRootGroups(nextRoots);
      setCompassFavorites((prev) => {
        const renamed = renameCompassProjectFavorite(prev, projectId, name);
        const next = syncCompassFavoriteLabelsFromRoots(renamed, nextRoots);
        saveCompassFavorites(next, compassFavoritesUserIdRef.current);
        return next;
      });
      setExpanded((prev) => {
        const next = new Set(prev);
        next.add(`project:${projectId}`);
        return next;
      });
      setProjectSeasonEdit(null);
      setProjectNameValue("");
      setProjectSeasonValue("");
    } catch (err) {
      setProjectSeasonError(
        err?.response?.data?.error ||
          t("skillAgent.dialog.editProjectError", {
            defaultValue: "No se pudo guardar el proyecto.",
          }),
      );
    } finally {
      setProjectSeasonSaving(false);
    }
  };

  const handleSelectTask = (id) => {
    setSelectedId(String(id));
    setSelectedSearchId("");
    setSelectedMoodboardId("");
    setSelectedRapportId("");
    setSelectedClientInfoId("");
    setSelectedMeetingId("");
    setSelectedAiDesignId("");
    setAiDesignCompassState(null);
    setSelectedProjectId("");
    setSearchParams({ id: String(id) });
    collapseTreeToRail();
  };

  const handleSelectSearch = (id) => {
    setSelectedSearchId(String(id));
    setSelectedMoodboardId("");
    setSelectedRapportId("");
    setSelectedClientInfoId("");
    setSelectedId("");
    setSelectedMeetingId("");
    setSelectedAiDesignId("");
    setAiDesignCompassState(null);
    setSelectedProjectId("");
    setSearchParams({ search: String(id) });
    collapseTreeToRail();
  };

  const handleSelectMoodboard = (id) => {
    setSelectedMoodboardId(String(id));
    setSelectedRapportId("");
    setSelectedClientInfoId("");
    setSelectedSearchId("");
    setSelectedId("");
    setSelectedMeetingId("");
    setSelectedAiDesignId("");
    setAiDesignCompassState(null);
    setSelectedProjectId("");
    setSearchParams({ moodboard: String(id) });
    collapseTreeToRail();
  };

  const handleSelectResumen = (id) => {
    setSelectedMoodboardId(String(id));
    setSelectedRapportId("");
    setSelectedClientInfoId("");
    setSelectedSearchId("");
    setSelectedId("");
    setSelectedMeetingId("");
    setSelectedAiDesignId("");
    setAiDesignCompassState(null);
    setSelectedProjectId("");
    setSearchParams({ resumen: String(id) });
    collapseTreeToRail();
  };

  const handleSelectRapport = (id) => {
    setSelectedRapportId(String(id));
    setSelectedClientInfoId("");
    setSelectedMoodboardId("");
    setSelectedSearchId("");
    setSelectedId("");
    setSelectedMeetingId("");
    setSelectedAiDesignId("");
    setAiDesignCompassState(null);
    setSelectedProjectId("");
    setSearchParams({ rapport: String(id) });
    collapseTreeToRail();
  };

  const handleSelectClientInfo = (id) => {
    setSelectedClientInfoId(String(id));
    setSelectedRapportId("");
    setSelectedMoodboardId("");
    setSelectedSearchId("");
    setSelectedId("");
    setSelectedMeetingId("");
    setSelectedAiDesignId("");
    setAiDesignCompassState(null);
    setSelectedProjectId("");
    setSearchParams({ clientInfo: String(id) });
    collapseTreeToRail();
  };

  const openNewClientInfo = async (ctxNode, dossierKind = "client_info") => {
    const node = ctxNode || hubCreateNode;
    if (!node) return;
    const kind =
      dossierKind === "status" || node?.kind === "status"
        ? "status"
        : dossierKind === "presentaciones_pdf" ||
            dossierKind === "presentaciones" ||
            node?.kind === "presentaciones"
          ? "presentaciones_pdf"
          : dossierKind === "produccion" || node?.kind === "producciones"
            ? "produccion"
            : "client_info";
    const projectId = Number(node.ProjectId || node.projectId) || null;
    const meetingId =
      Number(node.MeetingSessionId || node.meetingSessionId) || null;
    const customerId = Number(node.CustomerId) || null;
    const season = String(node.Temporada || node.seasonLabel || "").trim();
    const clientName = String(
      node.CustomerName || node.clientLabel || "",
    ).trim();
    const defaultTitle =
      kind === "status"
        ? clientName
          ? `Status · ${clientName}`
          : "Status"
        : kind === "presentaciones_pdf"
          ? clientName
            ? `Presentaciones PDF · ${clientName}`
            : "Presentaciones PDF"
          : kind === "produccion"
            ? clientName
              ? `Producción · ${clientName}`
              : "Producción"
            : clientName
              ? `Client Info · ${clientName}`
              : "Client Info";
    const folderKey =
      kind === "status"
        ? "status"
        : kind === "presentaciones_pdf"
          ? "presentaciones"
          : kind === "produccion"
            ? "produccion"
            : "info";
    try {
      const created = await createSkillAgentClientInfo({
        Titulo: defaultTitle,
        Kind: kind,
        CustomerId: customerId,
        Temporada: season || null,
        ProjectId: projectId,
        MeetingSessionId: meetingId,
        OrgBrand: normalizeOrgBrand(
          node?.OrgBrand || node?.orgBrand || createLinkCtx.OrgBrand || "STAGE",
        ),
      });
      if (projectId) {
        await loadProjectBundle(projectId, { force: true });
        expandOrgBrandPaths(
          projectId,
          created?.OrgBrand ||
            node?.OrgBrand ||
            node?.orgBrand ||
            createLinkCtx.OrgBrand ||
            "STAGE",
          meetingId,
          folderKey,
        );
      }
      if (created?.id) handleSelectClientInfo(created.id);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          (kind === "status"
            ? t("skillAgent.create.statusError", {
                defaultValue: "No se pudo crear Status.",
              })
            : kind === "presentaciones_pdf"
              ? t("skillAgent.create.presentacionesError", {
                  defaultValue: "No se pudo crear Presentaciones PDF.",
                })
              : kind === "produccion"
                ? t("skillAgent.create.produccionError", {
                    defaultValue: "No se pudo crear Producción.",
                  })
                : t("skillAgent.create.clientInfoError", {
                    defaultValue: "No se pudo crear Client Info.",
                  })),
      );
    }
  };

  const openNewProduccion = (ctxNode) =>
    openNewClientInfo(ctxNode, "produccion");

  const openNewPresentaciones = (ctxNode) =>
    openNewClientInfo(ctxNode, "presentaciones_pdf");

  const openNewStatus = (ctxNode) => openNewClientInfo(ctxNode, "status");

  /** Soft-select desde columnas Compás: marca proyecto y carga bundle SIN resetear el path. */
  const handleSelectProject = useCallback(
    (ctxNode) => {
      const pid = Number(ctxNode?.ProjectId || ctxNode?.projectId);
      if (!Number.isFinite(pid) || pid <= 0) return;
      setSelectedProjectId(String(pid));
      setSelectedMeetingId("");
      setSelectedId("");
      setSelectedSearchId("");
      setSelectedMoodboardId("");
      setSelectedRapportId("");
      setSelectedAiDesignId("");
      setAiDesignCompassState(null);
      setSearchParams({});
      loadProjectBundle(pid);
    },
    [loadProjectBundle, setSearchParams],
  );

  const openNewTaskDialog = (ctxNode, opts = {}) => {
    rememberCreateLink(ctxNode);
    const noSeasonLabel = t("skillAgent.tree.noSeason");
    const rawSeason = String(
      ctxNode?.Temporada || ctxNode?.seasonLabel || "",
    ).trim();
    const season = rawSeason && rawSeason !== noSeasonLabel ? rawSeason : "";
    const clientName = String(
      ctxNode?.CustomerName || ctxNode?.clientLabel || "",
    ).trim();
    const customerId =
      ctxNode?.CustomerId != null && Number(ctxNode.CustomerId) > 0
        ? Number(ctxNode.CustomerId)
        : null;
    const fromProject =
      Number(ctxNode?.ProjectId || ctxNode?.projectId) > 0 ||
      ctxNode?.kind === "project" ||
      ctxNode?.kind === "disenos" ||
      ctxNode?.kind === "sketches" ||
      ctxNode?.kind === "meeting";
    const createMode =
      opts.mode || (ctxNode?.kind === "sketches" ? "aiDesigns" : "skills");
    let fichaId = "";
    if (customerId) {
      const match = fichas.find((f) => Number(f.CustomerId) === customerId);
      if (match) fichaId = String(match.id);
    }
    if (!fichaId && clientName) {
      const needle = clientName.toLowerCase();
      const match = fichas.find((f) => {
        const titulo = String(f.Titulo || "").toLowerCase();
        const slug = String(f.SkillSlug || "").toLowerCase();
        return (
          titulo === needle ||
          slug === needle ||
          (titulo && (needle.includes(titulo) || titulo.includes(needle))) ||
          (slug && (needle.includes(slug) || slug.includes(needle)))
        );
      });
      if (match) fichaId = String(match.id);
    }
    setTaskFormError("");
    setTaskCreateMode(createMode);
    setNewTask({
      SkillFichaId: fichaId,
      CustomerId: customerId,
      CustomerName: clientName,
      Titulo: "",
      Temporada: season,
      TipoTarea: createMode === "aiDesigns" ? "nuevos_disenos" : "propuestas",
      Brief: "",
      searchId: "",
      lockClient: Boolean(fromProject || clientName || customerId),
      // Desde proyecto: no pedir temporada (aunque esté en Sin temporada).
      lockSeason: Boolean(fromProject || season),
    });
    setCreateOpen(true);
  };

  const openNewSearchDialog = async (ctxNode) => {
    rememberCreateLink(ctxNode);
    const rawSeason = String(ctxNode?.Temporada || "").trim();
    const season =
      rawSeason && rawSeason !== t("skillAgent.tree.noSeason") ? rawSeason : "";
    const clientName = String(
      ctxNode?.CustomerName || ctxNode?.clientLabel || "",
    ).trim();
    let customerId = ctxNode?.CustomerId ? String(ctxNode.CustomerId) : "";
    if (!customerId && clientName) {
      const match = fichas.find(
        (f) =>
          f.Titulo === clientName ||
          f.SkillSlug === clientName ||
          (f.Titulo && clientName.includes(String(f.Titulo))) ||
          (f.SkillSlug && clientName.includes(String(f.SkillSlug))),
      );
      if (match?.CustomerId) customerId = String(match.CustomerId);
    }
    const projectId = Number(ctxNode?.ProjectId || ctxNode?.projectId);
    let projectName =
      ctxNode?.kind === "project"
        ? String(ctxNode?.label || "").trim()
        : String(ctxNode?.projectLabel || ctxNode?.ProjectName || "").trim();
    if (
      !projectName &&
      Number.isFinite(projectId) &&
      projectId > 0 &&
      Array.isArray(rootGroups)
    ) {
      for (const g of rootGroups) {
        const hit = (g.projects || []).find((p) => Number(p.id) === projectId);
        if (hit?.name) {
          projectName = String(hit.name).trim();
          break;
        }
      }
    }
    const meetingTitle =
      Number(ctxNode?.MeetingSessionId) > 0 || ctxNode?.kind === "meeting"
        ? String(
            ctxNode?.meetingTitle ||
              (ctxNode?.kind === "meeting" ? ctxNode?.label : "") ||
              "",
          ).trim()
        : "";
    const branchPath = [clientName, projectName, meetingTitle, season]
      .filter(Boolean)
      .join(" · ");

    setSearchFormError("");
    setSearchContext({
      ...(ctxNode || {}),
      CustomerName: clientName || null,
      Temporada: season || null,
      CustomerId: customerId ? Number(customerId) : null,
      ProjectId: Number.isFinite(projectId) && projectId > 0 ? projectId : null,
      MeetingSessionId: ctxNode?.MeetingSessionId || null,
      branchPath,
    });

    // Solo catálogo BBDD de ese cliente (sin fallback GU). Sin preselección.
    if (customerId) {
      try {
        const meta = await getSkillAgentSearchesMeta({
          customerId: Number(customerId),
        });
        if (meta?.scrapingBee) setScrapingBeeStatus(meta.scrapingBee);
        const brandsForClient = (Array.isArray(meta?.brands) ? meta.brands : [])
          .slice()
          .sort((a, b) =>
            String(a.name || a.id).localeCompare(String(b.name || b.id), "es", {
              sensitivity: "base",
            }),
          );
        setSearchBrands(brandsForClient);
      } catch {
        setSearchBrands([]);
      }
    } else {
      setSearchBrands([]);
    }

    setNewSearch({
      Titulo: "",
      Temporada: season,
      Tipologia: "chaqueta Urban",
      CustomerId: customerId,
      CustomerName: clientName,
      branchPath,
      brandIds: [],
      urlSelection: {},
      customSources: [],
      maxImagesPerBrand: "3",
    });
    setCreateSearchOpen(true);
  };

  const handleCreateSearch = async () => {
    if (!String(newSearch.Titulo || "").trim()) {
      setSearchFormError(t("skillAgent.create.searchTitleRequired"));
      return;
    }
    if (!newSearch.CustomerId && !newSearch.CustomerName) {
      setSearchFormError(t("skillAgent.create.searchNeedClient"));
      return;
    }
    setCreatingSearch(true);
    setSearchFormError("");
    setError("");
    try {
      const tipology = String(newSearch.Tipologia || "").trim();
      if (!tipology) {
        setSearchFormError(t("skillAgent.create.tipologyRequired"));
        setCreatingSearch(false);
        return;
      }
      const customSources = normalizeCustomSources(newSearch.customSources);
      const customUrls = customSourcesToUrls(customSources);
      const researchBrands = Array.isArray(newSearch.brandIds)
        ? newSearch.brandIds
        : [];
      if (
        !researchBrands.length &&
        !customSources.filter((s) => s.enabled).length
      ) {
        setSearchFormError(t("skillAgent.create.needBrandOrUrl"));
        setCreatingSearch(false);
        return;
      }
      const created = await createSkillAgentSearch({
        Titulo: newSearch.Titulo.trim(),
        Temporada: newSearch.Temporada || null,
        Tipologia: tipology,
        CustomerId: newSearch.CustomerId ? Number(newSearch.CustomerId) : null,
        ProjectId: createLinkCtx.ProjectId,
        MeetingSessionId: createLinkCtx.MeetingSessionId,
        OrgBrand: createLinkCtx.OrgBrand || "STAGE",
        ConstraintsJson: {
          tipología: tipology,
          researchBrands,
          brandUrlSelection: newSearch.urlSelection || {},
          customSources,
          customUrls,
          maxImagesPerBrand: Number(newSearch.maxImagesPerBrand) || 3,
          customerName: newSearch.CustomerName || null,
          preferNewest: true,
          maxAgeDays: 60,
          skipKnown: true,
          skipKnownScope: "clienteTemporada",
        },
      });
      setCreateSearchOpen(false);
      if (createLinkCtx.ProjectId) {
        await loadProjectBundle(createLinkCtx.ProjectId, { force: true });
        const roots = await listSkillAgentProjectTreeRoots().catch(() => null);
        if (Array.isArray(roots)) setRootGroups(roots);
      } else {
        await load();
      }
      if (created?.id) handleSelectSearch(created.id);
    } catch (err) {
      const msg =
        err?.response?.data?.error || t("skillAgent.create.searchError");
      setSearchFormError(msg);
      setError(msg);
    } finally {
      setCreatingSearch(false);
    }
  };

  const ensureCustomerOptions = useCallback(async () => {
    if (Array.isArray(customerOptions) && customerOptions.length > 0) {
      return customerOptions;
    }
    try {
      const customers = await getCustomers().catch(() => []);
      const opts = optionsFromCustomers(customers);
      setCustomerOptions(opts);
      return opts;
    } catch {
      return [];
    }
  }, [customerOptions]);

  const openCaratulasDialog = useCallback(() => {
    setCaratulasOpen(true);
    void ensureCustomerOptions();
  }, [ensureCustomerOptions]);

  const loadCaratulaOptionsForCreate = useCallback(
    async (customerId, boardKind) => {
      setLoadingCaratulas(true);
      try {
        const params = { boardKind: boardKind || "moodboard" };
        if (customerId) params.CustomerId = Number(customerId);
        const data = await listSkillAgentCaratulas(params);
        const list = Array.isArray(data) ? data : [];
        let merged = list;
        if (customerId) {
          const globals = await listSkillAgentCaratulas({
            boardKind: boardKind || "moodboard",
          }).catch(() => []);
          const globalOnly = (Array.isArray(globals) ? globals : []).filter(
            (c) => !c.CustomerId,
          );
          const byId = new Map();
          [...list, ...globalOnly].forEach((c) => byId.set(Number(c.id), c));
          merged = [...byId.values()];
        }
        setCaratulaOptions(merged);
        return merged;
      } catch {
        setCaratulaOptions([]);
        return [];
      } finally {
        setLoadingCaratulas(false);
      }
    },
    [],
  );

  const openNewMoodboardDialog = (ctxNode, boardKind = "moodboard") => {
    rememberCreateLink(ctxNode);
    const kind =
      boardKind === "resumen"
        ? "resumen"
        : boardKind === "ficha_tecnica" || boardKind === "ficha"
          ? "ficha_tecnica"
          : boardKind === "propuesta" || boardKind === "propuestas"
            ? "propuesta"
            : "moodboard";
    const isResumen = kind === "resumen";
    const isFicha = kind === "ficha_tecnica";
    const isPropuesta = kind === "propuesta";
    const rawSeason = String(ctxNode?.Temporada || "").trim();
    const season =
      rawSeason && rawSeason !== t("skillAgent.tree.noSeason") ? rawSeason : "";
    const clientName = String(
      ctxNode?.CustomerName || ctxNode?.clientLabel || "",
    ).trim();
    let customerId = ctxNode?.CustomerId ? String(ctxNode.CustomerId) : "";
    if (!customerId && clientName) {
      const match = fichas.find(
        (f) =>
          f.Titulo === clientName ||
          f.SkillSlug === clientName ||
          (f.Titulo && clientName.includes(String(f.Titulo))) ||
          (f.SkillSlug && clientName.includes(String(f.SkillSlug))),
      );
      if (match?.CustomerId) customerId = String(match.CustomerId);
    }
    setMoodboardContext({
      CustomerName: clientName || null,
      Temporada: season || null,
      CustomerId: customerId ? Number(customerId) : null,
      ProjectId: ctxNode?.ProjectId || null,
      MeetingSessionId: ctxNode?.MeetingSessionId || null,
    });
    setMoodboardFormError("");
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, "0");
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const yy = String(today.getFullYear()).slice(-2);
    const dateTag = `${dd}-${mm}-${yy}`;
    const proposed = isResumen
      ? clientName
        ? `Recap · ${dateTag} ${clientName}`
        : `Recap · ${dateTag}`
      : isFicha
        ? clientName
          ? `Ficha · ${clientName}`
          : "Ficha técnica"
        : isPropuesta
          ? clientName
            ? `Propuesta · ${clientName}`
            : `Propuesta · ${dateTag}`
          : clientName
            ? `${clientName} · board`
            : "Aviator - JKT";
    setNewMoodboard({
      Titulo: proposed,
      CategoryLabel: proposed,
      Temporada: season,
      CustomerId: customerId,
      CustomerName: clientName,
      Kind: kind,
      CaratulaId: "",
    });
    setCreateMoodboardOpen(true);
    void loadCaratulaOptionsForCreate(
      customerId,
      isPropuesta ? "moodboard" : kind,
    ).then((opts) => {
      const preferred =
        opts.find(
          (c) => customerId && Number(c.CustomerId) === Number(customerId),
        ) || opts[0];
      if (preferred?.id) {
        setNewMoodboard((p) =>
          p.CaratulaId ? p : { ...p, CaratulaId: String(preferred.id) },
        );
      }
    });
  };

  const openNewResumenDialog = (ctxNode) => {
    openNewMoodboardDialog(ctxNode, "resumen");
  };

  const openNewPropuestaDialog = (ctxNode) => {
    openNewMoodboardDialog(ctxNode, "propuesta");
  };

  const openNewFichaDialog = (ctxNode) => {
    openNewMoodboardDialog(ctxNode, "ficha_tecnica");
  };

  const handleCreateMoodboard = async () => {
    const titulo =
      String(newMoodboard.Titulo || "").trim() ||
      String(newMoodboard.CategoryLabel || "").trim();
    if (!titulo) {
      setMoodboardFormError(t("skillAgent.create.moodboardTitleRequired"));
      return;
    }
    if (!newMoodboard.CustomerId && !newMoodboard.CustomerName) {
      setMoodboardFormError(t("skillAgent.create.moodboardNeedClient"));
      return;
    }
    const boardKind =
      newMoodboard.Kind === "resumen"
        ? "resumen"
        : newMoodboard.Kind === "ficha_tecnica" || newMoodboard.Kind === "ficha"
          ? "ficha_tecnica"
          : newMoodboard.Kind === "propuesta" ||
              newMoodboard.Kind === "propuestas"
            ? "propuesta"
            : "moodboard";
    setCreatingMoodboard(true);
    setMoodboardFormError("");
    setError("");
    try {
      let pagesJson = null;
      let chromeStyleJson = null;
      const caratulaId = newMoodboard.CaratulaId
        ? Number(newMoodboard.CaratulaId)
        : null;
      if (Number.isFinite(caratulaId) && caratulaId > 0) {
        try {
          const resolved = await resolveSkillAgentCaratulaForCreate({
            CustomerId: newMoodboard.CustomerId
              ? Number(newMoodboard.CustomerId)
              : null,
            boardKind,
            caratulaId,
          });
          if (resolved?.PagesJson) pagesJson = resolved.PagesJson;
          if (resolved?.ChromeStyleJson)
            chromeStyleJson = resolved.ChromeStyleJson;
        } catch {
          /* sin plantilla usable → fallback abajo */
        }
      }
      if (!pagesJson && boardKind === "ficha_tecnica") {
        pagesJson = buildFichaTecnicaPages({ titulo });
      }
      const created = await createSkillAgentMoodboard({
        Titulo: titulo,
        CategoryLabel:
          String(newMoodboard.CategoryLabel || "").trim() || titulo,
        Temporada: newMoodboard.Temporada || null,
        CustomerId: newMoodboard.CustomerId
          ? Number(newMoodboard.CustomerId)
          : null,
        Kind: boardKind,
        ProjectId: createLinkCtx.ProjectId,
        MeetingSessionId: createLinkCtx.MeetingSessionId,
        OrgBrand: createLinkCtx.OrgBrand || "STAGE",
        ...(pagesJson ? { PagesJson: pagesJson } : null),
        ...(chromeStyleJson ? { ChromeStyleJson: chromeStyleJson } : null),
      });
      setCreateMoodboardOpen(false);
      if (createLinkCtx.ProjectId) {
        await loadProjectBundle(createLinkCtx.ProjectId, { force: true });
        const roots = await listSkillAgentProjectTreeRoots().catch(() => null);
        if (Array.isArray(roots)) setRootGroups(roots);
      } else {
        await load();
      }
      if (created?.id) {
        if (boardKind === "resumen") handleSelectResumen(created.id);
        else handleSelectMoodboard(created.id);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.error || t("skillAgent.create.moodboardError");
      setMoodboardFormError(msg);
      setError(msg);
    } finally {
      setCreatingMoodboard(false);
    }
  };

  const openNewRapportDialog = (ctxNode, presetKind) => {
    rememberCreateLink(ctxNode);
    const rawSeason = String(ctxNode?.Temporada || "").trim();
    const season =
      rawSeason && rawSeason !== t("skillAgent.tree.noSeason") ? rawSeason : "";
    const clientName = String(
      ctxNode?.CustomerName || ctxNode?.clientLabel || "",
    ).trim();
    let customerId = ctxNode?.CustomerId ? String(ctxNode.CustomerId) : "";
    if (!customerId && clientName) {
      const match = fichas.find(
        (f) =>
          f.Titulo === clientName ||
          f.SkillSlug === clientName ||
          (f.Titulo && clientName.includes(String(f.Titulo))) ||
          (f.SkillSlug && clientName.includes(String(f.SkillSlug))),
      );
      if (match?.CustomerId) customerId = String(match.CustomerId);
    }
    setRapportFormError("");
    const kind =
      presetKind === "tartan" || presetKind === "tile"
        ? "tartan"
        : presetKind === "seamless" || presetKind === "interpretacion"
          ? "seamless"
          : "";
    setNewRapport({
      Titulo: "",
      Temporada: season,
      CustomerId: customerId,
      CustomerName: clientName,
      Kind: kind,
      Intent: "",
    });
    setCreateRapportOpen(true);
  };

  const handleHubCreatePick = (kind, ctxNode, extras = {}) => {
    setHubCreateOpen(false);
    const node = ctxNode || hubCreateNode;
    setHubCreateNode(null);
    if (!node) return;
    rememberCreateLink(node, extras);
    const withBrand = {
      ...node,
      OrgBrand: normalizeOrgBrand(
        extras?.OrgBrand || node.OrgBrand || node.orgBrand || "STAGE",
      ),
    };
    if (kind === "meeting") openNewMeetingDialog(withBrand);
    else if (kind === "task") openNewTaskDialog(withBrand, { mode: "skills" });
    else if (kind === "aiDesign")
      openNewTaskDialog(withBrand, { mode: "aiDesigns" });
    else if (kind === "search") openNewSearchDialog(withBrand);
    else if (kind === "moodboard") openNewMoodboardDialog(withBrand);
    else if (kind === "propuesta") openNewPropuestaDialog(withBrand);
    else if (kind === "resumen") openNewResumenDialog(withBrand);
    else if (kind === "rapport") openNewRapportDialog(withBrand);
    else if (kind === "clientInfo") openNewClientInfo(withBrand, "client_info");
    else if (kind === "produccion") openNewProduccion(withBrand);
    else if (kind === "presentaciones") openNewPresentaciones(withBrand);
    else if (kind === "status") openNewStatus(withBrand);
  };

  const handleCreateRapport = async () => {
    const titulo = String(newRapport.Titulo || "").trim();
    if (!titulo) {
      setRapportFormError(t("skillAgent.create.rapportTitleRequired"));
      return;
    }
    if (!newRapport.CustomerId && !newRapport.CustomerName) {
      setRapportFormError(t("skillAgent.create.rapportNeedClient"));
      return;
    }
    if (newRapport.Kind !== "tartan" && newRapport.Kind !== "seamless") {
      setRapportFormError(t("skillAgent.create.rapportKindRequired"));
      return;
    }
    setCreatingRapport(true);
    setRapportFormError("");
    setError("");
    try {
      const marks = {
        mode: "textile",
        tool: newRapport.Kind,
        engine: newRapport.Kind,
      };
      const created = await createSkillAgentRapport({
        Titulo: titulo,
        Temporada: newRapport.Temporada || null,
        CustomerId: newRapport.CustomerId
          ? Number(newRapport.CustomerId)
          : null,
        Kind: newRapport.Kind,
        MarksJson: marks,
        ProjectId: createLinkCtx.ProjectId,
        MeetingSessionId: createLinkCtx.MeetingSessionId,
        OrgBrand: createLinkCtx.OrgBrand || "STAGE",
      });
      setCreateRapportOpen(false);
      if (createLinkCtx.ProjectId) {
        await loadProjectBundle(createLinkCtx.ProjectId, { force: true });
        const roots = await listSkillAgentProjectTreeRoots().catch(() => null);
        if (Array.isArray(roots)) setRootGroups(roots);
      } else {
        await load();
      }
      if (created?.id) handleSelectRapport(created.id);
    } catch (err) {
      const msg =
        err?.response?.data?.error || t("skillAgent.create.rapportError");
      setRapportFormError(msg);
      setError(msg);
    } finally {
      setCreatingRapport(false);
    }
  };

  const openAiDesignFromCompass = useCallback(
    (row, designKind, extras = {}) => {
      const kind =
        designKind ||
        (isSketchTipoTarea(row?.CategoryLabel) ? row.CategoryLabel : null) ||
        "nuevos_disenos";
      const id = Number(row?.id);
      if (!Number.isFinite(id) || id <= 0) return;
      const compassState = {
        fromCompass: true,
        designKind: kind,
        AiDesignId: id,
        MoodboardId: id,
        ProjectId:
          extras.ProjectId ?? row?.ProjectId ?? createLinkCtx.ProjectId ?? null,
        MeetingSessionId:
          extras.MeetingSessionId ??
          row?.MeetingSessionId ??
          createLinkCtx.MeetingSessionId ??
          null,
        CustomerId:
          extras.CustomerId ?? row?.CustomerId ?? newTask.CustomerId ?? null,
        CustomerName:
          extras.CustomerName ??
          row?.CustomerName ??
          newTask.CustomerName ??
          "",
        Temporada:
          extras.Temporada ?? row?.Temporada ?? newTask.Temporada ?? "",
        Titulo: extras.Titulo ?? row?.Titulo ?? "",
        Brief: extras.Brief ?? "",
      };
      setAiDesignCompassState(compassState);
      setSelectedAiDesignId(String(id));
      setSelectedId("");
      setSelectedSearchId("");
      setSelectedMoodboardId("");
      setSelectedRapportId("");
      setSelectedClientInfoId("");
      setSelectedMeetingId("");
      setSelectedProjectId("");
      setSearchParams({ aiDesign: String(id) });
      collapseTreeToRail();
    },
    [createLinkCtx, newTask, setSearchParams, collapseTreeToRail],
  );

  const handleSelectAiDesign = useCallback(
    (row) => {
      if (!row?.id) return;
      openAiDesignFromCompass(row);
    },
    [openAiDesignFromCompass],
  );

  const handleCloseAiDesign = useCallback(() => {
    setSelectedAiDesignId("");
    setAiDesignCompassState(null);
    setSearchParams({});
  }, [setSearchParams]);

  const handleCreate = async () => {
    if (!newTask.Titulo.trim()) {
      setTaskFormError(t("skillAgent.create.taskTitleRequired"));
      return;
    }
    if (!newTask.TipoTarea) {
      setTaskFormError(
        t("skillAgent.create.taskTypeRequired", {
          defaultValue:
            "Elige Propuestas/Skills/Agentes o Siluetas/Nuevos diseños.",
        }),
      );
      return;
    }
    const tipo = newTask.TipoTarea || "propuestas";
    setCreating(true);
    setTaskFormError("");
    setError("");
    try {
      // Siluetas / Nuevos diseños → AI Designs (rama SKETCH / NEW DESIGNS).
      if (tipo === "siluetas" || tipo === "nuevos_disenos") {
        const created = await createSkillAgentMoodboard({
          Titulo: newTask.Titulo.trim(),
          CategoryLabel: tipo,
          Kind: "ai_design",
          CustomerId: newTask.CustomerId ? Number(newTask.CustomerId) : null,
          Temporada: newTask.Temporada.trim() || null,
          ProjectId: createLinkCtx.ProjectId,
          MeetingSessionId: createLinkCtx.MeetingSessionId,
          OrgBrand: createLinkCtx.OrgBrand || "STAGE",
        });
        const resetForm = {
          SkillFichaId: "",
          CustomerId: null,
          CustomerName: "",
          Titulo: "",
          Temporada: "",
          TipoTarea: "propuestas",
          Brief: "",
          searchId: "",
          lockClient: false,
          lockSeason: false,
        };
        const brief = String(newTask.Brief || "").trim();
        const title = newTask.Titulo.trim();
        const customerId = newTask.CustomerId;
        const customerName = newTask.CustomerName;
        const temporada = newTask.Temporada;
        setCreateOpen(false);
        setNewTask(resetForm);
        if (createLinkCtx.ProjectId) {
          await loadProjectBundle(createLinkCtx.ProjectId, { force: true });
          const roots = await listSkillAgentProjectTreeRoots().catch(
            () => null,
          );
          if (Array.isArray(roots)) setRootGroups(roots);
        } else {
          await load();
        }
        openAiDesignFromCompass(created, tipo, {
          Titulo: title,
          Brief: brief,
          CustomerId: customerId,
          CustomerName: customerName,
          Temporada: temporada,
          ProjectId: createLinkCtx.ProjectId,
          MeetingSessionId: createLinkCtx.MeetingSessionId,
        });
        return;
      }

      const created = await createSkillAgentTask({
        SkillFichaId: newTask.SkillFichaId
          ? Number(newTask.SkillFichaId)
          : null,
        CustomerId: newTask.CustomerId ? Number(newTask.CustomerId) : null,
        Titulo: newTask.Titulo.trim(),
        Temporada: newTask.Temporada.trim() || null,
        TipoTarea: newTask.TipoTarea,
        Brief: newTask.Brief || null,
        ProjectId: createLinkCtx.ProjectId,
        MeetingSessionId: createLinkCtx.MeetingSessionId,
        OrgBrand: createLinkCtx.OrgBrand || "STAGE",
        ConstraintsJson: newTask.searchId
          ? {
              searchId: Number(newTask.searchId),
              tipología: "",
              numPropuestas: 6,
            }
          : { numPropuestas: 6 },
      });
      setCreateOpen(false);
      setNewTask({
        SkillFichaId: "",
        CustomerId: null,
        CustomerName: "",
        Titulo: "",
        Temporada: "",
        TipoTarea: "propuestas",
        Brief: "",
        searchId: "",
        lockClient: false,
        lockSeason: false,
      });
      if (createLinkCtx.ProjectId) {
        await loadProjectBundle(createLinkCtx.ProjectId, { force: true });
        const roots = await listSkillAgentProjectTreeRoots().catch(() => null);
        if (Array.isArray(roots)) setRootGroups(roots);
      } else {
        await load();
      }
      if (created?.id) handleSelectTask(created.id);
    } catch (err) {
      const msg =
        err?.response?.data?.error || t("skillAgent.create.taskError");
      setTaskFormError(msg);
      setError(msg);
    } finally {
      setCreating(false);
    }
  };

  if (forbidden) {
    return (
      <Box sx={{ ...contentMargin, p: 3 }}>
        <Alert severity="warning">{t("skillAgent.page.forbidden")}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        height: "100dvh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: isDark
          ? alpha(theme.palette.background.default, 1)
          : MEETING_SURFACE_BG,
        backgroundImage: isDark
          ? "none"
          : `radial-gradient(1000px 400px at 0% -20%, ${alpha("#00897b", 0.08)}, transparent)`,
        ...contentMargin,
      }}
    >
      {/* Header Compás: solo en entrada vacía. Piezas con cabecera propia ganan altura. */}
      {!selectedMoodboardId &&
      !selectedMeetingId &&
      !selectedAiDesignId &&
      !selectedSearchId &&
      !selectedRapportId &&
      !selectedClientInfoId &&
      !selectedId ? (
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 1.5, md: 2.5 },
            py: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: alpha(theme.palette.background.paper, 0.92),
            backdropFilter: "blur(10px)",
            zIndex: 2,
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: "primary.main",
                }}
              >
                <FolderOpenOutlinedIcon fontSize="small" />
              </Box>
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{ color: "#ff4d00" }}
              >
                {t("skillAgent.page.title")}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      ) : null}

      {error && (
        <Alert
          severity="error"
          sx={{ mx: 2, mt: 1.5, flexShrink: 0 }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      {/* Body: rail + detail; el Compás abierto va en overlay (no aplasta el detalle). */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          height: "100%",
          overflow: "hidden",
          position: "relative",
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md:
              captureParked && selectedMeetingId && treeCollapsed
                ? "76px minmax(0, 1fr)"
                : "44px minmax(0, 1fr)",
          },
          gridTemplateRows: {
            xs: "48px minmax(0, 1fr)",
            md: "minmax(0, 1fr)",
          },
          gap: selectedMeetingId ? { xs: 0, md: 1 } : 0,
          pr: selectedMeetingId ? { xs: 0.5, md: 1 } : 0,
          pl: selectedMeetingId ? { md: 0.5 } : 0,
          boxSizing: "border-box",
        }}
      >
        {/* Rail fino (siempre en flujo) */}
        <Paper
          square
          elevation={0}
          sx={{
            borderRight: "none",
            borderBottom: { xs: "1px solid", md: "none" },
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            minHeight: { xs: 0, md: 0 },
            maxHeight: { xs: 48, md: "none" },
            bgcolor: isDark
              ? alpha(theme.palette.background.paper, 0.7)
              : MEETING_SURFACE_BG,
            overflow: "hidden",
            zIndex: 1,
          }}
        >
          {captureParked && selectedMeetingId && isDesktop && treeCollapsed ? (
            <Box
              ref={setCaptureSlotEl}
              sx={{
                flex: 1,
                minHeight: 0,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                overflowY: "auto",
                overflowX: "hidden",
                py: 0.5,
                px: 0.35,
              }}
            />
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: { xs: "row", md: "column" },
                alignItems: "center",
                justifyContent: { xs: "flex-start", md: "flex-start" },
                gap: 1,
                py: { xs: 0.5, md: 1.25 },
                px: { xs: 1, md: 0.5 },
              }}
            >
              <Tooltip
                title={
                  treeCollapsed
                    ? t("skillAgent.tree.showTree")
                    : t("skillAgent.tree.collapseTree")
                }
                placement="right"
              >
                <IconButton
                  size="small"
                  onClick={() => {
                    if (treeCollapsed) expandCompassTree();
                    else {
                      setTreeCollapsed(true);
                      try {
                        localStorage.setItem("skillAgentTreeCollapsed", "1");
                      } catch {
                        /* ignore */
                      }
                    }
                  }}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1.5,
                    bgcolor: treeCollapsed
                      ? "transparent"
                      : alpha("#00897b", 0.12),
                  }}
                >
                  {treeCollapsed ? (
                    <ChevronRightIcon fontSize="small" />
                  ) : (
                    <ChevronLeftIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              <ViewSidebarOutlinedIcon
                sx={{
                  fontSize: 18,
                  color: "text.disabled",
                  display: { xs: "none", md: "block" },
                }}
              />
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{
                  display: { xs: "none", md: "block" },
                  writingMode: "vertical-rl",
                  transform: "rotate(180deg)",
                  fontWeight: 700,
                  letterSpacing: 0.06,
                  mt: 1,
                }}
              >
                {t("skillAgent.tree.collapsedLabel")}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Overlay Compás (dialog/drawer de altura completa) */}
        {!treeCollapsed ? (
          <>
            <Box
              onClick={() => {
                setTreeCollapsed(true);
                try {
                  localStorage.setItem("skillAgentTreeCollapsed", "1");
                } catch {
                  /* ignore */
                }
              }}
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 50,
                bgcolor: alpha("#0f172a", isDark ? 0.45 : 0.28),
                ...motionSafe,
              }}
              aria-hidden
            />
            <Paper
              ref={compassPanelRef}
              elevation={0}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                bottom: compassPanelSize.height ? "auto" : 0,
                zIndex: 51,
                display: "flex",
                flexDirection: "column",
                width: compassPanelSize.width
                  ? `${compassPanelSize.width}px`
                  : "fit-content",
                maxWidth: "100%",
                minWidth: {
                  xs: `min(100%, ${COMPASS_COL_WIDTH}px)`,
                  md: COMPASS_COL_WIDTH,
                },
                height: compassPanelSize.height
                  ? `${compassPanelSize.height}px`
                  : "100%",
                maxHeight: "100%",
                borderRadius: { xs: "0 16px 16px 0", md: "0 18px 18px 0" },
                border: "1px solid",
                borderColor: alpha("#00897b", isDark ? 0.35 : 0.22),
                boxShadow: `12px 0 40px ${alpha("#0f172a", 0.28)}`,
                bgcolor: isDark
                  ? theme.palette.background.paper
                  : MEETING_SURFACE_BG,
                overflow: "hidden",
                animation: `${rise} 220ms cubic-bezier(0.22, 1, 0.36, 1) both`,
                ...motionSafe,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Box
                sx={{
                  p: 1,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  width: COMPASS_COL_WIDTH,
                  maxWidth: "100%",
                  flexShrink: 0,
                  boxSizing: "border-box",
                  alignSelf: "flex-start",
                }}
              >
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{ mb: 0.85 }}
                >
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={groupBy}
                    onChange={(_, v) => v && setGroupBy(v)}
                    sx={{
                      flexShrink: 0,
                      height: 30,
                      gap: 0.35,
                      "& .MuiToggleButtonGroup-grouped": {
                        border: "1px solid !important",
                        borderColor: `${theme.palette.divider} !important`,
                        borderRadius: "8px !important",
                        marginLeft: "0 !important",
                        px: 0.65,
                        minWidth: 30,
                        width: 30,
                        height: 30,
                      },
                      "& .MuiToggleButtonGroup-grouped.Mui-selected": {
                        borderColor: `${alpha("#00897b", 0.5)} !important`,
                        bgcolor: `${alpha("#00897b", 0.12)} !important`,
                        color: "#00897b",
                      },
                    }}
                  >
                    <Tooltip title={t("skillAgent.tree.groupBySeason")}>
                      <ToggleButton
                        value="season"
                        aria-label={t("skillAgent.tree.groupBySeason")}
                      >
                        <CalendarMonthOutlinedIcon sx={{ fontSize: 16 }} />
                      </ToggleButton>
                    </Tooltip>
                    <Tooltip title={t("skillAgent.tree.groupByClient")}>
                      <ToggleButton
                        value="client"
                        aria-label={t("skillAgent.tree.groupByClient")}
                      >
                        <BusinessOutlinedIcon sx={{ fontSize: 16 }} />
                      </ToggleButton>
                    </Tooltip>
                  </ToggleButtonGroup>
                  <TextField
                    size="small"
                    placeholder={t("skillAgent.tree.searchPlaceholder")}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 16 }} color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      ...pageFieldSx,
                      "& .MuiOutlinedInput-root": {
                        height: 30,
                        borderRadius: 1.5,
                        fontSize: "0.78rem",
                        pl: 0.5,
                      },
                      "& .MuiInputAdornment-root": { mr: 0.25 },
                    }}
                  />
                  <Tooltip title={t("skillAgent.tree.collapseTree")}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setTreeCollapsed(true);
                        try {
                          localStorage.setItem("skillAgentTreeCollapsed", "1");
                        } catch {
                          /* ignore */
                        }
                      }}
                      sx={{
                        flexShrink: 0,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1.25,
                        width: 28,
                        height: 28,
                      }}
                    >
                      <ChevronLeftIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Stack direction="row" spacing={0.45} alignItems="center">
                  <Button
                    size="small"
                    onClick={(e) => setTreeFilterMenuEl(e.currentTarget)}
                    aria-expanded={Boolean(treeFilterMenuEl)}
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      justifyContent: "flex-start",
                      gap: 0.45,
                      height: 28,
                      flex: 1,
                      minWidth: 0,
                      px: 0.55,
                      borderRadius: 1.5,
                      border: "1px solid",
                      borderColor: alpha(activeTreeFilter.accent, 0.35),
                      bgcolor: (th) =>
                        th.palette.mode === "dark"
                          ? alpha(activeTreeFilter.accent, 0.18)
                          : alpha(activeTreeFilter.accent, 0.08),
                      color: "text.primary",
                      textTransform: "none",
                      zIndex: 1,
                      transition:
                        "border-color 0.2s, box-shadow 0.2s, background-color 0.2s",
                      "&:hover": {
                        borderColor: alpha(activeTreeFilter.accent, 0.55),
                        bgcolor: (th) =>
                          th.palette.mode === "dark"
                            ? alpha(activeTreeFilter.accent, 0.26)
                            : alpha(activeTreeFilter.accent, 0.12),
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: 1,
                        flexShrink: 0,
                        display: "grid",
                        placeItems: "center",
                        bgcolor: alpha(activeTreeFilter.accent, 0.18),
                        color: activeTreeFilter.accent,
                        border: `1px solid ${alpha(activeTreeFilter.accent, 0.35)}`,
                      }}
                    >
                      <ActiveFilterIcon sx={{ fontSize: 13 }} />
                    </Box>
                    <Typography
                      noWrap
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontWeight: 700,
                        fontSize: "0.7rem",
                        textAlign: "left",
                      }}
                    >
                      {activeTreeFilter.label}
                    </Typography>
                    {treeFilterLoading ? (
                      <CircularProgress
                        size={12}
                        sx={{ color: activeTreeFilter.accent }}
                      />
                    ) : (
                      <ExpandMoreIcon
                        sx={{
                          fontSize: 16,
                          color: activeTreeFilter.accent,
                          transition: "transform 0.22s",
                          transform: treeFilterMenuEl
                            ? "rotate(180deg)"
                            : "none",
                        }}
                      />
                    )}
                  </Button>
                  <Tooltip
                    title={t("skillAgent.tree.syncBlablatonHint", {
                      defaultValue:
                        "Importa reuniones de Blablaton sin proyecto Compás: crea el proyecto si falta y enlaza moodboards/fichas huérfanos. Si ya creas todo desde Compás, casi no hace falta.",
                    })}
                  >
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleSyncFromBlablaton}
                        disabled={syncingBlablaton}
                        aria-label={t("skillAgent.tree.syncBlablaton", {
                          defaultValue: "Sincronizar reuniones de Blablaton",
                        })}
                        sx={{
                          flexShrink: 0,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1.25,
                          width: 28,
                          height: 28,
                          color: "#546e7a",
                        }}
                      >
                        {syncingBlablaton ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <SyncOutlinedIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={t("skillAgent.tree.newProject", {
                      defaultValue: "Nuevo proyecto (cliente / temporada)",
                    })}
                  >
                    <IconButton
                      size="small"
                      onClick={openCreateProjectDialog}
                      aria-label={t("skillAgent.tree.newProject", {
                        defaultValue: "Nuevo proyecto (cliente / temporada)",
                      })}
                      sx={{
                        flexShrink: 0,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1.25,
                        width: 28,
                        height: 28,
                        color: "#00897b",
                      }}
                    >
                      <AddOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={t("skillAgent.caratulas.title", {
                      defaultValue: "Carátulas",
                    })}
                  >
                    <IconButton
                      size="small"
                      onClick={openCaratulasDialog}
                      aria-label={t("skillAgent.caratulas.title", {
                        defaultValue: "Carátulas",
                      })}
                      sx={{
                        flexShrink: 0,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1.25,
                        width: 28,
                        height: 28,
                        color: "#5c6bc0",
                      }}
                    >
                      <DashboardCustomizeOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={treeFilterMenuEl}
                    open={Boolean(treeFilterMenuEl)}
                    onClose={() => setTreeFilterMenuEl(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    transitionDuration={{ enter: 240, exit: 150 }}
                    slotProps={{
                      paper: {
                        elevation: 0,
                        sx: {
                          mt: 1,
                          width: 268,
                          maxWidth: "92vw",
                          zIndex: 40,
                          overflow: "hidden",
                          borderRadius: 3,
                          border: "1px solid",
                          borderColor: alpha("#00897b", 0.24),
                          boxShadow: `0 22px 48px ${alpha("#0f172a", 0.2)}, 0 6px 16px ${alpha("#00897b", 0.12)}`,
                          bgcolor: (th) =>
                            th.palette.mode === "dark"
                              ? th.palette.background.paper
                              : "#ffffff",
                          backgroundImage: (th) =>
                            th.palette.mode === "dark"
                              ? `radial-gradient(120% 80% at 0% 0%, ${alpha("#00897b", 0.28)} 0%, transparent 55%), linear-gradient(180deg, ${th.palette.background.paper} 0%, ${th.palette.background.paper} 100%)`
                              : "radial-gradient(120% 80% at 0% 0%, #dff5f1 0%, transparent 55%), linear-gradient(180deg, #fff 0%, #f8fbfa 100%)",
                          animation: treeFilterMenuEl
                            ? `${rise} 300ms cubic-bezier(0.22, 1, 0.36, 1) both`
                            : undefined,
                          ...motionSafe,
                          "& .MuiList-root": { py: 0.85, px: 0.85 },
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        px: 1.2,
                        pt: 0.9,
                        pb: 1.05,
                        mb: 0.45,
                        borderRadius: 2.25,
                        bgcolor: (th) =>
                          th.palette.mode === "dark"
                            ? alpha("#00897b", 0.22)
                            : "#dff3ef",
                        border: `1px solid ${alpha("#00897b", 0.2)}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        animation: `${staggerIn} 260ms cubic-bezier(0.22, 1, 0.36, 1) both`,
                        ...motionSafe,
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1.75,
                          display: "grid",
                          placeItems: "center",
                          bgcolor: alpha("#00897b", 0.16),
                          color: "#00897b",
                        }}
                      >
                        <TuneOutlinedIcon sx={{ fontSize: 18 }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            fontWeight: 800,
                            letterSpacing: 0.05,
                            textTransform: "uppercase",
                            color: "#00897b",
                            fontSize: "0.6rem",
                          }}
                        >
                          {t("skillAgent.tree.filterMenuEyebrow", {
                            defaultValue: "Filtro Compás",
                          })}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 800, lineHeight: 1.2 }}
                        >
                          {t("skillAgent.tree.filterMenuTitle", {
                            defaultValue: "¿Qué quieres ver?",
                          })}
                        </Typography>
                      </Box>
                    </Box>
                    {TREE_TYPE_FILTERS.map((key, idx) => {
                      const meta =
                        treeTypeFilterMeta[key] || treeTypeFilterMeta.all;
                      const selected = treeTypeFilter === key;
                      const ItemIcon = meta.Icon;
                      return (
                        <MenuItem
                          key={key}
                          selected={selected}
                          onClick={() => {
                            setTreeTypeFilter(key);
                            setTreeFilterMenuEl(null);
                          }}
                          sx={{
                            my: 0.2,
                            py: 1.05,
                            px: 1,
                            borderRadius: 2.25,
                            gap: 1,
                            alignItems: "center",
                            animation: `${staggerIn} 320ms cubic-bezier(0.22, 1, 0.36, 1) both`,
                            animationDelay: `${50 + idx * 32}ms`,
                            ...motionSafe,
                            "&.Mui-selected": {
                              bgcolor: alpha(meta.accent, 0.12),
                            },
                            "&:hover": {
                              bgcolor: alpha(meta.accent, 0.1),
                              "& .filter-icon-tile": {
                                transform: "scale(1.06)",
                              },
                            },
                          }}
                        >
                          <Box
                            className="filter-icon-tile"
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: 1.75,
                              flexShrink: 0,
                              display: "grid",
                              placeItems: "center",
                              bgcolor: alpha(
                                meta.accent,
                                selected ? 0.22 : 0.12,
                              ),
                              color: meta.accent,
                              border: `1px solid ${alpha(meta.accent, 0.3)}`,
                              transition: "transform 0.18s ease",
                            }}
                          >
                            <ItemIcon sx={{ fontSize: 17 }} />
                          </Box>
                          <ListItemText
                            primary={meta.label}
                            secondary={meta.hint}
                            primaryTypographyProps={{
                              fontWeight: 800,
                              fontSize: "0.84rem",
                              lineHeight: 1.2,
                              color: selected ? meta.accent : "text.primary",
                            }}
                            secondaryTypographyProps={{
                              fontSize: "0.68rem",
                              color: "text.secondary",
                              noWrap: true,
                            }}
                          />
                          {selected ? (
                            <CheckCircleOutlineIcon
                              sx={{
                                fontSize: 18,
                                color: meta.accent,
                                flexShrink: 0,
                              }}
                            />
                          ) : null}
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </Stack>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                {loading ? (
                  <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : tree.length === 0 ? (
                  <Box sx={{ py: 6, px: 2, textAlign: "center" }}>
                    <FolderOpenOutlinedIcon
                      sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
                    />
                    <Typography fontWeight={700} color="text.secondary">
                      {t("skillAgent.tree.emptyTitle")}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {t("skillAgent.tree.emptyHintProject", {
                        defaultValue:
                          "Crea un proyecto con cliente y temporada, o sincroniza desde Blablaton.",
                      })}
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddOutlinedIcon />}
                      sx={pagePrimaryButtonSx}
                      onClick={openCreateProjectDialog}
                    >
                      {t("skillAgent.tree.newProjectButton", {
                        defaultValue: "Nuevo proyecto",
                      })}
                    </Button>
                  </Box>
                ) : (
                  <SkillAgentCompassColumns
                    tree={tree}
                    pathIds={columnPath}
                    onPathChange={setColumnPath}
                    selectedId={selectedId}
                    selectedSearchId={selectedSearchId}
                    selectedMoodboardId={selectedMoodboardId}
                    selectedRapportId={selectedRapportId}
                    selectedClientInfoId={selectedClientInfoId}
                    selectedMeetingId={selectedMeetingId}
                    selectedAiDesignId={selectedAiDesignId}
                    selectedProjectId={selectedProjectId}
                    onSelectTask={handleSelectTask}
                    onSelectSearch={handleSelectSearch}
                    onSelectMoodboard={handleSelectMoodboard}
                    onSelectResumen={handleSelectResumen}
                    onSelectRapport={handleSelectRapport}
                    onSelectClientInfo={handleSelectClientInfo}
                    onSelectAiDesign={handleSelectAiDesign}
                    onSelectProject={handleSelectProject}
                    onNewSearch={openNewSearchDialog}
                    onNewMoodboard={openNewMoodboardDialog}
                    onNewPropuesta={openNewPropuestaDialog}
                    onNewResumen={openNewResumenDialog}
                    onNewFicha={openNewFichaDialog}
                    onNewRapport={openNewRapportDialog}
                    onNewClientInfo={openNewClientInfo}
                    onNewProduccion={openNewProduccion}
                    onNewPresentaciones={openNewPresentaciones}
                    onNewStatus={openNewStatus}
                    onNewTask={openNewTaskDialog}
                    onNewMeeting={openNewMeetingDialog}
                    onOpenMeeting={handleOpenMeeting}
                    onHubCreate={openHubCreate}
                    onEditProjectSeason={openEditProjectSeason}
                    onDeleteProject={openDeleteProject}
                    onMoveOrgBrand={openMoveOrgBrand}
                    onExpandProject={(node) => {
                      const pid = Number(node?.ProjectId || node?.projectId);
                      if (Number.isFinite(pid) && pid > 0) {
                        loadProjectBundle(pid);
                      }
                    }}
                    favorites={compassFavorites}
                    onToggleFavorite={handleToggleCompassFavorite}
                    onOpenFavorite={handleOpenCompassFavorite}
                  />
                )}
              </Box>
              {/* Asas para estirar el Compás (lateral derecho / abajo / esquina). */}
              <Box
                onPointerDown={beginCompassPanelResize("e")}
                onPointerMove={onCompassPanelResizeMove}
                onPointerUp={endCompassPanelResize}
                onPointerCancel={endCompassPanelResize}
                onDoubleClick={resetCompassPanelSize}
                title={t("skillAgent.tree.resizePanel", {
                  defaultValue:
                    "Arrastra para cambiar el ancho · doble clic restablece",
                })}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 0,
                  bottom: 14,
                  width: 8,
                  cursor: "ew-resize",
                  zIndex: 5,
                  touchAction: "none",
                  "&:hover": {
                    bgcolor: alpha("#00897b", 0.18),
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    width: 3,
                    height: 36,
                    borderRadius: 2,
                    transform: "translate(-50%, -50%)",
                    bgcolor: alpha("#00897b", 0.45),
                  },
                }}
              />
              <Box
                onPointerDown={beginCompassPanelResize("s")}
                onPointerMove={onCompassPanelResizeMove}
                onPointerUp={endCompassPanelResize}
                onPointerCancel={endCompassPanelResize}
                onDoubleClick={resetCompassPanelSize}
                title={t("skillAgent.tree.resizePanelHeight", {
                  defaultValue:
                    "Arrastra para cambiar la altura · doble clic restablece",
                })}
                sx={{
                  position: "absolute",
                  left: 8,
                  right: 14,
                  bottom: 0,
                  height: 8,
                  cursor: "ns-resize",
                  zIndex: 5,
                  touchAction: "none",
                  "&:hover": {
                    bgcolor: alpha("#00897b", 0.18),
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    width: 36,
                    height: 3,
                    borderRadius: 2,
                    transform: "translate(-50%, -50%)",
                    bgcolor: alpha("#00897b", 0.45),
                  },
                }}
              />
              <Box
                onPointerDown={beginCompassPanelResize("se")}
                onPointerMove={onCompassPanelResizeMove}
                onPointerUp={endCompassPanelResize}
                onPointerCancel={endCompassPanelResize}
                onDoubleClick={resetCompassPanelSize}
                title={t("skillAgent.tree.resizePanelCorner", {
                  defaultValue: "Arrastra la esquina para redimensionar",
                })}
                sx={{
                  position: "absolute",
                  right: 0,
                  bottom: 0,
                  width: 16,
                  height: 16,
                  cursor: "nwse-resize",
                  zIndex: 6,
                  touchAction: "none",
                  borderRadius: "0 0 16px 0",
                  bgcolor: alpha("#00897b", 0.12),
                  "&:hover": {
                    bgcolor: alpha("#00897b", 0.28),
                  },
                }}
              />
            </Paper>
          </>
        ) : null}

        {/* Detail panel (siempre a ancho completo bajo el overlay) */}
        <Box
          sx={{
            minHeight: 0,
            minWidth: 0,
            maxWidth: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            zIndex: 0,
            isolation: "isolate",
            overflow:
              selectedMoodboardId ||
              selectedMeetingId ||
              selectedSearchId ||
              selectedClientInfoId ||
              selectedAiDesignId
                ? "hidden"
                : "auto",
            bgcolor: alpha(theme.palette.background.default, 0.35),
          }}
        >
          {selectedMeetingId ? (
            <MeetingSessionPage
              key={String(selectedMeetingId)}
              embedded
              sessionId={selectedMeetingId}
              captureSlotEl={captureParked && isDesktop ? captureSlotEl : null}
              onCaptureRailChange={onCaptureRailChange}
              onRegisterUnpark={registerUnparkCapture}
              onSessionChange={handleMeetingSessionChange}
              onLeave={handleCloseMeeting}
            />
          ) : selectedAiDesignId ? (
            <AiDesignsStudioPage
              key={String(selectedAiDesignId)}
              embedded
              designId={Number(selectedAiDesignId)}
              compassState={
                aiDesignCompassState || {
                  fromCompass: true,
                  AiDesignId: Number(selectedAiDesignId),
                  MoodboardId: Number(selectedAiDesignId),
                }
              }
              onLeave={handleCloseAiDesign}
              onDesignIdChange={(id) => {
                const next = Number(id);
                if (!Number.isFinite(next) || next <= 0) return;
                setSelectedAiDesignId(String(next));
                setSearchParams({ aiDesign: String(next) });
                setAiDesignCompassState((prev) =>
                  prev
                    ? {
                        ...prev,
                        AiDesignId: next,
                        MoodboardId: next,
                      }
                    : prev,
                );
              }}
            />
          ) : selectedRapportId ? (
            <SkillAgentRapportDetail
              key={selectedRapportId}
              rapportId={selectedRapportId}
              onChanged={() => load()}
              onDeleted={() => {
                setSelectedRapportId("");
                setSearchParams({});
                load();
              }}
            />
          ) : selectedClientInfoId ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <SkillAgentClientInfoDetail
                key={selectedClientInfoId}
                clientInfoId={selectedClientInfoId}
                onChanged={async () => {
                  const ids = Object.keys(projectBundles || {});
                  await Promise.all(
                    ids.map((id) => loadProjectBundle(id, { force: true })),
                  );
                }}
                onDeleted={() => {
                  setSelectedClientInfoId("");
                  setSearchParams({});
                  Object.keys(projectBundles || {}).forEach((id) => {
                    loadProjectBundle(id, { force: true });
                  });
                }}
              />
            </Box>
          ) : selectedMoodboardId ? (
            <SkillAgentMoodboardDetail
              key={selectedMoodboardId}
              moodboardId={selectedMoodboardId}
              viewerName={
                sessionUser?.username ||
                sessionUser?.Nombre ||
                sessionUser?.name ||
                ""
              }
              viewerUserId={sessionUser?.id || null}
              onOpenHelp={() => setHelpTopic("board")}
              onChanged={() => load()}
              onDeleted={() => {
                setSelectedMoodboardId("");
                setSearchParams({});
                load();
              }}
            />
          ) : selectedSearchId ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <SkillAgentSearchDetail
                key={selectedSearchId}
                searchId={selectedSearchId}
                onChanged={() => load()}
                onMoodboardCreated={async (moodboardId, meta = {}) => {
                  const pid = Number(meta?.ProjectId);
                  const mid = Number(meta?.MeetingSessionId);
                  if (Number.isFinite(pid) && pid > 0) {
                    await loadProjectBundle(pid, { force: true });
                    const roots = await listSkillAgentProjectTreeRoots().catch(
                      () => null,
                    );
                    if (Array.isArray(roots)) setRootGroups(roots);
                    setExpanded((prev) => {
                      const next = new Set(prev);
                      next.add(`project:${pid}`);
                      if (Number.isFinite(mid) && mid > 0) {
                        next.add(`meeting:${mid}`);
                        next.add(`meeting:${mid}::moodboards`);
                      } else {
                        next.add(`project:${pid}::moodboards`);
                      }
                      return next;
                    });
                  } else {
                    await load();
                  }
                  if (moodboardId) handleSelectMoodboard(moodboardId);
                }}
                onDeleted={() => {
                  setSelectedSearchId("");
                  setSearchParams({});
                  load();
                }}
              />
            </Box>
          ) : !selectedId ? (
            <Box
              sx={{
                height: "100%",
                minHeight: 320,
                display: "grid",
                placeItems: "center",
                p: 4,
                textAlign: "center",
              }}
            >
              <Box sx={{ maxWidth: 420 }}>
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: 4,
                    mx: "auto",
                    mb: 2,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: "primary.main",
                  }}
                >
                  <AssignmentOutlinedIcon sx={{ fontSize: 36 }} />
                </Box>
                <Typography variant="h6" fontWeight={800}>
                  {t("skillAgent.page.pickItemTitle")}
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{ mt: 1 }}
                  component="div"
                >
                  <Trans
                    i18nKey="skillAgent.page.pickItemBody"
                    components={{ b: <b /> }}
                  />
                </Typography>
              </Box>
            </Box>
          ) : (
            <TaskDetailPanel
              key={`${selectedId}-${
                rows.find((r) => Number(r.id) === Number(selectedId))?.Status ||
                ""
              }`}
              taskId={selectedId}
              fichas={fichas}
              searches={searches}
              showResearchDebug={isSkillsApprover(sessionUser)}
              onChanged={(updated) => {
                setRows((prev) =>
                  prev.map((r) =>
                    Number(r.id) === Number(updated.id)
                      ? { ...r, ...updated }
                      : r,
                  ),
                );
              }}
              onDeleted={(id) => {
                setRows((prev) =>
                  prev.filter((r) => Number(r.id) !== Number(id)),
                );
                setSelectedId("");
                setSearchParams({});
                Object.keys(projectBundles || {}).forEach((pid) => {
                  loadProjectBundle(pid, { force: true });
                });
                load();
              }}
            />
          )}
        </Box>
      </Box>

      <SkillAgentTasksHelpDialog
        open={helpTopic === "board"}
        onClose={() => setHelpTopic("")}
      />
      <SkillAgentInterpretHelpDialog
        open={helpTopic === "interpretacion"}
        onClose={() => setHelpTopic("")}
      />
      <SkillAgentTileHelpDialog
        open={helpTopic === "tile"}
        onClose={() => setHelpTopic("")}
      />
      <SkillAgentSectionHelpDialog
        open={
          Boolean(helpTopic) &&
          helpTopic !== "board" &&
          helpTopic !== "interpretacion" &&
          helpTopic !== "tile"
        }
        topic={helpTopic}
        onClose={() => setHelpTopic("")}
      />

      <SkillAgentHubCreateDialog
        open={hubCreateOpen}
        onClose={() => {
          setHubCreateOpen(false);
          setHubCreateNode(null);
        }}
        contextNode={hubCreateNode}
        allowMeeting={
          hubCreateNode?.kind === "project" ||
          hubCreateNode?.kind === "orgBranch"
        }
        onPick={handleHubCreatePick}
      />

      <SkillAgentNewProjectDialog
        open={createProjectOpen}
        onClose={() => !createProjectSaving && setCreateProjectOpen(false)}
        form={createProjectForm}
        onFormChange={setCreateProjectForm}
        customerOptions={customerOptions}
        seasonChips={catalogSeasons.length ? catalogSeasons : knownSeasons}
        saving={createProjectSaving}
        error={createProjectError}
        onClearError={() => setCreateProjectError("")}
        onSubmit={handleCreateProject}
      />

      <SkillAgentDeleteProjectDialog
        open={Boolean(deleteProjectTarget)}
        onClose={() => !deleteProjectSaving && setDeleteProjectTarget(null)}
        projectName={deleteProjectTarget?.name || ""}
        itemCount={deleteProjectTarget?.itemCount || 0}
        meetingCount={deleteProjectTarget?.meetingCount || 0}
        artifactCount={deleteProjectTarget?.artifactCount || 0}
        saving={deleteProjectSaving}
        error={deleteProjectError}
        onConfirm={handleDeleteProject}
      />

      <SkillAgentMoveOrgBrandDialog
        open={Boolean(moveOrgBrandTarget)}
        onClose={() => !moveOrgBrandSaving && setMoveOrgBrandTarget(null)}
        kind={moveOrgBrandTarget?.kind || ""}
        item={moveOrgBrandTarget?.item || null}
        targetBrand={moveOrgBrandTarget?.targetBrand || "STAGE"}
        saving={moveOrgBrandSaving}
        error={moveOrgBrandError}
        onConfirm={handleMoveOrgBrand}
      />

      <SkillAgentEditProjectDialog
        open={Boolean(projectSeasonEdit)}
        onClose={() => {
          if (projectSeasonSaving) return;
          setProjectSeasonEdit(null);
          setProjectNameValue("");
          setProjectSeasonValue("");
          setProjectSeasonError("");
        }}
        projectName={projectSeasonEdit?.name || ""}
        nameValue={projectNameValue}
        onNameChange={setProjectNameValue}
        seasonValue={projectSeasonValue}
        onSeasonChange={setProjectSeasonValue}
        seasonChips={knownSeasons}
        missingSeason={!String(projectSeasonEdit?.Temporada || "").trim()}
        saving={projectSeasonSaving}
        error={projectSeasonError}
        onClearError={() => setProjectSeasonError("")}
        onSubmit={handleSaveProjectSeason}
      />

      <Dialog
        open={meetingCreateOpen}
        onClose={() => !creatingMeeting && setMeetingCreateOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography variant="h6" fontWeight={800}>
            {t("skillAgent.dialog.newMeetingTitle", {
              defaultValue: "Nueva reunión",
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t("skillAgent.dialog.newMeetingLead", {
              defaultValue:
                "Ponle un nombre. Luego lo puedes cambiar en Datos.",
            })}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1.25 }}>
            <Box
              sx={{
                px: 1.5,
                py: 1.1,
                borderRadius: 2,
                bgcolor: (th) => alpha(th.palette.primary.main, 0.06),
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
              >
                {t("skillAgent.dialog.treeBranchLabel")}
              </Typography>
              <Typography variant="body1" fontWeight={800} noWrap>
                {[
                  meetingCreateNode?.clientLabel ||
                    meetingCreateNode?.CustomerName,
                  meetingCreateNode?.label,
                ]
                  .filter(Boolean)
                  .join(" · ") || t("skillAgent.common.emDash")}
              </Typography>
            </Box>
            <TextField
              autoFocus
              size="small"
              fullWidth
              label={t("skillAgent.form.title")}
              placeholder={t("skillAgent.dialog.newMeetingTitlePlaceholder", {
                defaultValue: "Ej. Arteixo visita AW27",
              })}
              value={meetingCreateTitle}
              onChange={(e) => setMeetingCreateTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleCreateMeeting();
                }
              }}
              disabled={creatingMeeting}
              inputProps={{ maxLength: 200 }}
              sx={pageFieldSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setMeetingCreateOpen(false)}
            disabled={creatingMeeting}
          >
            {t("skillAgent.common.cancel")}
          </Button>
          <Button
            variant="contained"
            sx={pagePrimaryButtonSx}
            disabled={creatingMeeting || !String(meetingCreateTitle).trim()}
            onClick={handleCreateMeeting}
          >
            {creatingMeeting
              ? t("skillAgent.common.creating")
              : t("skillAgent.common.create")}
          </Button>
        </DialogActions>
      </Dialog>

      <SkillAgentDesignPickDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        value={newTask}
        onChange={setNewTask}
        fichas={fichas}
        searches={searchesForNewTask}
        error={taskFormError}
        onClearError={() => setTaskFormError("")}
        submitting={creating}
        onSubmit={handleCreate}
        createMode={taskCreateMode}
      />

      {/* Create SEARCH dialog */}
      <Dialog
        open={createSearchOpen}
        onClose={() => !creatingSearch && setCreateSearchOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography variant="h6" fontWeight={800}>
            {t("skillAgent.dialog.newSearchTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t("skillAgent.dialog.newSearchHint")}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1.25 }}>
            {searchFormError ? (
              <Alert severity="error" onClose={() => setSearchFormError("")}>
                {searchFormError}
              </Alert>
            ) : null}
            <SkillAgentScrapingBeeCreditsAlert
              initial={scrapingBeeStatus}
              sx={{ mb: 0 }}
            />
            <SkillAgentSearchIntro />
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                borderRadius: 2,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
              >
                {t("skillAgent.dialog.treeBranchLabel")}
              </Typography>
              <Typography variant="body1" fontWeight={800}>
                {newSearch.branchPath ||
                  [newSearch.CustomerName, newSearch.Temporada]
                    .filter(Boolean)
                    .join(" · ") ||
                  t("skillAgent.common.emDash")}
              </Typography>
            </Box>
            <TextField
              size="small"
              label={t("skillAgent.form.title")}
              placeholder={t("skillAgent.dialog.newSearchTitlePlaceholder")}
              value={newSearch.Titulo}
              onChange={(e) =>
                setNewSearch((p) => ({ ...p, Titulo: e.target.value }))
              }
              fullWidth
              sx={pageFieldSx}
            />
            <TextField
              size="small"
              label={t("skillAgent.form.tipologyKeywords")}
              value={newSearch.Tipologia}
              onChange={(e) =>
                setNewSearch((p) => ({ ...p, Tipologia: e.target.value }))
              }
              fullWidth
              multiline
              minRows={2}
              helperText={t("skillAgent.form.tipologyHelper")}
              sx={pageFieldSx}
            />
            <Stack direction="row" spacing={1.5}>
              <TextField
                size="small"
                label={t("skillAgent.form.imgsPerBrand")}
                type="number"
                inputProps={{ min: 1, max: 12 }}
                value={newSearch.maxImagesPerBrand}
                onChange={(e) =>
                  setNewSearch((p) => ({
                    ...p,
                    maxImagesPerBrand: e.target.value,
                  }))
                }
                helperText={t("skillAgent.form.imgsPerBrandHelper")}
                sx={{ width: 120, ...pageFieldSx }}
              />
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              useFlexGap
              spacing={1}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flex: 1 }}
              >
                {searchBrands.length
                  ? t("skillAgent.dialog.newSearchPickSources", {
                      defaultValue:
                        "Elige marcas/webs de este SEARCH (o añade URLs abajo). Nada viene marcado; el catálogo es del cliente.",
                    })
                  : t("skillAgent.dialog.newSearchEmptyCatalog", {
                      defaultValue:
                        "Este cliente aún no tiene marcas/webs en catálogo. Añádelas (quedan guardadas para el próximo SEARCH) o pon URLs one-off abajo.",
                    })}
              </Typography>
              <Button
                size="small"
                disabled={!newSearch.CustomerId}
                onClick={() => setSearchCatalogOpen(true)}
                sx={{ textTransform: "none", fontWeight: 700, flexShrink: 0 }}
              >
                {t("skillAgent.search.manageCatalog")}
              </Button>
            </Stack>

            {searchBrands.length ? (
              <SkillAgentBrandSourcesFields
                brands={searchBrands}
                brandIds={newSearch.brandIds}
                urlSelection={newSearch.urlSelection}
                tipologia={newSearch.Tipologia}
                onBrandIdsChange={(brandIds) =>
                  setNewSearch((p) => ({ ...p, brandIds }))
                }
                onUrlSelectionChange={(urlSelection) =>
                  setNewSearch((p) => ({ ...p, urlSelection }))
                }
                maxHeight={280}
              />
            ) : (
              <Box
                sx={{
                  px: 1.5,
                  py: 2,
                  borderRadius: 2,
                  border: "1px dashed",
                  borderColor: "divider",
                  textAlign: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t("skillAgent.researchCatalog.emptyBrands")}
                </Typography>
              </Box>
            )}

            <SkillAgentCustomSourcesFields
              sources={newSearch.customSources}
              tipologia={newSearch.Tipologia}
              onChange={(customSources) =>
                setNewSearch((p) => ({ ...p, customSources }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCreateSearchOpen(false)}
            disabled={creatingSearch}
          >
            {t("skillAgent.common.cancel")}
          </Button>
          <Button
            variant="contained"
            sx={pagePrimaryButtonSx}
            disabled={creatingSearch}
            onClick={handleCreateSearch}
          >
            {creatingSearch
              ? t("skillAgent.common.creating")
              : t("skillAgent.dialog.newSearchSubmit")}
          </Button>
        </DialogActions>
      </Dialog>

      <SkillAgentResearchCatalogDialog
        open={searchCatalogOpen}
        onClose={() => setSearchCatalogOpen(false)}
        customerId={newSearch.CustomerId ? Number(newSearch.CustomerId) : null}
        customerName={newSearch.CustomerName || ""}
        onChanged={async () => {
          const cid = Number(newSearch.CustomerId);
          if (!Number.isFinite(cid) || cid <= 0) return;
          try {
            const meta = await getSkillAgentSearchesMeta({ customerId: cid });
            const brandsForClient = (
              Array.isArray(meta?.brands) ? meta.brands : []
            )
              .slice()
              .sort((a, b) =>
                String(a.name || a.id).localeCompare(
                  String(b.name || b.id),
                  "es",
                  { sensitivity: "base" },
                ),
              );
            setSearchBrands(brandsForClient);
          } catch {
            /* ignore */
          }
        }}
      />

      {/* Create Moodboard dialog */}
      <Dialog
        open={createMoodboardOpen}
        onClose={() => !creatingMoodboard && setCreateMoodboardOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography variant="h6" fontWeight={800}>
            {newMoodboard.Kind === "resumen"
              ? t("skillAgent.dialog.newResumenTitle")
              : newMoodboard.Kind === "ficha_tecnica"
                ? t("skillAgent.dialog.newFichaTitle", {
                    defaultValue: "Nueva ficha técnica",
                  })
                : newMoodboard.Kind === "propuesta"
                  ? t("skillAgent.dialog.newPropuestaTitle", {
                      defaultValue: "Nueva Propuesta",
                    })
                  : t("skillAgent.dialog.newMoodboardTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {newMoodboard.Kind === "resumen"
              ? t("skillAgent.dialog.newResumenHint")
              : newMoodboard.Kind === "ficha_tecnica"
                ? t("skillAgent.dialog.newFichaHint", {
                    defaultValue:
                      "Elige una carátula del cliente (o global) como punto de partida editable.",
                  })
                : newMoodboard.Kind === "propuesta"
                  ? t("skillAgent.dialog.newPropuestaHint", {
                      defaultValue:
                        "Collage/PDF con fotos y vídeos del proyecto (Sketch, meeting, SEARCH). Sin Recaps.",
                    })
                  : t("skillAgent.dialog.newMoodboardHint")}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1.25 }}>
            {moodboardFormError ? (
              <Alert severity="error" onClose={() => setMoodboardFormError("")}>
                {moodboardFormError}
              </Alert>
            ) : null}
            <SkillAgentBoardIntro kind={newMoodboard.Kind} />
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                borderRadius: 2,
                bgcolor: (t) => alpha(t.palette.warning.main, 0.08),
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
              >
                {t("skillAgent.dialog.treeBranchLabel")}
              </Typography>
              <Typography variant="body1" fontWeight={800}>
                {[newMoodboard.CustomerName, newMoodboard.Temporada]
                  .filter(Boolean)
                  .join(" · ") || t("skillAgent.common.emDash")}
              </Typography>
            </Box>
            <TextField
              size="small"
              label={t("skillAgent.form.title")}
              placeholder={t("skillAgent.dialog.newMoodboardTitlePlaceholder")}
              helperText={t("skillAgent.dialog.newBoardTitleHelper")}
              value={newMoodboard.Titulo}
              onChange={(e) => {
                const next = e.target.value;
                setNewMoodboard((p) => {
                  const synced =
                    !String(p.CategoryLabel || "").trim() ||
                    p.CategoryLabel === p.Titulo;
                  return {
                    ...p,
                    Titulo: next,
                    ...(synced ? { CategoryLabel: next } : null),
                  };
                });
              }}
              required
              autoFocus
              fullWidth
              sx={pageFieldSx}
            />
            <TextField
              size="small"
              label={t("skillAgent.form.categoryLabel")}
              placeholder={t("skillAgent.form.categoryPlaceholder")}
              helperText={t("skillAgent.form.categoryHelper")}
              value={newMoodboard.CategoryLabel}
              onChange={(e) =>
                setNewMoodboard((p) => ({
                  ...p,
                  CategoryLabel: e.target.value,
                }))
              }
              fullWidth
              sx={pageFieldSx}
            />
            <TextField
              select
              size="small"
              label={t("skillAgent.caratulas.pick", {
                defaultValue: "Carátula (plantilla)",
              })}
              helperText={t("skillAgent.caratulas.pickHint", {
                defaultValue:
                  "Opcional. Si eliges una, las mesas arrancan con esa plantilla editable.",
              })}
              value={newMoodboard.CaratulaId || ""}
              onChange={(e) =>
                setNewMoodboard((p) => ({
                  ...p,
                  CaratulaId: e.target.value,
                }))
              }
              fullWidth
              sx={pageFieldSx}
              disabled={loadingCaratulas}
            >
              <MenuItem value="">
                {loadingCaratulas
                  ? "Cargando…"
                  : "Sin carátula (layout por defecto)"}
              </MenuItem>
              {caratulaOptions.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.Name}
                  {c.LineLabel ? ` · ${c.LineLabel}` : ""}
                  {c.CustomerName ? ` · ${c.CustomerName}` : " · Global"}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCreateMoodboardOpen(false)}
            disabled={creatingMoodboard}
          >
            {t("skillAgent.common.cancel")}
          </Button>
          <Button
            variant="contained"
            sx={pagePrimaryButtonSx}
            disabled={creatingMoodboard}
            onClick={handleCreateMoodboard}
          >
            {creatingMoodboard
              ? t("skillAgent.common.creating")
              : newMoodboard.Kind === "propuesta"
                ? t("skillAgent.dialog.newPropuestaSubmit", {
                    defaultValue: "Crear Propuesta",
                  })
                : t("skillAgent.dialog.newMoodboardSubmit")}
          </Button>
        </DialogActions>
      </Dialog>

      <SkillAgentCaratulasDialog
        open={caratulasOpen}
        onClose={() => setCaratulasOpen(false)}
        customers={customerOptions}
        onEditLayout={(row) => {
          setEditingCaratula(row);
          setCaratulasOpen(false);
        }}
      />
      <SkillAgentCaratulaEditor
        open={Boolean(editingCaratula?.id)}
        caratulaId={editingCaratula?.id}
        caratulaName={editingCaratula?.Name || ""}
        onClose={() => {
          setEditingCaratula(null);
          setCaratulasOpen(true);
        }}
      />

      <SkillAgentRapportPickDialog
        open={createRapportOpen}
        onClose={() => setCreateRapportOpen(false)}
        value={newRapport}
        onChange={setNewRapport}
        error={rapportFormError}
        onClearError={() => setRapportFormError("")}
        submitting={creatingRapport}
        onSubmit={handleCreateRapport}
      />
    </Box>
  );
}
